/**
 * Plansheet — the host application.
 *
 * Composes the auth + session-browse workflow on top of the vendored Claude
 * session viewer (which renders any individual session; see
 * source/vendor/claudetransformer/).
 *
 * Lifecycle:
 *   - On boot: check session; if logged in → workspace; else → Auth view.
 *   - Sessions are a normal entity list at #/sessions; Upload is #/sessions/upload.
 *   - Clicking a session's "Open" routes to #/session/:id; openServerSession()
 *     downloads the blob and hands it to the viewer via loadSessionFromZip().
 *   - User clicks "back" inside the viewer → showSessions() routes to the list.
 */

const libPictApplication = require('pict-application');

const libPictSectionModal = require('pict-section-modal');
const libPictSectionPicker = require('pict-section-picker');
const libPictSectionTheme = require('pict-section-theme');
const libPictRouter = require('pict-router');
// The Claude session viewer, vendored under source/vendor/claudetransformer/
// (a fork of claude-session-transformer). Its parser/exporter providers and
// its views are registered in the constructor; this app supplies the host
// seams they call (onSessionLoaded / showSessions / the SessionAPI provider).
// The standalone module was retired as a dependency; vendoring keeps the
// feature self-contained and buildable without an external checkout.
const libProviderSessionParser   = require('./vendor/claudetransformer/Pict-Provider-SessionParser.js');
const libProviderSessionExporter = require('./vendor/claudetransformer/Pict-Provider-SessionExporter.js');
const libViewCTDropzone     = require('./vendor/claudetransformer/PictView-Dropzone.js');
const libViewCTLayout       = require('./vendor/claudetransformer/PictView-Layout.js');
const libViewCTSignature    = require('./vendor/claudetransformer/PictView-Signature.js');
const libViewCTTimeline     = require('./vendor/claudetransformer/PictView-Timeline.js');
const libViewCTConversation = require('./vendor/claudetransformer/PictView-Conversation.js');
const libViewCTToolDetail   = require('./vendor/claudetransformer/PictView-ToolDetail.js');
const libViewCTAgentDetail  = require('./vendor/claudetransformer/PictView-AgentDetail.js');
const libPictSectionClaudeTransformer = require('./vendor/claudetransformer/PictView-ClaudeTransformer-Section.js');
const libBrand = require('./Plansheet-Brand.js');
const libBrandChrome = require('./Plansheet-Brand-Chrome.js');

const libProviderAuthClient = require('./providers/Pict-Provider-AuthClient.js');
const libProviderSessionAPI = require('./providers/Pict-Provider-SessionAPI.js');
const libProviderEntityAPI  = require('./providers/Pict-Provider-EntityAPI.js');
const libProviderTestRunAPI = require('./providers/Pict-Provider-TestRunAPI.js');
const libProviderRoutineStepAPI = require('./providers/Pict-Provider-RoutineStepAPI.js');
const libProviderTestRunStepAPI = require('./providers/Pict-Provider-TestRunStepAPI.js');
const libProviderBoardAPI   = require('./providers/Pict-Provider-BoardAPI.js');
const libProviderLaneAPI    = require('./providers/Pict-Provider-LaneAPI.js');
const libProviderBlueprintAPI = require('./providers/Pict-Provider-BlueprintAPI.js');
const libProviderLinkAPI    = require('./providers/Pict-Provider-LinkAPI.js');
const libProviderDocLakeAPI = require('./providers/Pict-Provider-DocLakeAPI.js');
const libProviderMediaAPI   = require('./providers/Pict-Provider-MediaAPI.js');
const libProviderTokenAPI   = require('./providers/Pict-Provider-TokenAPI.js');
const libProviderUserAPI    = require('./providers/Pict-Provider-UserAPI.js');
const libProviderPhotoAPI   = require('./providers/Pict-Provider-PhotoAPI.js');
const libProviderTeamAPI    = require('./providers/Pict-Provider-TeamAPI.js');
const libProviderOrgAPI     = require('./providers/Pict-Provider-OrgAPI.js');
const libProviderDocSyncAPI = require('./providers/Pict-Provider-DocSyncAPI.js');
const libProviderMessageAPI = require('./providers/Pict-Provider-MessageAPI.js');
const libProviderWorkflowAPI = require('./providers/Pict-Provider-WorkflowAPI.js');
const libProviderNotebookAPI = require('./providers/Pict-Provider-NotebookAPI.js');
const libProviderOpsAPI = require('./providers/Pict-Provider-OpsAPI.js');
const libProviderDeployAPI = require('./providers/Pict-Provider-DeployAPI.js');
const libProviderMarkup = require('./providers/Pict-Provider-Markup.js');
const libProviderRunAPI = require('./providers/Pict-Provider-RunAPI.js');
const libProviderAskAPI = require('./providers/Pict-Provider-AskAPI.js');
const libFrameworkClient = require('./providers/FrameworkClient.js');
const libRetoldWorkflowViews = require('retold-workflow/source/Retold-Workflow-Views.js');
const libInlineDocumentation = require('pict-section-inlinedocumentation');
const libPictSectionNavigation = require('pict-section-navigation');
const libPictSectionContent = require('pict-section-content');
const libEntityDescriptors  = require('./EntityDescriptors.js');
const libSiteMap            = require('./SiteMap.js');
const libPlansheetIcons     = require('./PlansheetIcons.js');
const libFeedbackPicker     = require('./FeedbackPicker.js');
const libCandidateSearch    = require('./CandidateSearch.js');

const libViewAuth         = require('./views/PictView-Auth.js');
const libViewShell        = require('./views/PictView-Shell.js');
const libViewRail         = require('./views/PictView-Rail.js');
const libViewTopBarNav    = require('./views/PictView-TopBar-Nav.js');
const libViewTopBarUser   = require('./views/PictView-TopBar-User.js');
const libViewUserSettings  = require('./views/PictView-UserSettings.js');
const libViewAboutMeEditor = require('./views/PictView-AboutMeEditor.js');
const libViewProfile       = require('./views/PictView-Profile.js');
const libViewCommunity     = require('./views/PictView-Community.js');
const libViewNotebook = require('./views/PictView-Notebook.js');
const libViewEntityList    = require('./views/PictView-EntityList.js');
const libViewBoard         = require('./views/PictView-Board.js');
const libViewEntityDetail  = require('./views/PictView-EntityDetail.js');
const libNodeWorkspace     = require('./workspaces/NodeWorkspace.js');
const libViewDocEditor     = require('./views/PictView-DocEditor.js');
const libViewRichDocEditor = require('./views/PictView-RichDocEditor.js');
const libViewFieldMarkdownEditor = require('./views/PictView-FieldMarkdownEditor.js');
const libViewFieldCodeEditor = require('./views/PictView-FieldCodeEditor.js');
const libViewNotebookNoteEditor = require('./views/PictView-NotebookNoteEditor.js');
const libViewNotesPage = require('./views/PictView-NotesPage.js');
const libViewNotesPageEditor = require('./views/PictView-NotesPageEditor.js');
const libViewBookmarksPage = require('./views/PictView-BookmarksPage.js');
const libViewHome = require('./views/PictView-Home.js');
const libViewGalleryPage = require('./views/PictView-GalleryPage.js');
const libViewDrawingsPage = require('./views/PictView-DrawingsPage.js');
const libViewOpsConsole = require('./views/PictView-OpsConsole.js');
const libViewReleases = require('./views/PictView-Releases.js');
const libViewRuns = require('./views/PictView-Runs.js');
const libViewAsk = require('./views/PictView-Ask.js');
const libViewProject = require('./views/PictView-Project.js');
const libViewProjectTypes = require('./views/PictView-ProjectTypes.js');
const libViewCapabilityActivation = require('./views/PictView-CapabilityActivation.js');
const libViewOrgActivity = require('./views/PictView-OrgActivity.js');
const libViewSystemActivity = require('./views/PictView-SystemActivity.js');
const libSectionActivityGraph = require('pict-section-activitygraph');
const libViewLoopsMarkdownEditor = require('./views/PictView-LoopsMarkdownEditor.js');
const libViewDrawingEditor = require('./views/PictView-DrawingEditor.js');
const libViewFoundationDocs = require('./views/PictView-FoundationDocs.js');
const libViewWorkflow       = require('./views/PictView-Workflow.js');
const libViewWhiteboards    = require('./views/PictView-Whiteboards.js');
const libViewStickerSetsPreview = require('./views/PictView-StickerSetsPreview.js');
const libViewHelpDrawer = require('./views/PictView-HelpDrawer.js');
const libViewChecklists     = require('./views/PictView-Checklists.js');
const libViewIssueBoard     = require('./views/PictView-IssueBoard.js');
const libViewFeatureBoard   = require('./views/PictView-FeatureBoard.js');
const libViewDeliverableBoard = require('./views/PictView-DeliverableBoard.js');
const libViewCallDesk       = require('./views/PictView-CallDesk.js');
const libViewVisionLoops    = require('./views/PictView-VisionLoops.js');
const libViewConnections    = require('./views/PictView-Connections.js');
const libViewRoutineSteps   = require('./views/PictView-RoutineSteps.js');
const libViewTestRunner     = require('./views/PictView-TestRunner.js');
const libViewBlueprint      = require('./views/PictView-Blueprint.js');
const libViewBlueprintComposer = require('./views/PictView-BlueprintComposer.js');
const libViewBlueprintStudio = require('./views/PictView-BlueprintStudio.js');
const libViewMediaViewer    = require('./views/PictView-MediaViewer.js');
const libViewReAuth         = require('./views/PictView-ReAuth.js');
const libProviderMoodboardAPI = require('./providers/Pict-Provider-MoodboardAPI.js');
const libProviderStickerAPI = require('./providers/Pict-Provider-StickerAPI.js');
const libProviderStickerSetAPI = require('./providers/Pict-Provider-StickerSetAPI.js');
const libProviderShapesAPI = require('./providers/Pict-Provider-ShapesAPI.js');
const libProviderChecklistAPI = require('./providers/Pict-Provider-ChecklistAPI.js');
const libProviderIssueBoardAPI = require('./providers/Pict-Provider-IssueBoardAPI.js');
const libProviderFeatureBoardAPI = require('./providers/Pict-Provider-FeatureBoardAPI.js');
const libProviderDeliverableBoardAPI = require('./providers/Pict-Provider-DeliverableBoardAPI.js');
const libProviderCallDeskAPI = require('./providers/Pict-Provider-CallDeskAPI.js');
const libProviderLoopsAPI = require('./providers/Pict-Provider-LoopsAPI.js');
const libProviderConnectionsAPI = require('./providers/Pict-Provider-ConnectionsAPI.js');
const libProviderProfileBlockAPI = require('./providers/Pict-Provider-ProfileBlockAPI.js');
const libMoodboardMediaImageSource = require('./providers/Moodboard-MediaImageSource.js');
const libMoodboardDrawingSource = require('./providers/Moodboard-DrawingSource.js');
const libMoodboardStickerSource = require('./providers/Moodboard-StickerSource.js');
const libMoodboardSection   = require('pict-section-moodboard');
const libProviderCommentThreadAPI = require('./providers/Pict-Provider-CommentThreadAPI.js');
const libProviderCommentAPI = require('./providers/Pict-Provider-CommentAPI.js');
const libPlansheetCommentProvider = require('./providers/PlansheetCommentProvider.js');
const libCommentsSection    = require('pict-section-comments');
const libProviderPromptAPI = require('./providers/Pict-Provider-PromptAPI.js');
const libPlansheetPromptProvider = require('./providers/PlansheetPromptProvider.js');
const libPromptEditorSection = require('pict-section-prompteditor');
// The reusable passwordless sign-in component (magic links). Plansheet uses it as the request + verify
// engine; plansheet's own auth screen is the UI and the hooks bridge the result into AppData.Auth.
const libPictSectionLogin = require('pict-section-login');
const libViewPromptLibrary  = require('./views/PictView-PromptLibrary.js');
const libViewPromptShapes   = require('./views/PictView-PromptShapes.js');
const libViewPromptAttachments = require('./views/PictView-PromptAttachments.js');

// The list and detail route slugs, by entity key. At module scope rather than inside _setupRoutes
// because a URL has to be BUILDABLE from anywhere now that the create and edit forms are routes of
// their own: a view that opens a form navigates to it, and needs the slug to do that.
const _LIST_SLUGS = { 'visions': 'Vision', 'modules': 'Module', 'features': 'Feature', 'work-items': 'WorkItem', 'sessions': 'Session', 'routines': 'Routine', 'test-runs': 'TestRun', 'feedback': 'Feedback', 'keystone-customers': 'KeystoneCustomer', 'contacts': 'Contact', 'calls': 'Call', 'issue-types': 'IssueType', 'blueprints': 'Blueprint', 'expectations': 'Expectation', 'deliverables': 'Deliverable', 'as-builts': 'AsBuilt', 'deploy-requests': 'DeployRequest', 'node-registrations': 'NodeRegistration', 'tasks': 'Task' };
const _DETAIL_SLUGS = { 'vision': 'Vision', 'module': 'Module', 'feature': 'Feature', 'work-item': 'WorkItem', 'routine': 'Routine', 'feedback': 'Feedback', 'keystone-customer': 'KeystoneCustomer', 'issue': 'Issue', 'contact': 'Contact', 'call': 'Call', 'issue-type': 'IssueType', 'expectation': 'Expectation', 'deliverable': 'Deliverable', 'as-built': 'AsBuilt', 'deploy-request': 'DeployRequest', 'node-registration': 'NodeRegistration', 'task': 'Task', 'request': 'Request' };

// Reverse lookups: entity key -> slug. Not every entity has both. Blueprint has a list route and a
// bespoke detail page; Issue has a detail route and a board instead of a list. A caller that gets ''
// back must fall back to rendering in place rather than navigating somewhere that does not exist.
function _listSlugFor(pKey) { return Object.keys(_LIST_SLUGS).find((pSlug) => _LIST_SLUGS[pSlug] === pKey) || ''; }
function _detailSlugFor(pKey) { return Object.keys(_DETAIL_SLUGS).find((pSlug) => _DETAIL_SLUGS[pSlug] === pKey) || ''; }

class PictApplicationPlansheet extends libPictApplication
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		// 1. Modal section — confirm / toast / alert API for any view.
		this.pict.addView('Pict-Section-Modal',
			libPictSectionModal.default_configuration, libPictSectionModal);

		// 2. Plansheet-owned providers (server-talk).
		// Markup is the exception: it talks to nothing. It turns the per-plan-sheet numbers every
		// entity already carries into a designator (WI-301) and the title block / card / tooltip built
		// from it, so one thing renders an entity the same way on every surface.
		this.pict.addProvider('Markup', libProviderMarkup.default_configuration, libProviderMarkup);
		this.pict.addProvider('AuthClient', libProviderAuthClient.default_configuration, libProviderAuthClient);
		this.pict.addProvider('SessionAPI', libProviderSessionAPI.default_configuration, libProviderSessionAPI);
		// Vendored Claude session viewer: the parser (zip/jsonl -> normalized
		// session on AppData.Session) and the markdown/PDF exporter.
		this.pict.addProvider('SessionParser', libProviderSessionParser.default_configuration, libProviderSessionParser);
		this.pict.addProvider('SessionExporter', libProviderSessionExporter.default_configuration, libProviderSessionExporter);

		// SDLC entity CRUD: one generic provider class, one instance per entity
		// (VisionAPI/ModuleAPI/FeatureAPI/WorkItemAPI/...), plus the board aggregator.
		// Subclass exceptions carry entity-specific calls (TestRunAPI adds the
		// one-call start() against POST /1.0/TestRun/Start).
		// Blueprints are addressable two ways -- by the entity that owns one, and by the blueprint
		// record itself -- and BlueprintAPI is what knows the difference. Registered HERE, before the
		// descriptor loop below, because that loop skips any entity whose provider already exists
		// ("don't clobber it with a generic EntityAPI"). It subclasses EntityAPI, so the Blueprints
		// list keeps the generic CRUD it has always had.
		this.pict.addProvider('BlueprintAPI', libProviderBlueprintAPI.default_configuration, libProviderBlueprintAPI);

		let tmpEntityKeys = Object.keys(libEntityDescriptors.Entities);
		let tmpEntityProviderClasses = { TestRun: libProviderTestRunAPI };
		for (let i = 0; i < tmpEntityKeys.length; i++)
		{
			let tmpDescriptor = libEntityDescriptors.Entities[tmpEntityKeys[i]];
			// Entities with a bespoke provider already registered (Session ->
			// SessionAPI, above) keep it; don't clobber it with a generic EntityAPI.
			if (this.pict.providers[tmpDescriptor.Key + 'API']) { continue; }
			let tmpProviderClass = tmpEntityProviderClasses[tmpDescriptor.Key] || libProviderEntityAPI;
			this.pict.addProvider(tmpDescriptor.Key + 'API',
				Object.assign({}, tmpProviderClass.default_configuration,
					{
						ProviderIdentifier: tmpDescriptor.Key + 'API',
						EntityName: tmpDescriptor.Key,
						PluralRoute: tmpDescriptor.Plural,
						IDField: tmpDescriptor.IDField
					}),
				tmpProviderClass);
		}
		// Child-entity providers: steps are not in EntityDescriptors.Entities (no
		// routes or lists of their own; they live inside their parent's editor).
		this.pict.addProvider('RoutineStepAPI', libProviderRoutineStepAPI.default_configuration, libProviderRoutineStepAPI);
		this.pict.addProvider('TestRunStepAPI', libProviderTestRunStepAPI.default_configuration, libProviderTestRunStepAPI);
		this.pict.addProvider('BoardAPI', libProviderBoardAPI.default_configuration, libProviderBoardAPI);
		this.pict.addProvider('LaneAPI', libProviderLaneAPI.default_configuration, libProviderLaneAPI);
		this.pict.addProvider('LinkAPI', libProviderLinkAPI.default_configuration, libProviderLinkAPI);
		this.pict.addProvider('DocLakeAPI', libProviderDocLakeAPI.default_configuration, libProviderDocLakeAPI);
		this.pict.addProvider('MediaAPI', libProviderMediaAPI.default_configuration, libProviderMediaAPI);
		this.pict.addProvider('TokenAPI', libProviderTokenAPI.default_configuration, libProviderTokenAPI);
		this.pict.addProvider('UserAPI', libProviderUserAPI.default_configuration, libProviderUserAPI);
		this.pict.addProvider('PhotoAPI', libProviderPhotoAPI.default_configuration, libProviderPhotoAPI);
		this.pict.addProvider('Content', libPictSectionContent.PictContentProvider.default_configuration, libPictSectionContent.PictContentProvider);
		this.pict.addProvider('TeamAPI', libProviderTeamAPI.default_configuration, libProviderTeamAPI);
		this.pict.addProvider('OrgAPI', libProviderOrgAPI.default_configuration, libProviderOrgAPI);
		this.pict.addProvider('DocSyncAPI', libProviderDocSyncAPI.default_configuration, libProviderDocSyncAPI);
		this.pict.addProvider('MessageAPI', libProviderMessageAPI.default_configuration, libProviderMessageAPI);
		this.pict.addProvider('WorkflowAPI', libProviderWorkflowAPI.default_configuration, libProviderWorkflowAPI);
			this.pict.addProvider('NotebookAPI', libProviderNotebookAPI.default_configuration, libProviderNotebookAPI);
		this.pict.addProvider('OpsAPI', libProviderOpsAPI.default_configuration, libProviderOpsAPI);
		this.pict.addProvider('DeployAPI', libProviderDeployAPI.default_configuration, libProviderDeployAPI);
		this.pict.addProvider('RunAPI', libProviderRunAPI.default_configuration, libProviderRunAPI);
		this.pict.addProvider('AskAPI', libProviderAskAPI.default_configuration, libProviderAskAPI);
		this.pict.addProvider('MoodboardAPI', libProviderMoodboardAPI.default_configuration, libProviderMoodboardAPI);
		this.pict.addProvider('StickerAPI', libProviderStickerAPI.default_configuration, libProviderStickerAPI);
		this.pict.addProvider('StickerSetAPI', libProviderStickerSetAPI.default_configuration, libProviderStickerSetAPI);
		this.pict.addProvider('ShapesAPI', libProviderShapesAPI.default_configuration, libProviderShapesAPI);
		this.pict.addProvider('ChecklistAPI', libProviderChecklistAPI.default_configuration, libProviderChecklistAPI);
		this.pict.addProvider('IssueBoardAPI', libProviderIssueBoardAPI.default_configuration, libProviderIssueBoardAPI);
		this.pict.addProvider('FeatureBoardAPI', libProviderFeatureBoardAPI.default_configuration, libProviderFeatureBoardAPI);
		this.pict.addProvider('DeliverableBoardAPI', libProviderDeliverableBoardAPI.default_configuration, libProviderDeliverableBoardAPI);
		this.pict.addProvider('CallDeskAPI', libProviderCallDeskAPI.default_configuration, libProviderCallDeskAPI);
		this.pict.addProvider('LoopsAPI', libProviderLoopsAPI.default_configuration, libProviderLoopsAPI);
		this.pict.addProvider('ConnectionsAPI', libProviderConnectionsAPI.default_configuration, libProviderConnectionsAPI);
		this.pict.addProvider('ProfileBlockAPI', libProviderProfileBlockAPI.default_configuration, libProviderProfileBlockAPI);
		this.pict.addProvider('CommentThreadAPI', libProviderCommentThreadAPI.default_configuration, libProviderCommentThreadAPI);
		this.pict.addProvider('CommentAPI', libProviderCommentAPI.default_configuration, libProviderCommentAPI);
		// Prompt management: the caller's library + shape cascade clients, plus
		// plain owner-scoped CRUD for word lists and generated output.
		this.pict.addProvider('PromptAPI', libProviderPromptAPI.default_configuration, libProviderPromptAPI);
		this.pict.addProvider('PromptShapeAPI', libProviderPromptAPI.PromptShapeAPI.default_configuration, libProviderPromptAPI.PromptShapeAPI);
		this.pict.addProvider('WordListAPI', Object.assign({}, libProviderEntityAPI.default_configuration,
			{ ProviderIdentifier: 'WordListAPI', EntityName: 'WordList', PluralRoute: 'WordLists', IDField: 'IDWordList' }), libProviderEntityAPI);
		this.pict.addProvider('GeneratedPromptAPI', Object.assign({}, libProviderEntityAPI.default_configuration,
			{ ProviderIdentifier: 'GeneratedPromptAPI', EntityName: 'GeneratedPrompt', PluralRoute: 'GeneratedPrompts', IDField: 'IDGeneratedPrompt' }), libProviderEntityAPI);
		// Register plansheet's glyphs with Pict's Icon provider so views render them as
		// themeable inline SVG via {~I:Name~} / pict.icon('Name') -- we do not bundle
		// FontAwesome (see the nav-icon-hide note below).
		libPlansheetIcons.registerPlansheetIcons(this.pict);
		// pict-section-navigation drives the whole app's navigation from one site-map graph (SiteMap.js):
		// the docked main sidebar (non-settings groups), the in-page settings sub-rail (the Settings
		// group, scoped), and the cmd+K launcher (everything). The graph is set after login (so
		// entitlements gate the admin items); onNavigate routes by each item's Route.
		this.pict.addProvider('Pict-Navigation', { NavigationGraph: [] }, libPictSectionNavigation);
		this.pict.providers['Pict-Navigation'].onNavigate = (pNode) => this._onSiteNavigate(pNode);
		// The navigation now renders item/category icons through pict's SVG icon registry (SiteMap.js
		// supplies registry names; the glyphs are registered above via registerPlansheetIcons), so the
		// icon chips show real themable SVGs. We only trim two nav affordances the site map does not use.
		if (this.pict.CSSMap && typeof this.pict.CSSMap.addCSS === 'function')
		{
			this.pict.CSSMap.addCSS('Plansheet-Nav-Icon-Hide',
				// No "advanced" items in the site map, so drop the toggle; and the settings sub-rail (a List)
				// is short, so drop its redundant search (the sidebar search + cmd+K already cover it).
				'.pict-nav-adv { display: none !important; }'
				+ ' .pict-nav-list .pict-nav-search { display: none !important; }',
				600);
			// The brand chrome layer: Open Gorton on the drafting layer, IBM Plex
			// Sans as the content face, and crisp drafting-sheet corners and
			// linework, over every theme. Priority 1200 sits ABOVE the pict-view
			// default CSS priority (1000) so equal-specificity chrome rules win on
			// the cascade -- notably the body content font, which the Shell view
			// also sets. (The nav-icon hide above wins by !important regardless.)
			this.pict.CSSMap.addCSS('Plansheet-Brand-Chrome', libBrandChrome, 1200);
		}
		this.pict.addProvider('Pict-InlineDocumentation', libInlineDocumentation.default_configuration, libInlineDocumentation);

		// 3. Plansheet-owned views.
		this.pict.addView('Plansheet-Auth',           libViewAuth.default_configuration,         libViewAuth);
		this.pict.addView('Plansheet-ReAuth',         libViewReAuth.default_configuration,       libViewReAuth);
		// Magic-link (passwordless) engine. Headless: RenderOnLoad/CheckSessionOnLoad off -- plansheet's
		// AuthClient owns session state and PictView-Auth owns the UI; this just drives the request + verify
		// fetches and single-use token handling, with the hooks below bridging into AppData.Auth.
		this.pict.addView('Plansheet-MagicLogin',
			Object.assign({}, libPictSectionLogin.default_configuration,
			{
				ViewIdentifier: 'Plansheet-MagicLogin',
				ShowMagicLink: true,
				RenderOnLoad: false,
				CheckSessionOnLoad: false,
				MagicLinkRequestEndpoint: '/1.0/Login/MagicLink/Request',
				MagicLinkVerifyEndpoint: '/1.0/Login/MagicLink/Verify'
			}),
			libPictSectionLogin);
		this._wireMagicLogin();
		this.pict.addView('Plansheet-Shell',          libViewShell.default_configuration,        libViewShell);
		this.pict.addView('Plansheet-Rail',           libViewRail.default_configuration,         libViewRail);
		this.pict.addView('Plansheet-TopBar-Nav',     libViewTopBarNav.default_configuration,    libViewTopBarNav);
		this.pict.addView('Plansheet-TopBar-User',    libViewTopBarUser.default_configuration,   libViewTopBarUser);
		this.pict.addView('Plansheet-UserSettings',   libViewUserSettings.default_configuration,  libViewUserSettings);
		this.pict.addView('Plansheet-AboutMeEditor',  libViewAboutMeEditor.default_configuration, libViewAboutMeEditor);
		this.pict.addView('Plansheet-Profile',        libViewProfile.default_configuration,       libViewProfile);
		this.pict.addView('Plansheet-Community',      libViewCommunity.default_configuration,     libViewCommunity);
		this.pict.addView('Plansheet-EntityList',     libViewEntityList.default_configuration,    libViewEntityList);
		this.pict.addView('Plansheet-Board',          libViewBoard.default_configuration,         libViewBoard);
		this.pict.addView('Plansheet-EntityDetail',   libViewEntityDetail.default_configuration,  libViewEntityDetail);
		// The Node workspace (WI #465): registers the 'NodeWorkspace' provider + view pair (single-node detail +
		// lifecycle actions + Provision create). Pict-Section-Modal is already registered above, which its token
		// reveal and confirms need.
		libNodeWorkspace.registerNodeWorkspace(this.pict);
		this.pict.addView('Plansheet-DocEditor',      libViewDocEditor.default_configuration,     libViewDocEditor);
		this.pict.addView('Plansheet-RichDocEditor',  libViewRichDocEditor.default_configuration,  libViewRichDocEditor);
		this.pict.addView('Plansheet-FieldMarkdownEditor', libViewFieldMarkdownEditor.default_configuration, libViewFieldMarkdownEditor);
		// The code-field editor for any descriptor field declared T:'code' (WI #456). pict-section-code and
		// codejar already ride the bundle via pict-section-content, so this is a view, not a payload.
		this.pict.addView('Plansheet-FieldCodeEditor', libViewFieldCodeEditor.default_configuration, libViewFieldCodeEditor);
		// A second instance for the feature acceptance dialog. Its own CodeMirror state, its own
		// content address and its own host, because the entity form's instance is live at the same
		// time and the two must not write over each other.
		this.pict.addView('Plansheet-AcceptanceMarkdownEditor',
			libViewFieldMarkdownEditor.buildConfiguration(
				{
					ViewIdentifier: 'Plansheet-AcceptanceMarkdownEditor',
					HostAddress: '#Acceptance-MarkdownHost',
					DataKey: 'AcceptanceMarkdownEditor',
					HostViewIdentifier: 'Plansheet-FeatureBoard',
					AllowSegments: false
				}), libViewFieldMarkdownEditor);
		this.pict.addView('Plansheet-Notebook-NoteEditor', libViewNotebookNoteEditor.default_configuration, libViewNotebookNoteEditor);
		// The full-page Tools surfaces (Notes / Bookmarks / Gallery), reachable from the Tools nav group and
		// as whole-screen pages on a phone. They reuse the NotebookAPI + MediaAPI providers; the Notes page
		// has its own markdown-editor child (its own DOM host + AppData buffer), so it never collides with
		// the rail notebook's editor.
		this.pict.addView('Plansheet-NotesPage-Editor', libViewNotesPageEditor.default_configuration, libViewNotesPageEditor);
		this.pict.addView('Plansheet-NotesPage', libViewNotesPage.default_configuration, libViewNotesPage);
		this.pict.addView('Plansheet-BookmarksPage', libViewBookmarksPage.default_configuration, libViewBookmarksPage);
		this.pict.addView('Plansheet-Home', libViewHome.default_configuration, libViewHome);
		this.pict.addView('Plansheet-GalleryPage', libViewGalleryPage.default_configuration, libViewGalleryPage);
		// The Drawings board surface (Tools group): a gallery of Excalidraw drawings, opening the same
		// full-screen editor the doc "Insert drawing" uses. Storage is Media (OwnerType 'Drawing').
		this.pict.addView('Plansheet-DrawingsPage', libViewDrawingsPage.default_configuration, libViewDrawingsPage);
		['design', 'build', 'deliver', 'feedback'].forEach((pLoopKey) =>
		{
			this.pict.addView('Plansheet-LoopEditor-' + pLoopKey, libViewLoopsMarkdownEditor.configForLoop(pLoopKey), libViewLoopsMarkdownEditor);
		});
		this.pict.addView('Plansheet-DrawingEditor',  libViewDrawingEditor.default_configuration,  libViewDrawingEditor);
		this.pict.addView('Plansheet-FoundationDocs', libViewFoundationDocs.default_configuration, libViewFoundationDocs);
		this.pict.addView('Plansheet-RoutineSteps',  libViewRoutineSteps.default_configuration,   libViewRoutineSteps);
		this.pict.addView('Plansheet-TestRunner',    libViewTestRunner.default_configuration,     libViewTestRunner);
		this.pict.addView('Plansheet-Blueprint',     libViewBlueprint.default_configuration,      libViewBlueprint);
		this.pict.addView('Plansheet-BlueprintComposer', libViewBlueprintComposer.default_configuration, libViewBlueprintComposer);
		this.pict.addView('Plansheet-BlueprintStudio', libViewBlueprintStudio.default_configuration, libViewBlueprintStudio);
		this.pict.addView('Plansheet-MediaViewer',    libViewMediaViewer.default_configuration,    libViewMediaViewer);

		// The "visually map out your workflow" section: the product-agnostic retold-workflow views
		// (catalog, designer, board, subject) hosted by a thin plansheet shell. Each view talks to
		// the server through the WorkflowAPI provider; navigation between them is wired to the shell.
		let tmpWorkflowShellID = 'Plansheet-Workflow';
		// The catalog lists ONLY the tenant's imported workflows (owned types), never the full
		// platform catalog. Crafts are brought in through the Drafting Kit (Settings); this page is
		// for designing and running what you have imported. A Client shim swaps getTypes for the
		// owned-only feed (the view supports options.Client); with no built-ins in the list it
		// renders no Adopt buttons. Resolve the provider lazily, since it registers after the views.
		let tmpOwnedCatalogClient =
		{
			getTypes: () => this.pict.providers.WorkflowAPI.getOwnedTypes(),
			adoptType: (pID) => this.pict.providers.WorkflowAPI.adoptType(pID)
		};
		this.pict.addView('Workflow-Catalog', Object.assign({}, libRetoldWorkflowViews.WorkflowCatalogView.default_configuration,
			{
				ClientProvider: 'WorkflowAPI',
				Client: tmpOwnedCatalogClient,
				onOpenType: (pType) => this.pict.views[tmpWorkflowShellID].openDesigner(pType),
				onShowBoard: (pType) => this.pict.views[tmpWorkflowShellID].openBoard(pType)
			}), libRetoldWorkflowViews.WorkflowCatalogView);
		this.pict.addView('Workflow-Map', Object.assign({}, libRetoldWorkflowViews.WorkflowMapView.default_configuration,
			{ ClientProvider: 'WorkflowAPI' }), libRetoldWorkflowViews.WorkflowMapView);
		this.pict.addView('Workflow-Board', Object.assign({}, libRetoldWorkflowViews.WorkflowBoardView.default_configuration,
			{
				ClientProvider: 'WorkflowAPI',
				onOpenSubject: (pSubjectID, pType, pTitle) => this.pict.views[tmpWorkflowShellID].openSubject(pSubjectID, pType, pTitle)
			}), libRetoldWorkflowViews.WorkflowBoardView);
		this.pict.addView('Workflow-Subject', Object.assign({}, libRetoldWorkflowViews.WorkflowSubjectView.default_configuration,
			{ ClientProvider: 'WorkflowAPI' }), libRetoldWorkflowViews.WorkflowSubjectView);
		this.pict.addView('Plansheet-Workflow', libViewWorkflow.default_configuration, libViewWorkflow);

		// Profile boards: the personal moodboard banner at the top of a profile page. The Edit board
		// (your own) autosaves through the profile shell and gets a Media-backed gallery scoped to your
		// board; the View board (a teammate's) is read-only. Both render into the profile's
		// #Profile-Board. They are separate moodboard instances; the on-screen one claims the shared
		// AppData.Moodboard.ViewID on render so its toolbar and card panels resolve to it.
		let fProfileBoardOwnerId = () => (this.pict.views['Plansheet-Profile'] ? this.pict.views['Plansheet-Profile'].currentBoardId() : null);
		let tmpProfileBoardImageSource = new libMoodboardMediaImageSource(
			{
				MediaAPI: this.pict.providers.MediaAPI,
				getOwnerId: fProfileBoardOwnerId
			});
		// The Drawing card's picker: the team's Excalidraw drawings + this board's gallery.
		let tmpProfileBoardDrawingSource = new libMoodboardDrawingSource(
			{
				MediaAPI: this.pict.providers.MediaAPI,
				getOwnerId: fProfileBoardOwnerId
			});
		// The sticker library is customer-scoped (the bundled set + the customer's uploads), but the set
		// shown BEFORE searching is per board context: profiles get the expressive 'moodboard' set,
		// design boards (vision inspiration + whiteboards) get 'drafting-design'. One source instance per
		// context, kept on the app so a vision board can swap to its kit's set later (Phase 2). The full
		// library is always one search away regardless of set. Set keys live in source/StickerSets.js.
		let fStickerSource = (pSetKey) => new libMoodboardStickerSource(
			{ StickerAPI: this.pict.providers.StickerAPI, ShapesAPI: this.pict.providers.ShapesAPI, SetKey: pSetKey });
		this._StickerSources =
		{
			profile: fStickerSource('moodboard'),
			vision: fStickerSource('drafting-design'),
			whiteboard: fStickerSource('drafting-design')
		};
		this.pict.addView('Profile-Board-Edit', Object.assign({}, libMoodboardSection.default_configuration,
			{
				ViewIdentifier: 'Profile-Board-Edit',
				AutoRender: false,
				// The editable instance is always the free canvas while editing (the moodboard's effective-
				// style model: an Editable board presents as the canvas whatever its stored style), so the
				// chosen jumbotron / background rides ViewState untouched and shows on the read-only banner.
				Editable: true,
				DefaultDestinationAddress: '#Profile-Board',
				ImageSource: tmpProfileBoardImageSource,
				DrawingSource: tmpProfileBoardDrawingSource,
				StickerSource: this._StickerSources.profile,
				onBoardChanged: (pBoard) => { if (this.pict.views['Plansheet-Profile']) { this.pict.views['Plansheet-Profile'].onBoardSaved(pBoard); } },
				// The display-style toggle + set-view-area live in the moodboard's own toolbar; the board
				// color + backdrop margin in its gear. The host only sizes the banner when the style changes.
				onDisplayStyleChanged: (pInfo) => { if (this.pict.views['Plansheet-Profile']) { this.pict.views['Plansheet-Profile'].onBoardDisplayStyleChanged(pInfo); } },
				Renderables: [ { RenderableHash: 'Moodboard-Container', TemplateHash: 'Moodboard-Container', ContentDestinationAddress: '#Profile-Board', RenderMethod: 'replace' } ]
			}), libMoodboardSection);
		this.pict.addView('Profile-Board-View', Object.assign({}, libMoodboardSection.default_configuration,
			{
				ViewIdentifier: 'Profile-Board-View',
				AutoRender: false,
				Editable: false,
				// Banner presentation: let the read-only fit zoom IN to frame a sparse board, so a single
				// sticker fills the banner instead of sitting tiny at 1:1. The editable canvas keeps the
				// true-to-scale fit (FitZoomIn only affects the read-only view).
				FitZoomIn: true,
				DefaultDestinationAddress: '#Profile-Board',
				// Double-clicking a card on your own board drops into edit (the Edit board button is the
				// primary affordance, next to Edit profile -- see PictView-Profile).
				onCardActivate: (pHash) => { if (this.pict.views['Plansheet-Profile']) { this.pict.views['Plansheet-Profile'].activateCard(pHash); } },
				onDisplayStyleChanged: (pInfo) => { if (this.pict.views['Plansheet-Profile']) { this.pict.views['Plansheet-Profile'].onBoardDisplayStyleChanged(pInfo); } },
				Renderables: [ { RenderableHash: 'Moodboard-Container', TemplateHash: 'Moodboard-Container', ContentDestinationAddress: '#Profile-Board', RenderMethod: 'replace' } ]
			}), libMoodboardSection);

		// Vision boards: an inspiration moodboard embedded on the Vision detail page. Same two-view
		// pattern as the profile banner -- an editable board (autosaving, for anyone with content.edit)
		// and a read-only one -- sharing the detail's #Vision-Board container. The image gallery is the
		// Media owned by the open board.
		let fVisionBoardOwnerId = () => (this.pict.views['Plansheet-EntityDetail'] ? this.pict.views['Plansheet-EntityDetail'].currentBoardId() : null);
		let tmpVisionBoardImageSource = new libMoodboardMediaImageSource(
			{
				MediaAPI: this.pict.providers.MediaAPI,
				getOwnerId: fVisionBoardOwnerId
			});
		let tmpVisionBoardDrawingSource = new libMoodboardDrawingSource(
			{
				MediaAPI: this.pict.providers.MediaAPI,
				getOwnerId: fVisionBoardOwnerId
			});
		this.pict.addView('Vision-Board-Edit', Object.assign({}, libMoodboardSection.default_configuration,
			{
				ViewIdentifier: 'Vision-Board-Edit',
				AutoRender: false,
				Editable: true,
				// Editing the inspiration board is a focused activity -- drop into the flow's fullscreen so
				// the canvas fills the viewport (and in-board popups have room). Leaving edit swaps back to
				// the read-only view, which unmounts this instance and drops the fullscreen wrapper.
				FullscreenOnEdit: true,
				DefaultDestinationAddress: '#Vision-Board',
				ImageSource: tmpVisionBoardImageSource,
				DrawingSource: tmpVisionBoardDrawingSource,
				StickerSource: this._StickerSources.vision,
				onBoardChanged: (pBoard) => { if (this.pict.views['Plansheet-EntityDetail']) { this.pict.views['Plansheet-EntityDetail'].onBoardSaved(pBoard); } },
				onDisplayStyleChanged: (pInfo) => { if (this.pict.views['Plansheet-EntityDetail']) { this.pict.views['Plansheet-EntityDetail'].onBoardDisplayStyleChanged(pInfo); } },
				Renderables: [ { RenderableHash: 'Moodboard-Container', TemplateHash: 'Moodboard-Container', ContentDestinationAddress: '#Vision-Board', RenderMethod: 'replace' } ]
			}), libMoodboardSection);
		this.pict.addView('Vision-Board-View', Object.assign({}, libMoodboardSection.default_configuration,
			{
				ViewIdentifier: 'Vision-Board-View',
				AutoRender: false,
				Editable: false,
				// Same as the profile banner: the read-only inspiration board zooms in to frame a sparse
				// board so it fills the banner rather than showing tiny at 1:1.
				FitZoomIn: true,
				DefaultDestinationAddress: '#Vision-Board',
				onDisplayStyleChanged: (pInfo) => { if (this.pict.views['Plansheet-EntityDetail']) { this.pict.views['Plansheet-EntityDetail'].onBoardDisplayStyleChanged(pInfo); } },
				Renderables: [ { RenderableHash: 'Moodboard-Container', TemplateHash: 'Moodboard-Container', ContentDestinationAddress: '#Vision-Board', RenderMethod: 'replace' } ]
			}), libMoodboardSection);

		// The top-bar composer (WI #360): the board a plan-sheet author arranges the bar's decoration on,
		// mounted into the Top Bar settings section. Unlike every other board here it belongs to the PLAN
		// SHEET, not to a record, so it has no entity id to own its media -- its gallery is the tenant-scoped
		// (OwnerType 'PlansheetTopBar', IDOwner 0) bucket, which the server resolves from the session (the
		// client never names a plan sheet). It gets the drafting sticker set, since a bar decoration is
		// mostly cutouts. The board is NOT autosaved: the author composes freely and the explicit "Set as
		// top bar" action is what bakes and stores it, so a half-arranged bar never lands on the chrome.
		let fTopBarBoardOwnerId = () => 0;
		let tmpTopBarImageSource = new libMoodboardMediaImageSource(
			{
				MediaAPI: this.pict.providers.MediaAPI,
				getOwnerId: fTopBarBoardOwnerId,
				OwnerType: 'PlansheetTopBar',
				AllowZeroOwner: true
			});
		let tmpTopBarDrawingSource = new libMoodboardDrawingSource(
			{
				MediaAPI: this.pict.providers.MediaAPI,
				getOwnerId: fTopBarBoardOwnerId,
				OwnerType: 'PlansheetTopBar',
				AllowZeroOwner: true
			});
		this.pict.addView('TopBar-Board-Edit', Object.assign({}, libMoodboardSection.default_configuration,
			{
				ViewIdentifier: 'TopBar-Board-Edit',
				AutoRender: false,
				Editable: true,
				DefaultDestinationAddress: '#Settings-TopBar-Board',
				ImageSource: tmpTopBarImageSource,
				DrawingSource: tmpTopBarDrawingSource,
				StickerSource: this._StickerSources.whiteboard,
				Renderables: [ { RenderableHash: 'Moodboard-Container', TemplateHash: 'Moodboard-Container', ContentDestinationAddress: '#Settings-TopBar-Board', RenderMethod: 'replace' } ]
			}), libMoodboardSection);

		// The standalone Whiteboards library page + the editor it opens boards into. The editor gets the
		// same Media-backed image gallery the vision board has, scoped to whichever board is open, so
		// photos (e.g. a snap of a physical whiteboard) upload and drop in.
		this.pict.addView('Plansheet-Whiteboards', libViewWhiteboards.default_configuration, libViewWhiteboards);
		this.pict.addView('Plansheet-StickerSetsPreview', libViewStickerSetsPreview.default_configuration, libViewStickerSetsPreview);
		// The contextual help drawer's content view, registered before the Shell renders so the shell's
		// right panel can bind it as its ContentView. Toggled by F1 / the top-bar "?" (see toggleHelpDrawer).
		this.pict.addView('Plansheet-HelpDrawer', libViewHelpDrawer.default_configuration, libViewHelpDrawer);
			this.pict.addView('Plansheet-Notebook', libViewNotebook.default_configuration, libViewNotebook);
		this.pict.addView('Plansheet-OpsConsole', libViewOpsConsole.default_configuration, libViewOpsConsole);
		this.pict.addView('Plansheet-Releases', libViewReleases.default_configuration, libViewReleases);
		this.pict.addView('Plansheet-Runs', libViewRuns.default_configuration, libViewRuns);
		this.pict.addView('Plansheet-Ask', libViewAsk.default_configuration, libViewAsk);
		this.pict.addView('Plansheet-Project', libViewProject.default_configuration, libViewProject);
		this.pict.addView('Plansheet-ProjectTypes', libViewProjectTypes.default_configuration, libViewProjectTypes);
		this.pict.addView('Plansheet-CapabilityActivation', libViewCapabilityActivation.default_configuration, libViewCapabilityActivation);
		this.pict.addView('Plansheet-OrgActivity', libViewOrgActivity.default_configuration, libViewOrgActivity);
		this.pict.addView('Plansheet-SystemActivity', libViewSystemActivity.default_configuration, libViewSystemActivity);
		// The reusable activity-graph section, configured for plansheet's two domains and pointed at the
		// System Activity page's chart slot. The granularity switcher calls back to refetch that period.
		this.pict.addView('Plansheet-SystemActivity-Chart', Object.assign({}, libSectionActivityGraph.default_configuration,
			{
				ActivityGraph: Object.assign({}, libSectionActivityGraph.default_configuration.ActivityGraph,
					{
						Period: 'Week', Title: '',
						Columns:
						[
							{ Key: 'WorkItems', Title: 'Work Items', Accent: '#e0891e' },
							{ Key: 'Knowledge', Title: 'Knowledge', Accent: '#3f7fd0' }
						],
						PeriodChangeHandler: (pPeriod) => { let tmpView = this.pict.views['Plansheet-SystemActivity']; if (tmpView) { tmpView.selectPeriod(pPeriod); } }
					}),
				Renderables:
				[
					{ RenderableHash: 'Pict-ActivityGraph-Container', TemplateHash: 'Pict-ActivityGraph-Container', ContentDestinationAddress: '#SystemActivity-Chart', RenderMethod: 'replace' }
				]
			}), libSectionActivityGraph);
		this.pict.addView('Plansheet-IssueBoard', libViewIssueBoard.default_configuration, libViewIssueBoard);
		this.pict.addView('Plansheet-FeatureBoard', libViewFeatureBoard.default_configuration, libViewFeatureBoard);
		this.pict.addView('Plansheet-DeliverableBoard', libViewDeliverableBoard.default_configuration, libViewDeliverableBoard);
		this.pict.addView('Plansheet-CallDesk', libViewCallDesk.default_configuration, libViewCallDesk);
		this.pict.addView('Plansheet-VisionLoops', libViewVisionLoops.default_configuration, libViewVisionLoops);
		// The searchable picker provider backs the connections linker (type + candidate pickers).
		this.pict.addProvider('Pict-Section-Picker', libPictSectionPicker.default_configuration, libPictSectionPicker);
		this.pict.addView('Plansheet-Connections', libViewConnections.default_configuration, libViewConnections);
		// The personal "Checklists" page (the user's private lists). Renders into the workspace; the
		// work-item embed uses a second instance with its own destination.
		this.pict.addView('Plansheet-Checklists', Object.assign({}, libViewChecklists.default_configuration,
			{
				Mode: 'mine',
				Heading: 'Checklists',
				DefaultDestinationAddress: '#Plansheet-Workspace',
				Renderables: [{ RenderableHash: 'Plansheet-Checklists', TemplateHash: 'Plansheet-Checklists', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }]
			}), libViewChecklists);
		// The work-item embed: the SHARED checklists on a work item's detail page. EntityDetail configures
		// and mounts this per work item into its #Entity-Checklists container.
		this.pict.addView('Plansheet-Checklists-Embed', Object.assign({}, libViewChecklists.default_configuration,
			{
				Mode: 'owner',
				Heading: 'Checklists',
				DefaultDestinationAddress: '#Entity-Checklists',
				Renderables: [{ RenderableHash: 'Plansheet-Checklists', TemplateHash: 'Plansheet-Checklists', ContentDestinationAddress: '#Entity-Checklists', RenderMethod: 'replace' }]
			}), libViewChecklists);
		// The notebook's Checklists tab: the user's personal lists embedded in the left drawer. A second
		// 'mine' instance with its own destination, so it coexists with the full /checklists page.
		this.pict.addView('Plansheet-Notebook-Checklists', Object.assign({}, libViewChecklists.default_configuration,
			{
				Mode: 'mine',
				Heading: 'Checklists',
				Compact: true,
				DefaultDestinationAddress: '#PS-Notebook-Checklists',
				Renderables: [{ RenderableHash: 'Plansheet-Checklists', TemplateHash: 'Plansheet-Checklists', ContentDestinationAddress: '#PS-Notebook-Checklists', RenderMethod: 'replace' }]
			}), libViewChecklists);
		let fWhiteboardPageOwnerId = () => (this.pict.views['Plansheet-Whiteboards'] ? this.pict.views['Plansheet-Whiteboards'].currentBoardId() : null);
		let tmpWhiteboardPageImageSource = new libMoodboardMediaImageSource(
			{
				MediaAPI: this.pict.providers.MediaAPI,
				getOwnerId: fWhiteboardPageOwnerId
			});
		let tmpWhiteboardPageDrawingSource = new libMoodboardDrawingSource(
			{
				MediaAPI: this.pict.providers.MediaAPI,
				getOwnerId: fWhiteboardPageOwnerId
			});
		this.pict.addView('Whiteboard-Page-Edit', Object.assign({}, libMoodboardSection.default_configuration,
			{
				ViewIdentifier: 'Whiteboard-Page-Edit',
				AutoRender: false,
				Editable: true,
				DefaultDestinationAddress: '#Whiteboard-Page-Canvas',
				ImageSource: tmpWhiteboardPageImageSource,
				DrawingSource: tmpWhiteboardPageDrawingSource,
				StickerSource: this._StickerSources.whiteboard,
				onBoardChanged: (pBoard) => { if (this.pict.views['Plansheet-Whiteboards']) { this.pict.views['Plansheet-Whiteboards'].onBoardSaved(pBoard); } },
				Renderables: [ { RenderableHash: 'Moodboard-Container', TemplateHash: 'Moodboard-Container', ContentDestinationAddress: '#Whiteboard-Page-Canvas', RenderMethod: 'replace' } ]
			}), libMoodboardSection);
		// A read-only board mounted into the full-screen overlay (openBoardFullscreen). It presents the
		// board's stored display style, so a photo whiteboard (jumbotron) shows full-bleed edge to edge.
		this.pict.addView('Whiteboard-Fullscreen-View', Object.assign({}, libMoodboardSection.default_configuration,
			{
				ViewIdentifier: 'Whiteboard-Fullscreen-View',
				AutoRender: false,
				Editable: false,
				DefaultDestinationAddress: '#Whiteboard-Fullscreen-Canvas',
				Renderables: [ { RenderableHash: 'Moodboard-Container', TemplateHash: 'Moodboard-Container', ContentDestinationAddress: '#Whiteboard-Fullscreen-Canvas', RenderMethod: 'replace' } ]
			}), libMoodboardSection);

		// Comments: an embedded discussion section on the Vision and WorkItem detail pages, sharing the
		// detail's #Entity-Comments container. It persists through PlansheetCommentProvider (the
		// CommentThread + Comment tables, tenant-scoped). EntityDetail sets the Context + CurrentUser +
		// read-only per mount. Inline markdown composer by default; 'rich' opens the full editor in a modal.
		let tmpCommentProvider = new libPlansheetCommentProvider(this.pict);
		this.pict.addView('Plansheet-Comments', Object.assign({}, libCommentsSection.default_configuration,
			{
				ViewIdentifier: 'Plansheet-Comments',
				AutoRender: false,
				DefaultDestinationAddress: '#Entity-Comments',
				Threaded: true,
				EditorMode: 'markdown',
				Title: 'Comments',
				DataProvider: tmpCommentProvider,
				Renderables: [ { RenderableHash: 'Comments-Section', TemplateHash: 'Comments-Section', ContentDestinationAddress: '#Entity-Comments', RenderMethod: 'replace' } ]
			}), libCommentsSection);

		// Prompt management: the pict-section-prompteditor section (My Library inside
		// the Prompt Library page) persisted through PlansheetPromptProvider - the
		// Prompt/WordList/GeneratedPrompt tables, owner-scoped and version-captured
		// server-side. The library page feeds it the merged shape cascade (platform
		// defaults shadowed by org adoptions plus the user's own shapes) and the
		// signed-in CurrentUser on every show. CodeMirror rides the same
		// window.CodeMirrorModules bundle the doc editors use, so rich segment
		// editing works out of the box.
		let tmpPromptProvider = new libPlansheetPromptProvider(this.pict);
		this.pict.addView('Plansheet-PromptEditor', Object.assign({}, libPromptEditorSection.default_configuration,
			{
				ViewIdentifier: 'Plansheet-PromptEditor',
				AutoRender: false,
				DefaultDestinationAddress: '#PromptLibrary-Editor-Container',
				Title: 'My prompt library',
				DataProvider: tmpPromptProvider,
				Renderables: [ { RenderableHash: 'PromptEditor-Section', TemplateHash: 'PromptEditor-Section', ContentDestinationAddress: '#PromptLibrary-Editor-Container', RenderMethod: 'replace' } ]
			}), libPromptEditorSection);
		this.pict.addView('Plansheet-PromptLibrary', libViewPromptLibrary.default_configuration, libViewPromptLibrary);
		this.pict.addView('Plansheet-PromptShapes', libViewPromptShapes.default_configuration, libViewPromptShapes);
		this.pict.addView('Plansheet-PromptAttachments', libViewPromptAttachments.default_configuration, libViewPromptAttachments);

		// 4. The vendored Claude session viewer.
		//
		//    The viewer is a set of cooperating Pict views that render into
		//    #ClaudeTransformer-Workspace (mounted lazily by
		//    _mountTransformerContainer). The hashes are fixed: the views
		//    cross-reference each other by name (e.g. _Pict.views.Conversation
		//    in onclick handlers, this.pict.views.Layout from siblings), so
		//    they must register as Dropzone/Layout/Signature/Timeline/
		//    Conversation/ToolDetail/AgentDetail exactly.
		//
		//    The flow: a signed-in user who drops a session zip has it uploaded
		//    to their account by the Dropzone (via the SessionAPI provider),
		//    which then returns to the refreshed calendar. Clicking a stored
		//    session downloads its blob and renders it through onSessionLoaded.
		this.pict.addView('Dropzone',     libViewCTDropzone.default_configuration,     libViewCTDropzone);
		this.pict.addView('Layout',       libViewCTLayout.default_configuration,       libViewCTLayout);
		this.pict.addView('Signature',    libViewCTSignature.default_configuration,    libViewCTSignature);
		this.pict.addView('Timeline',     libViewCTTimeline.default_configuration,     libViewCTTimeline);
		this.pict.addView('Conversation', libViewCTConversation.default_configuration, libViewCTConversation);
		this.pict.addView('ToolDetail',   libViewCTToolDetail.default_configuration,   libViewCTToolDetail);
		this.pict.addView('AgentDetail',  libViewCTAgentDetail.default_configuration,  libViewCTAgentDetail);
		// The one section plansheet drives directly (showDropzone /
		// loadSessionFromZip); see openServerSession / showUploader below.
		this.pict.addView('Pict-Section-ClaudeTransformer',
			libPictSectionClaudeTransformer.default_configuration, libPictSectionClaudeTransformer);
		// The vendored Layout/Signature open detail popups through
		// _Pict.views.PictSectionModal; alias it to plansheet's modal section.
		this.pict.views.PictSectionModal = this.pict.views['Pict-Section-Modal'];

		// 5. Theme — last, so its TopBar can wire the slot view ids above.
		//    The brand palettes register into the theme catalog before the
		//    provider boots: Plan Sheet is the default pair (vellum in light
		//    mode, night drafting in dark, following the system), and the
		//    Blueprint + Drafting Paper singles and the Southwest desert set join the picker. The bundled
		//    catalog is cleared first so only these curated palettes show, not the ~45 pict-section-theme
		//    ships. Amber in the drafting palettes is measurement ink:
		//    dimensions, revisions, warnings, nothing else.
		// pict-section-theme 1.x ships ~45 themes in its catalog; clear them so the picker shows only the
		// curated set below. The runtime provider is fed from this catalog at boot (RegisterCatalog default).
		libPictSectionTheme.Catalog.clear();
		libPictSectionTheme.Catalog.register({ Hash: 'plansheet', Category: 'Plansheet', Bundle: require('./themes/plansheet.json') });
		libPictSectionTheme.Catalog.register({ Hash: 'plansheet-blueprint', Category: 'Plansheet', Bundle: require('./themes/plansheet-blueprint.json') });
		libPictSectionTheme.Catalog.register({ Hash: 'plansheet-paper', Category: 'Plansheet', Bundle: require('./themes/plansheet-paper.json') });
		// The Southwest desert set, adapted from the palettes Ultravisor ships in pict-section-theme.
		libPictSectionTheme.Catalog.register({ Hash: 'southwest-desert-day', Category: 'Southwest', Bundle: require('./themes/southwest-desert-day.json') });
		libPictSectionTheme.Catalog.register({ Hash: 'southwest-desert-dusk', Category: 'Southwest', Bundle: require('./themes/southwest-desert-dusk.json') });
		libPictSectionTheme.Catalog.register({ Hash: 'southwest-desert-sunset', Category: 'Southwest', Bundle: require('./themes/southwest-desert-sunset.json') });
		libPictSectionTheme.Catalog.register({ Hash: 'southwest-desert-canyon', Category: 'Southwest', Bundle: require('./themes/southwest-desert-canyon.json') });
		// Showcase set: each a light+dark pair. Colours live in the bundle; the per-theme font + signature
		// (Win9x bevels, etc.) ride the html[data-theme=...] marker the apply hook below stamps.
		libPictSectionTheme.Catalog.register({ Hash: 'high-contrast', Category: 'Accessible', Bundle: require('./themes/high-contrast.json') });
		libPictSectionTheme.Catalog.register({ Hash: 'plain', Category: 'Accessible', Bundle: require('./themes/plain.json') });
		libPictSectionTheme.Catalog.register({ Hash: 'nostalgia', Category: 'Retro', Bundle: require('./themes/nostalgia.json') });
		libPictSectionTheme.Catalog.register({ Hash: '1990s-website', Category: 'Retro', Bundle: require('./themes/1990s-website.json') });
		libPictSectionTheme.Catalog.register({ Hash: 'london-subway', Category: 'Retro', Bundle: require('./themes/london-subway.json') });
		libPictSectionTheme.Catalog.register({ Hash: 'cyberpunk', Category: 'Neon', Bundle: require('./themes/cyberpunk.json') });
		libPictSectionTheme.Catalog.register({ Hash: 'neo-tokyo', Category: 'Neon', Bundle: require('./themes/neo-tokyo.json') });
		this.pict.addProvider('Theme-Section',
			{
				ApplyDefault: 'plansheet',
				DefaultMode:  'system',
				DefaultScale: 1.0,
				Brand:        libBrand,
				Views: ['Picker', 'ModeToggle', 'ScaleSelect', 'BrandMark', 'TopBar'],
				ViewOptions:
				{
					// Optgroup order in the picker: the Plansheet drafting palettes first, then the Southwest set.
					Picker: { Categories: ['Plansheet', 'Southwest', 'Accessible', 'Retro', 'Neon'] },
					TopBar: { NavView: 'Plansheet-TopBar-Nav', UserView: 'Plansheet-TopBar-User', Height: 56 }
				}
			}, libPictSectionTheme);

			// pict-section-theme 1.x sets the .theme-dark / .theme-light class only for an EXPLICIT mode; in
			// 'system' mode it switches the CSS vars via @media but leaves the class off. Plansheet's shell and
			// the moodboard note key their dark styling off .theme-dark, so reflect the RESOLVED mode as that
			// class on every apply (the system-follows-OS case included). One hook restores the always-set-class
			// behavior 0.0.3 had, so every html.theme-dark rule keeps working without a prefers-color-scheme copy.
			let tmpThemeRuntime = this.pict.providers.Theme;
			if (tmpThemeRuntime && typeof tmpThemeRuntime.onApply === 'function')
			{
				let fReflectThemeMode = () =>
				{
					let tmpActive = (typeof tmpThemeRuntime.getActiveTheme === 'function') ? tmpThemeRuntime.getActiveTheme() : null;
					let tmpResolved = tmpActive && tmpActive.ResolvedMode;
					if (typeof document !== 'undefined' && document.documentElement)
					{
						document.documentElement.classList.toggle('theme-dark', tmpResolved === 'dark');
						document.documentElement.classList.toggle('theme-light', tmpResolved === 'light');
						// The active theme hash drives per-theme fonts + signatures (html[data-theme=...]) in the shell CSS.
						document.documentElement.setAttribute('data-theme', (tmpActive && tmpActive.Hash) || '');
					}
				};
				tmpThemeRuntime.onApply(fReflectThemeMode);
				fReflectThemeMode(); // the boot apply fired during addProvider, before this listener attached
			}

		// 6. Router — hash-based stable URLs (pict-router). Routes register now
		//    but only resolve after auth (SkipRouteResolveOnAdd), so a pasted
		//    deep-link lands once signed in (via navigateCurrent in _showWorkspace).
		//    The router also keeps document.title in step with the route (a page title plus the
		//    " - Plansheet" suffix, falling back to "Plansheet"), which is what makes bookmarks and the
		//    recent-pages history read as real page names instead of a constant "Plansheet".
		this.pict.addProvider('Pict-Router', { SkipRouteResolveOnAdd: true, DefaultTitle: 'Plansheet', TitleSuffix: ' - Plansheet' }, libPictRouter);
		this._setupRoutes();
	}

	onAfterInitializeAsync(fCallback)
	{
		// Render the shell — creates #Plansheet-Workspace, which the rest
		// of the views render into.
		this.pict.views['Plansheet-Shell'].render();

		// Friendly "checking session…" splash while we ask the server.
		let tmpWorkspace = document.getElementById('Plansheet-Workspace');
		if (tmpWorkspace) tmpWorkspace.textContent = 'Loading…';

		// Kick off the OAuth provider list (a small JSON fetch) in parallel with the session check, but make
		// the login screen WAIT for it before its first paint so the Google / GitHub buttons are present on
		// the very first render. The earlier "paint now, repaint when the list lands" approach had a race: on
		// a cold load (uncached JS, slower everything) the list could arrive in the window between showAuth()
		// rendering and the view's initialRenderComplete flag flipping, so the repaint was skipped and the
		// buttons never appeared until a reload. Waiting is bounded (3s) so a slow or failed provider fetch
		// never blocks sign-in -- the form just shows without the OAuth buttons in that worst case.
		// --- TEMP DIAGNOSTIC ([auth-boot]) for the OAuth-button cold-load issue: trace the provider-fetch vs
		// auth-render ordering so a cold load on prod prints the actual sequence in the console. Remove once
		// confirmed (grep "auth-boot"). performance.now() is ms since page load, so the numbers show how long
		// after load each step happened. ---
		let fAuthLog = (pMsg) => { try { console.log('[auth-boot] +' + Math.round((typeof performance !== 'undefined' ? performance.now() : 0)) + 'ms ' + pMsg); } catch (pErr) { /* no-op */ } };
		let fProviderCount = () => ((this.pict.AppData.Auth && this.pict.AppData.Auth.Providers) ? this.pict.AppData.Auth.Providers.length : 0);

		let tmpLoadProviders = this.pict.providers.AuthClient.loadProviders().catch(() => {});
		tmpLoadProviders.then(() => { fAuthLog('providers resolved: ' + fProviderCount()); });
		let tmpProvidersReady = Promise.race(
			[
				tmpLoadProviders,
				new Promise((resolve) => { setTimeout(resolve, 3000); })
			]);
		// Belt-and-suspenders: if the provider list lands AFTER the login screen is already on-screen (only
		// possible if it took longer than the bound above), repaint the auth screen so its buttons appear.
		// Keyed on a DOM check (is the sign-in screen up?) rather than a view flag, so it is reliable even on
		// a cold load -- the flag-based version of this had a race where the repaint was skipped.
		tmpLoadProviders.then(() =>
		{
			if (typeof document !== 'undefined' && document.body && document.body.classList.contains('plansheet-auth-mode'))
			{
				fAuthLog('late repaint (providers arrived after login shown): ' + fProviderCount());
				let tmpAuthView = this.pict.views['Plansheet-Auth'];
				if (tmpAuthView) { tmpAuthView.render(); }
			}
		});
		// A magic-link URL (#/magic/<token>) is resolved BEFORE the normal auth gate: the recipient clicks
		// it signed out, so a plain checkSession would just show the login screen. Verify the token (the hooks
		// sign in, run the SMS 2FA step, or fall back to the login screen with an error), and skip the gate.
		// Purely additive -- every other load falls through to the unchanged checkSession below.
		let tmpMagicToken = this._magicTokenFromURL();
		if (tmpMagicToken)
		{
			fAuthLog('magic-link token present; verifying');
			this._handleMagicLink(tmpMagicToken);
			return super.onAfterInitializeAsync(fCallback);
		}
		// An invite URL (#/invite/<token>): auto-accept if already signed in with the matching email, else show
		// the sign-in screen and accept right after they sign in (onAuthChanged). Additive, same as magic links.
		let tmpInviteToken = this._inviteTokenFromURL();
		if (tmpInviteToken)
		{
			fAuthLog('invite token present; handling');
			this._handleInviteToken(tmpInviteToken);
			return super.onAfterInitializeAsync(fCallback);
		}
		this.pict.providers.AuthClient.checkSession().then((pLoggedIn) =>
		{
			fAuthLog('checkSession: loggedIn=' + (!!pLoggedIn));
			if (this.pict.views['Plansheet-TopBar-User']) this.pict.views['Plansheet-TopBar-User'].render();
			if (pLoggedIn)
			{
				this._enableSessionRecovery();
				// If the user pinned an explicit default plan sheet on this device, switch into it before
				// rendering the workspace. The server already remembers the last-visited plan sheet via
				// the LastLoginDate stamp on every switch; the pin setting overrides that for hard
				// affinity. If the switch fails (no longer a member) the pin is cleared automatically.
				this._applyPinnedCustomerIfNeeded().then(() => this._showWorkspace());
			}
			else
			{
				tmpProvidersReady.then(() => { fAuthLog('showAuth(login) -- providers in AppData: ' + fProviderCount()); this.showAuth('login'); });
			}
		}).catch(() =>
		{
			tmpProvidersReady.then(() => { fAuthLog('showAuth(login) [after checkSession error] -- providers: ' + fProviderCount()); this.showAuth('login'); });
		});

		return super.onAfterInitializeAsync(fCallback);
	}

	// ── SDLC workspace navigation (URL-driven via pict-router) ───────────────
	// Public show* methods set the URL; the route handlers render. The browser
	// URL is the single source of truth, so links are copy/paste stable and
	// back / forward / pasted deep-links all work.

	_router() { return this.pict.providers['Pict-Router']; }
	// navigo's own navigate() matches the route and calls its handler BEFORE it writes the new
	// hash to the browser (updateBrowserURL is the LAST middleware in its chain) -- so a route
	// handler that reads window.location.hash while it renders still sees the PREVIOUS route's
	// hash, query string included. Board and the entity list both do exactly that, to honor a
	// shared/reloaded link (FocusFilters.stateFromHash): clicking Board -> Feedback with a
	// "recent" window active left window.location.hash reading "#/board?recent=1d" while the
	// Feedback route handler ran, so it read the Board's filter as its own landing state (#356).
	//
	// The #356 fix silently wrote the destination hash first via history.REPLACESTATE, reasoning
	// that navigo's own updateBrowserURL would then push the same value, netting exactly one new
	// history entry. That reasoning was wrong: replaceState does not "reserve" an entry, it
	// overwrites whatever is CURRENTLY on top of the stack -- the entry for the page being LEFT.
	// So every navigation silently rewrote the outgoing page's history entry to the destination
	// and then navigo pushed a duplicate of that same destination on top of it. Two entries in a
	// row always read the same page, so pressing Back once landed back on a duplicate of the page
	// you were already on (no visible change) instead of the page you came from -- Back had to be
	// pressed twice to go anywhere, and the entry that used to name the previous page was gone
	// (#363). Fix: pre-write the hash ourselves with PUSHSTATE (a real new entry, so the outgoing
	// entry is left untouched), then tell navigo not to write the URL again (updateBrowserURL:
	// false) since we already did. Falls back to the old (still #356-safe) behavior if the raw
	// navigo instance is not reachable.
	// RETURNS WHETHER THE ROUTE ACTUALLY RESOLVED. The router declines to re-run a handler for the route
	// it is already on, which is right -- but it used to do so silently, so a caller could not tell
	// "went there" from "did nothing" and code that assumed it had moved left the page where it was.
	// pict-router 1.0.13 reports that; this passes the answer up, and refuses to push a history entry
	// for a navigation that is not going to happen (a duplicate entry is the #363 Back-does-nothing bug
	// in miniature). One guard, here, instead of a hash comparison hand-rolled into every caller.
	// Whether the router has ALREADY RESOLVED this exact route, matched the way the router matches:
	// path and query string both. Not the same question as "does the address bar say this".
	_routerIsOn(pRoute)
	{
		let tmpRouter = this._router();
		if (!tmpRouter || typeof tmpRouter.lastMatch !== 'function') { return false; }
		let tmpMatch = tmpRouter.lastMatch();
		if (!tmpMatch) { return false; }
		let tmpWanted = String(pRoute || '/').replace(/^#/, '').replace(/^\/+/, '');
		let tmpSplit = tmpWanted.indexOf('?');
		let tmpPath = (tmpSplit >= 0) ? tmpWanted.slice(0, tmpSplit) : tmpWanted;
		let tmpQuery = (tmpSplit >= 0) ? tmpWanted.slice(tmpSplit + 1) : '';
		return (String(tmpMatch.url || '') === tmpPath) && (String(tmpMatch.queryString || '') === tmpQuery);
	}

	_navigate(pRoute)
	{
		let tmpRouter = this._router();
		if (!tmpRouter) { return false; }
		let tmpHash = '#' + String(pRoute || '/').replace(/^#/, '');
		// ASK THE ROUTER, NOT THE ADDRESS BAR. Whether a navigation will do anything is the router's
		// state to answer: the hash can name a route the router has NOT resolved (boot into a pasted
		// deep link, or a sign-out/sign-in where the hash survived), and reading the hash here refused
		// those navigations and left the page blank. Skipping only when the router really is on this
		// route also keeps the history clean, since a duplicate entry is the #363 Back-does-nothing bug.
		if (this._routerIsOn(pRoute)) { return false; }
		if (typeof window !== 'undefined' && window.location && window.history && typeof window.history.pushState === 'function'
			&& tmpRouter.router && typeof tmpRouter.navigate === 'function')
		{
			window.history.pushState(null, '', (window.location.pathname || '') + (window.location.search || '') + tmpHash);
			// updateBrowserURL: false because the line above already wrote it (#363).
			return tmpRouter.navigate(pRoute, { updateBrowserURL: false }) !== false;
		}
		if (typeof window !== 'undefined' && window.location && window.history && typeof window.history.replaceState === 'function')
		{
			window.history.replaceState(null, '', (window.location.pathname || '') + (window.location.search || '') + tmpHash);
		}
		return tmpRouter.navigate(pRoute) !== false;
	}
	// Entity Key -> route slug. These MUST stay in sync with the tmpLists / tmpDetails route tables in
	// _setupRoutes. A key missing here silently falls back to a bare lowercased slug, which for any multi-word
	// key matches no registered route at all: AsBuilt fell back to "asbuilt" while its route is "as-built", so
	// Open and row-click on the As-Builts list did nothing, with no error.
	_pluralRoute(pKey) { return ({ Vision: 'visions', Module: 'modules', Feature: 'features', WorkItem: 'work-items', Routine: 'routines', TestRun: 'test-runs', Feedback: 'feedback', KeystoneCustomer: 'keystone-customers', Issue: 'issues', Contact: 'contacts', Call: 'calls', IssueType: 'issue-types', Expectation: 'expectations', Deliverable: 'deliverables', Session: 'sessions', Blueprint: 'blueprints', AsBuilt: 'as-builts', DeployRequest: 'deploy-requests', NodeRegistration: 'node-registrations' })[pKey] || String(pKey).toLowerCase(); }
	// ASKS _DETAIL_SLUGS FIRST, because that map is what actually REGISTERS /<slug>/:id. This used to be
	// a second hand-maintained copy of the same knowledge, and it drifted. An entity was added to
	// _LIST_SLUGS, to _DETAIL_SLUGS and to _pluralRoute, and missed here, so its row click fell through
	// to String(Key).toLowerCase() and navigated to a hyphen-less route nobody had registered. The
	// router matched nothing and the pane never changed: clicking a row simply did nothing, with no
	// error and no blank page. Edit worked the whole time, because showEntityEdit reads _detailSlugFor.
	//
	// A silent no-op is the worst shape this can take: no error, no blank pane, no 404, just a click that
	// does not land. Deriving from the registration removes the second copy that can be wrong.
	//
	// The literal map that remains is for routes registered SEPARATELY from _DETAIL_SLUGS -- TestRun opens
	// in the runner via its own tmpRouter.addRoute('/test-run/:id') -- so it is a list of exceptions now
	// rather than a duplicate of the rule.
	_singularRoute(pKey) { return _detailSlugFor(pKey) || ({ TestRun: 'test-run' })[pKey] || String(pKey).toLowerCase(); }

	// "work-items" -> "Work Items" -- a readable document title from a route slug.
	_titleCaseSlug(pSlug) { return String(pSlug || '').split('-').map((pPart) => pPart ? (pPart.charAt(0).toUpperCase() + pPart.slice(1)) : pPart).join(' '); }

	// The tenant-safe "feature area" root for a route. Switching, creating, or leaving a plan sheet reloads
	// the app under a different active tenant; a record-specific route (a document path, an entity id) points
	// at a record that tenant does not have, so a blind reload lands on a not-found screen -- the reported
	// route leak. Reduce the route to the feature area it belongs to (the list / overview page every tenant
	// has) and drop the record-specific tail. Every value returned here is a parameterless route, so it can
	// never itself not-found in the new tenant. An unrecognized route falls back to the board. See WI #328.
	//
	// _TenantDetailRouteToArea below mirrors the record routes registered in _setupRoutes: the singular /:id
	// detail routes (head is the singular slug, area is the plural list) plus the id-bearing specials. Keep it
	// in step when those routes change -- a head missing here falls back to the board, which stays safe but is
	// coarser than the intended area.
	_tenantSafeRoute(pRoutePath)
	{
		let tmpDetailRouteToArea =
		{
			// entity detail (singular slug) -> its list page (plural slug)
			'vision': '/visions', 'module': '/modules', 'feature': '/features', 'work-item': '/work-items',
			'routine': '/routines', 'keystone-customer': '/keystone-customers', 'issue': '/issues',
			'contact': '/contacts', 'call': '/calls', 'issue-type': '/issue-types', 'blueprint': '/blueprints',
			'expectation': '/expectations', 'deliverable': '/deliverables', 'as-built': '/as-builts',
			'deploy-request': '/deploy-requests', 'node-registration': '/node-registrations',
			// id-bearing routes that render outside the generic detail pane
			'test-run': '/test-runs', 'session': '/sessions', 'prompt': '/prompts', 'prompt-shape': '/prompt-shapes',
			'whiteboard': '/whiteboards', 'note': '/notes', 'drawing': '/drawings', 'doc-edit': '/docs',
			// the media viewer has no list page of its own
			'media': '/board'
		};
		let tmpPath = String(pRoutePath == null ? '' : pRoutePath).trim().replace(/^#/, '').split('?')[0];
		if (tmpPath.charAt(0) !== '/') { tmpPath = '/' + tmpPath; }
		let tmpSegments = tmpPath.split('/').filter((pSegment) => pSegment.length > 0);
		if (!tmpSegments.length) { return '/board'; }
		let tmpHead = tmpSegments[0];
		// A record route whose area root is spelled differently from its head (work-item -> work-items, ...).
		if (tmpDetailRouteToArea[tmpHead]) { return tmpDetailRouteToArea[tmpHead]; }
		// Two-segment feature areas that are themselves parameterless roots: keep them whole.
		if (tmpSegments.length >= 2)
		{
			let tmpTwo = tmpHead + '/' + tmpSegments[1];
			if (tmpTwo === 'admin/ops' || tmpTwo === 'org/activity') { return '/' + tmpTwo; }
		}
		// Otherwise the head is a single-segment feature area (/docs, /work-items, /settings, /feedback, ...):
		// keep the head and drop any record tail (/docs/<path>, /workflow/subject/:id, /settings/:section/:id,
		// /profile/:id, /prompt-shapes/new, /sessions/upload, ...).
		return '/' + tmpHead;
	}

	// Reduce the current route to its tenant-safe feature area (dropping any record-specific tail), then
	// reload. Call this on every active-plan-sheet change -- switch, create, leave, join, and the pinned-tenant
	// auto-switch on load -- so the reload never carries a record route into a tenant that does not have that
	// record (the reported not-found on switch). See WI #328 and _tenantSafeRoute.
	reloadForActivePlansheetChange()
	{
		if (typeof window === 'undefined' || !window.location) { return; }
		try
		{
			let tmpCurrent = String(window.location.hash || '').replace(/^#/, '');
			let tmpSafe = this._tenantSafeRoute(tmpCurrent);
			if (tmpSafe) { window.location.hash = tmpSafe; }
		}
		catch (pError) { /* fall through to a plain reload */ }
		window.location.reload();
	}

	_setupRoutes()
	{
		let tmpRouter = this._router();
		if (!tmpRouter || typeof tmpRouter.addRoute !== 'function') { return; }
		tmpRouter.addRoute('/', () => this._renderBoard(), 'Board');
		tmpRouter.addRoute('/board', () => this._renderBoard(), 'Board');
		// The cross-tenant "Your plan sheets" home (WI #339): the default landing after sign-in and a
		// tenant-safe area root (a single-segment route, so _tenantSafeRoute keeps it whole on a switch).
		tmpRouter.addRoute('/home', () => this._renderHome(), 'Your plan sheets');
		let tmpLists = _LIST_SLUGS;
		// List pages title from the route slug (Work Items, Test Runs, ...); detail pages set their own title
		// from the loaded record (see PictView-EntityDetail), so those pass no static title.
		Object.keys(tmpLists).forEach((pPlural) => tmpRouter.addRoute('/' + pPlural, () => this._renderList(tmpLists[pPlural]), this._titleCaseSlug(pPlural)));
		let tmpDetails = _DETAIL_SLUGS;
		// THE CREATE AND EDIT FORMS ARE PLACES. They used to render in place at the list's own URL, so a
		// reload dropped a half-written record without warning, Back left the whole list screen instead of
		// closing the form, and the address bar said "list" while the pane showed a form. Same shape as
		// /drawings/new and /prompt-shapes/new, which have been routes for exactly this reason.
		//
		// REGISTERED BEFORE /<singular>/:id, AND THAT ORDER IS LOAD-BEARING. One slug is both a plural
		// and a singular: 'feedback'. So /feedback/new has the same shape as /feedback/:id, this router
		// takes the first route registered for a shape, and with the detail route first every "+ New"
		// on the feedback list resolved as the DETAIL of a record whose id parsed to NaN -- a not-found
		// pane where the create form should be. A literal segment cannot swallow a real id, so putting
		// the literal first is safe for every other slug.
		Object.keys(tmpLists).forEach((pPlural) => tmpRouter.addRoute('/' + pPlural + '/new',
			() => this._renderListNew(tmpLists[pPlural])));
		Object.keys(tmpDetails).forEach((pSing) => tmpRouter.addRoute('/' + pSing + '/:id',
			(pData) => this._renderDetail(tmpDetails[pSing], Number(pData && pData.data && pData.data.id))));
		Object.keys(tmpDetails).forEach((pSing) => tmpRouter.addRoute('/' + pSing + '/:id/edit',
			(pData) => this._renderListEdit(tmpDetails[pSing], Number(pData && pData.data && pData.data.id))));
		// The Node workspace (WI #465): a focused single-node screen (lifecycle actions + Provision), not the
		// generic detail pane. /node/new is the Provision create body; register it BEFORE /node/:id so the literal
		// 'new' is not parsed as an id (the same load-bearing order as /feedback/new above).
		tmpRouter.addRoute('/node/new', () => this._renderNodeCreate(), 'Provision Node');
		tmpRouter.addRoute('/node/:id', (pData) => this._renderNodeWorkspace(Number(pData && pData.data && pData.data.id)), 'Node');
		// Test runs open in the runner, not the generic detail pane.
		tmpRouter.addRoute('/test-run/:id', (pData) => this._renderTestRun(Number(pData && pData.data && pData.data.id)));
		tmpRouter.addRoute('/work-item/:id/blueprint', (pData) => this._renderBlueprint(Number(pData && pData.data && pData.data.id)));
		// The same full page for every other blueprintable entity (WI #340). Registered per type rather than
		// as /:type/:id/blueprint because this router matches on SEGMENT COUNT, and a wildcard first segment
		// at three segments would start swallowing routes that have nothing to do with blueprints.
		tmpRouter.addRoute('/vision/:id/blueprint', (pData) => this._renderBlueprint(Number(pData && pData.data && pData.data.id), 'Vision'));
		tmpRouter.addRoute('/module/:id/blueprint', (pData) => this._renderBlueprint(Number(pData && pData.data && pData.data.id), 'Module'));
		tmpRouter.addRoute('/feature/:id/blueprint', (pData) => this._renderBlueprint(Number(pData && pData.data && pData.data.id), 'Feature'));
		tmpRouter.addRoute('/media/:id', (pData) => this._renderMediaViewer(Number(pData && pData.data && pData.data.id)));
		// A blueprint opened as itself. It used to fall into the generic detail loop above, which gave it
		// a record pane with a Description box and no contents -- the blueprint's own page was the one
		// place in the product that could not show a blueprint. Registered here, and removed from that
		// loop, because this router matches the first route registered for a shape.
		tmpRouter.addRoute('/blueprint/:id', (pData) => this._renderBlueprintRecord(Number(pData && pData.data && pData.data.id)));
		// The generic record pane, still reachable: name, description, documentation, comments. It is no
		// longer what /blueprint/:id opens, because a blueprint's own page should show the blueprint.
		tmpRouter.addRoute('/blueprint/:id/details', (pData) => this._renderDetail('Blueprint', Number(pData && pData.data && pData.data.id)));
		tmpRouter.addRoute('/blueprint/:id/compose', (pData) => this._renderBlueprintComposer(Number(pData && pData.data && pData.data.id)));
		tmpRouter.addRoute('/blueprint/:id/design', (pData) => this._renderBlueprintStudio(Number(pData && pData.data && pData.data.id)));
		// Sessions: the viewer (a stored session) and the uploader are routed so
		// they are copy/paste-linkable and survive a reload. They used to render
		// imperatively, which left the URL stuck on /sessions.
		tmpRouter.addRoute('/session/:id', (pData) => this.openServerSession(Number(pData && pData.data && pData.data.id)));
		tmpRouter.addRoute('/sessions/upload', () => this._renderUploader(), 'Upload Session');
		tmpRouter.addRoute('/docs', () => this._renderDocsOverview(), 'Docs');
		tmpRouter.addRoute('/docs/*', (pData) => this._renderDoc(this._docPathFromMatch(pData)));
		// The full-page doc editor (over the Docs Lake). Wildcard because the doc
		// path carries slashes; ownerType/idOwner ride in front of it.
		tmpRouter.addRoute('/doc-edit/*', (pData) => this._renderDocEditor(this._docEditFromMatch(pData)));
		tmpRouter.addRoute('/workflow', () => this._renderWorkflow(), 'Workflow');
		// A workflow subject open in its detail tab is its own route, so a reload keeps it open (it used to
		// reset to the default catalog tab). showSubject fetches by id, so a cold deep-link still resolves.
		tmpRouter.addRoute('/workflow/subject/:id', (pData) => this._renderWorkflowSubject(Number(pData && pData.data && pData.data.id)));
		// The other three tabs are pages in their own right, the same way the subject already is.
		tmpRouter.addRoute('/workflow/:tab', (pData) => this._renderWorkflowTab(String((pData && pData.data && pData.data.tab) || 'catalog')), 'Workflow');
		tmpRouter.addRoute('/prompts', () => this._renderPromptLibrary(), 'Prompt Library');
		// A shared prompt open in the library's reader is its own route, so a reload keeps it open (and it
		// can be linked to a teammate) instead of resetting to the default tab.
		tmpRouter.addRoute('/prompt/:id', (pData) => this._renderPrompt(Number(pData && pData.data && pData.data.id)));
		// Which tab of the library you are on is a place: your own library, the shared one, your shapes.
		// Registered after /prompts and /prompt/:id so neither is shadowed.
		tmpRouter.addRoute('/prompts/:tab', (pData) => this._renderPromptLibraryTab(String((pData && pData.data && pData.data.tab) || 'library')), 'Prompt Library');
		tmpRouter.addRoute('/prompt-shapes', () => this._renderPromptShapes(), 'Prompt Shapes');
		// The standalone shape editor (new + existing) is its own route, so an in-progress edit survives a
		// reload. Only the standalone page is routed; the shape editor embedded in the Prompt Library / org
		// settings stays inline (it is not a page of its own).
		tmpRouter.addRoute('/prompt-shapes/new', () => this._renderPromptShapeEditor(0));
		tmpRouter.addRoute('/prompt-shape/:id', (pData) => this._renderPromptShapeEditor(Number(pData && pData.data && pData.data.id)));
		tmpRouter.addRoute('/community', () => this._renderCommunity(), 'Community');
		tmpRouter.addRoute('/whiteboards', () => this._renderWhiteboards(), 'Whiteboards');
		// One board open in the editor is its own route, so it deep-links and survives a reload (the board
		// used to open imperatively from the list, leaving the URL on /whiteboards -- a reload dropped you
		// back to the list, which is easy to lose in a big list). Detail title is set from the loaded board.
		tmpRouter.addRoute('/whiteboard/:id', (pData) => this._renderWhiteboardEditor(Number(pData && pData.data && pData.data.id)));
		// The read-only full-screen "peek" of a board is its own route too, so a reload keeps it up (it is
		// opened from the whiteboards list, the editor, and entity-detail boards). Closing returns to where
		// it was opened from (a cold deep-link has no origin, so it falls back to /whiteboards).
		tmpRouter.addRoute('/whiteboard/:id/fullscreen', (pData) => this._renderBoardFullscreen(Number(pData && pData.data && pData.data.id)));
		tmpRouter.addRoute('/sticker-sets-preview', () => this._renderStickerSetsPreview(), 'Sticker Sets');
		tmpRouter.addRoute('/checklists', () => this._renderChecklists(), 'Checklists');
		tmpRouter.addRoute('/notes', () => this._renderNotesPage(), 'Notes');
		// The note open in the editor pane is its own route, so a reload keeps it open (the selection used
		// to live only in memory, so a reload emptied the editor and lost any unsaved title/body draft).
		tmpRouter.addRoute('/note/:id', (pData) => this._renderNote(Number(pData && pData.data && pData.data.id)));
		tmpRouter.addRoute('/bookmarks', () => this._renderBookmarksPage(), 'Bookmarks');
		tmpRouter.addRoute('/gallery', () => this._renderGalleryPage(), 'Gallery');
		tmpRouter.addRoute('/drawings', () => this._renderDrawingsPage(), 'Drawings');
		// One drawing open in the Excalidraw editor is its own route (new + existing), so the open canvas
		// deep-links and survives a reload instead of vanishing back to /drawings.
		tmpRouter.addRoute('/drawings/new', () => this._renderDrawingEditor(0));
		tmpRouter.addRoute('/drawing/:id', (pData) => this._renderDrawingEditor(Number(pData && pData.data && pData.data.id)));
		tmpRouter.addRoute('/admin/ops', () => this._renderOpsConsole(), 'Ops Console');
		tmpRouter.addRoute('/releases', () => this._renderReleases(), 'Releases');
		tmpRouter.addRoute('/runs', () => this._renderRuns(), 'Runs');
		tmpRouter.addRoute('/ask', () => this._renderAsk(), 'Ask');
		// Each Ask state is a route so Back and reload work: the task's input form and an in-flight/answered
		// request both deep-link instead of living only in the view's memory (which broke the Back button).
		tmpRouter.addRoute('/ask/task/:id', (pData) => this._renderAskTask(Number(pData && pData.data && pData.data.id)), 'Ask');
		tmpRouter.addRoute('/ask/request/:id', (pData) => this._renderAskRequest(Number(pData && pData.data && pData.data.id)), 'Ask');
		tmpRouter.addRoute('/projects', () => this._renderProjects(), 'Projects');
		tmpRouter.addRoute('/project/:id', (pData) => this._renderProject(Number(pData && pData.data && pData.data.id)), 'Projects');
		tmpRouter.addRoute('/project-types', () => this._renderProjectTypes(), 'Project Types');
		tmpRouter.addRoute('/project-type/:id', (pData) => this._renderProjectType(Number(pData && pData.data && pData.data.id)), 'Project Types');
		tmpRouter.addRoute('/capabilities', () => this._renderCapabilityActivation(), 'CapabilityActivation');
		tmpRouter.addRoute('/org/activity', () => this._renderOrgActivity(), 'Activity');
		tmpRouter.addRoute('/system-activity', () => this._renderSystemActivity(), 'System Activity');
		// The granularity you are looking at is part of what the page IS, so it belongs in the URL.
		tmpRouter.addRoute('/system-activity/:period', (pData) => this._renderSystemActivity(String((pData && pData.data && pData.data.period) || '')), 'System Activity');
		tmpRouter.addRoute('/issues', () => this._renderIssues(), 'Issues');
		tmpRouter.addRoute('/feature-board', () => this._renderFeatureBoard(), 'Feature Board');
		tmpRouter.addRoute('/deliver', () => this._renderDeliverBoard(), 'Deliver');
		tmpRouter.addRoute('/call-desk', () => this._renderCallDesk(), 'Call Desk');
		// One customer's desk is a place: their contacts, their call history, the log-a-call form.
		tmpRouter.addRoute('/call-desk/:id', (pData) => this._renderCallDeskCustomer(Number(pData && pData.data && pData.data.id)), 'Call Desk');
		tmpRouter.addRoute('/settings/:section/:id', (pData) => this._renderSettingsSection(pData && pData.data && pData.data.section, pData && pData.data && pData.data.id));
		tmpRouter.addRoute('/settings/:section', (pData) => this._renderSettingsSection(pData && pData.data && pData.data.section, null));
		tmpRouter.addRoute('/settings', () => this._renderUserSettings());
		tmpRouter.addRoute('/profile', () => this._renderProfile());
		tmpRouter.addRoute('/profile/:id', (pData) => this._renderProfileFor(Number(pData && pData.data && pData.data.id)));
	}

	// The matched doc path (with optional ::section) from Navigo's match data. A
	// document path has slashes that a single-segment :param can't hold, so we
	// take the wildcard tail from pData.url. Reading it from the match (not from
	// window.location.hash, which lags during the re-entrant navigate that
	// syncDocURL fires) keeps the /docs route idempotent and loop-free.
	_docPathFromMatch(pData)
	{
		let tmpUrl = (pData && pData.url) || '';
		let tmpRest = String(tmpUrl).replace(/^\/?docs\/?/, '');
		try { tmpRest = decodeURIComponent(tmpRest); } catch (pError) { /* leave as-is */ }
		return tmpRest;
	}

	// Parse /doc-edit/<OwnerType>/<IDOwner>/<doc/path/with/slashes> from the match.
	_docEditFromMatch(pData)
	{
		let tmpUrl = (pData && pData.url) || '';
		let tmpRest = String(tmpUrl).replace(/^\/?doc-edit\/?/, '');
		let tmpParts = tmpRest.split('/');
		let tmpOwnerType = tmpParts.shift() || '';
		let tmpIDOwner = Number(tmpParts.shift()) || 0;
		let tmpPath = tmpParts.join('/');
		try { tmpPath = decodeURIComponent(tmpPath); } catch (pError) { /* leave as-is */ }
		return { OwnerType: tmpOwnerType, IDOwner: tmpIDOwner, Path: tmpPath };
	}

	_setActiveNav(pKey)
	{
		// App-owned record of the active workspace. AppData.Nav is not always populated, so _renderDoc keys
		// its idempotency skip off this instead (see the comment there).
		this._activeNavKey = pKey;
		if (this.pict.AppData.Nav) { this.pict.AppData.Nav.Active = pKey; }
		// Any page route sets active nav; the fullscreen-board route deliberately does not. So this is a
		// reliable point to take the board-fullscreen overlay down when navigating to a normal page (the
		// router's navigate does not always fire hashchange).
		this._syncBoardFullscreenToRoute();
		// Same for the board's card tooltips, which anchor to cards this navigation is about to paint
		// over. Decided from pKey rather than the URL: the router runs its route handler BEFORE it
		// updates the browser URL, so the hash still reads the board here. Opening a card is exactly
		// that path -- one click both pins a tooltip and routes to the detail view -- and a pinned
		// tooltip ignores the pointer, so it would otherwise ride along to the next page.
		if (pKey !== 'Board') { this._teardownBoardTooltips(); }
		// Same leak, same fix, for the Work Item hover tooltips Releases anchors to its "WI-080" links
		// (WI #452): once another view paints over the workspace those anchors are gone for good.
		if (pKey !== 'Releases') { this._teardownReleasesTooltips(); }
		// Reflect the current URL in the docked site nav (highlights the active item).
		this._syncSiteNav();
	}

	// Take the board's card tooltips down. They anchor to card elements, so once another view owns the
	// workspace the anchor is detached and can no longer fire the mouseleave that would have hidden the
	// tooltip: it stays up, floating over whatever page came next, naming a record that page does not
	// even list. Destroying the handle also stops them accumulating across board visits.
	_teardownBoardTooltips()
	{
		let tmpBoard = this.pict.views['Plansheet-Board'];
		if (tmpBoard && typeof tmpBoard.teardown === 'function') { tmpBoard.teardown(); }
	}

	// Take Releases' own Work Item tooltips down on the way to another page, mirroring _teardownBoardTooltips.
	_teardownReleasesTooltips()
	{
		let tmpReleases = this.pict.views['Plansheet-Releases'];
		if (tmpReleases && typeof tmpReleases.teardown === 'function') { tmpReleases.teardown(); }
	}

	// The URL-driven net, for the routes that set no active nav (the blueprint, media and doc views).
	// By the time hashchange fires the URL is settled, so here the hash is the honest answer.
	_syncBoardTooltipsToRoute()
	{
		if (typeof window === 'undefined' || !window.location) { return; }
		// Compare the path only: the board carries its focus filter as a hash query (#/board?assignee=me),
		// and that is still the board. '' and '/' both mean the board too.
		let tmpRoute = (window.location.hash || '').replace(/^#/, '').split('?')[0];
		if (tmpRoute === '' || tmpRoute === '/' || tmpRoute === '/board') { return; }
		this._teardownBoardTooltips();
	}

	// Whether the signed-in user has an entitlement (platform admins have everything; a falsy ent is
	// always allowed). Drives which admin items appear in the site map.
	_can(pEnt)
	{
		if (!pEnt) { return true; }
		let tmpUser = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
		if (tmpUser.IsPlatformAdmin === 1 || tmpUser.IsPlatformAdmin === true) { return true; }
		let tmpEnts = Array.isArray(tmpUser.Entitlements) ? tmpUser.Entitlements : [];
		return tmpEnts.indexOf(pEnt) >= 0;
	}

	// The nav entries the plan sheet owner hid from the sidebar, resolved onto the session at boot (set
	// from Org Settings). buildSiteMap drops these from the sidebar, the settings rail, and the launcher.
	_hiddenNavItems()
	{
		let tmpUser = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
		return Array.isArray(tmpUser.HiddenNavItems) ? tmpUser.HiddenNavItems : null;
	}

	// Build + install the site-map graph, render the docked main sidebar (the non-settings groups),
	// and arm the cmd+K launcher. Called once the workspace is shown (entitlements known).
	_installSiteNav()
	{
		let tmpNav = this.pict.providers['Pict-Navigation'];
		if (!tmpNav) { return; }
		let tmpLex = (this.pict.AppData.Auth && this.pict.AppData.Auth.User && this.pict.AppData.Auth.User.Lexicon) || null;
		tmpNav.setNavigationGraph(libSiteMap.buildSiteMap((pEnt) => this._can(pEnt), tmpLex, this._hiddenNavItems()));
		if (typeof tmpNav.bindLauncherHotkey === 'function') { tmpNav.bindLauncherHotkey(); }
		this._installHelpHotkey();
		this._renderRail();
		this._installMobileMenu();
		this._installBrandMarkLink();
		this._applyHouseStickerSet();
		this.applyNavVisibility();
	}

	// ── Contextual help drawer ─────────────────────────────────────────────────────────────────────
	// F1 and the top-bar "?" toggle the shell's hidden right "help" panel, which shows help for the
	// current screen. Document/window-level listeners only -- the allowed exception, the same pattern
	// the cmd+K launcher uses (Pict-Provider-Navigation bindLauncherHotkey) -- never element handlers.
	_installHelpHotkey()
	{
		if (this._HelpHotkeyBound || typeof document === 'undefined') { return; }
		let tmpSelf = this;
		document.addEventListener('keydown', (pEvent) =>
		{
			if (pEvent.key === 'F1') { pEvent.preventDefault(); tmpSelf.toggleHelpDrawer(); }
			else if (pEvent.key === 'Escape' && tmpSelf._isHelpDrawerOpen()) { tmpSelf.closeHelpDrawer(); }
			else if ((pEvent.metaKey || pEvent.ctrlKey) && !pEvent.shiftKey && !pEvent.altKey && (pEvent.key === '\\' || ((pEvent.key === 'b' || pEvent.key === 'B') && !tmpSelf._isEditableTarget(pEvent.target))))
			{
				// Cmd/Ctrl+B (VS Code) and Cmd/Ctrl+\\ (Notion) show/hide the sidebar. B yields to a focused
				// editor so it stays Bold while typing; backslash never conflicts, so it always toggles.
				pEvent.preventDefault();
				tmpSelf._toggleSidebar();
			}
		}, true);
		// Keep an already-open drawer in sync as the user navigates (the panel only re-renders its view
		// on expand, not on route change); hashchange fires after the hash has updated.
		if (typeof window !== 'undefined')
		{
			window.addEventListener('hashchange', () =>
			{
				tmpSelf.recordCurrentView();
				// Leaving the board-fullscreen route (Close, Escape, or any nav) takes the overlay down.
				tmpSelf._syncBoardFullscreenToRoute();
				// Leaving the board takes its card tooltips down, so none is left floating over the next page.
				tmpSelf._syncBoardTooltipsToRoute();
				if (!tmpSelf._isHelpDrawerOpen()) { return; }
				let tmpView = tmpSelf.pict.views['Plansheet-HelpDrawer'];
				if (tmpView && typeof tmpView.refreshForRoute === 'function') { tmpView.refreshForRoute(); }
			});
		}
		this._HelpHotkeyBound = true;
	}

	// Record a visit to the current route in the notebook history. The label is the current
	// document title (same source bookmarks use), so history reads as real page names. Detail
	// pages set their specific title only after the record loads, so this is called both on
	// navigation (the hashchange handler above) and again by PictView-EntityDetail once the
	// record is in hand; the NotebookAPI provider collapses the two into one relabeled row.
	recordCurrentView()
	{
		if (typeof window === 'undefined') { return; }
		let tmpProvider = this.pict.providers.NotebookAPI;
		if (!tmpProvider || typeof tmpProvider.recordView !== 'function') { return; }
		let tmpRoute = (window.location && window.location.hash) ? window.location.hash.replace(/^#/, '') : '';
		let tmpLabel = this._pageLabelFromTitle((typeof document !== 'undefined' && document.title) ? document.title : '');
		tmpProvider.recordView({ Route: tmpRoute, Label: tmpLabel });
	}

	// A clean notebook label from the document title. The router appends the site suffix (its
	// TitleSuffix, " - Plansheet") so the browser tab reads "Board - Plansheet", but History, Trending
	// and pinned Bookmarks read better as the bare page name ("Board"). Strip one trailing suffix if
	// present; a titleless route (whose title is the bare DefaultTitle) has no suffix and passes through
	// unchanged. (Work Item #234 -- the suffix was making every history row read "... - Plansheet".)
	_pageLabelFromTitle(pTitle)
	{
		let tmpLabel = String(pTitle || '');
		let tmpRouter = this._router();
		let tmpSuffix = (tmpRouter && tmpRouter.options && tmpRouter.options.TitleSuffix) ? tmpRouter.options.TitleSuffix : '';
		if (tmpSuffix && tmpLabel.length > tmpSuffix.length && tmpLabel.slice(-tmpSuffix.length) === tmpSuffix)
		{
			tmpLabel = tmpLabel.slice(0, tmpLabel.length - tmpSuffix.length);
		}
		return tmpLabel;
	}

	// Set document.title to a page's own name once it is known (often only after an async load),
	// then re-record the notebook history for the current route so it reads distinctly. Same move
	// PictView-EntityDetail makes for entity pages; render methods whose title depends on loaded data
	// (or a route with no static title) call this so History/Trending never fall back to "Plansheet".
	_setPageTitle(pTitle)
	{
		let tmpRouter = this._router();
		if (tmpRouter && typeof tmpRouter.setDocumentTitle === 'function') { tmpRouter.setDocumentTitle(String(pTitle || '')); }
		this.recordCurrentView();
	}

	// A readable page name from a Docs Lake path: the last segment, extension stripped, separators
	// spaced, word-cased (e.g. "reference/pict/section-form.md" -> "Section Form").
	_docTitleFromPath(pPath)
	{
		let tmpBase = String(pPath || '').split('::')[0].split('#')[0].replace(/\/+$/, '');
		let tmpLast = tmpBase.slice(tmpBase.lastIndexOf('/') + 1).replace(/\.md$/i, '').replace(/[-_]+/g, ' ').trim();
		if (!tmpLast) { return 'Docs'; }
		return tmpLast.split(' ').map((pWord) => pWord ? (pWord.charAt(0).toUpperCase() + pWord.slice(1)) : pWord).join(' ');
	}

	// A readable title for a settings section slug; a few awkward slugs get a friendly label, the
	// rest are word-cased. Suffixed " Settings" so the History row says which screen it was.
	_settingsTitle(pSection)
	{
		let tmpSlug = String(pSection || 'profile').split('/')[0];
		let tmpOverrides = { docsync: 'Doc Sync', draftingkit: 'Drafting Kits', tokens: 'API Tokens', org: 'Organization', lanes: 'Workflow Lanes' };
		let tmpLabel = tmpOverrides[tmpSlug] || (tmpSlug.charAt(0).toUpperCase() + tmpSlug.slice(1));
		return tmpLabel + ' Settings';
	}

	_helpPanel()
	{
		let tmpShellView = this.pict.views['Plansheet-Shell'];
		let tmpShell = tmpShellView ? tmpShellView._shell : null;
		return (tmpShell && typeof tmpShell.getPanel === 'function') ? tmpShell.getPanel('help') : null;
	}

	_isHelpDrawerOpen() { let tmpPanel = this._helpPanel(); return !!(tmpPanel && tmpPanel.Collapsed === false); }
	openHelpDrawer()    { let tmpPanel = this._helpPanel(); if (tmpPanel) { tmpPanel.expand(); } }
	closeHelpDrawer()   { let tmpPanel = this._helpPanel(); if (tmpPanel) { tmpPanel.collapse(); } }
	toggleHelpDrawer()  { let tmpPanel = this._helpPanel(); if (tmpPanel) { tmpPanel.toggle(); } }

	_notebookPanel()
	{
		let tmpShellView = this.pict.views['Plansheet-Shell'];
		let tmpShell = tmpShellView ? tmpShellView._shell : null;
		return (tmpShell && typeof tmpShell.getPanel === 'function') ? tmpShell.getPanel('notebook') : null;
	}

	_refreshNotebook() { let tmpView = this.pict.views['Plansheet-Notebook']; if (tmpView && typeof tmpView.refresh === 'function') { tmpView.refresh(); } }
	// The notebook is a mode of the shared rail now, not its own panel. Keep these method names so the
	// notebook's own close button and any other callers keep working; they drive the rail's mode.
	_railView() { return this.pict.views['Plansheet-Rail']; }
	_railMode() { let tmpRoot = (typeof document !== 'undefined') && document.getElementById('Plansheet-Rail-Root'); return (tmpRoot && tmpRoot.getAttribute('data-rail-mode')) || 'nav'; }
	_isNotebookOpen() { let tmpMode = this._railMode(); return tmpMode === 'notebook' || tmpMode === 'split'; }
	openNotebookDrawer()  { let tmpRail = this._railView(); if (tmpRail) { tmpRail.setMode('notebook'); this._refreshNotebook(); } }
	closeNotebookDrawer() { let tmpRail = this._railView(); if (tmpRail) { tmpRail.setMode('nav'); } }
	toggleNotebookDrawer() { let tmpRail = this._railView(); if (tmpRail) { tmpRail.setMode(this._isNotebookOpen() ? 'nav' : 'notebook'); this._refreshNotebook(); } }
	// The rail-view toggle: cycle the left rail's view (navigation / notebook / split). It does ONLY this.
	// Showing/hiding the whole sidebar is a separate control (toggleSidebar / Cmd+B); while the sidebar is
	// hidden there is nothing to cycle, so the button is disabled and this is a no-op guard for safety.
	cycleRail()
	{
		if (this.isNavHidden()) { return; }
		let tmpRail = this._railView();
		if (tmpRail) { tmpRail.cycle(); }
	}

	// The sidebar show/hide toggle (the top-bar button + Cmd+B / Cmd+\\ share this). One job: flip the
	// left rail's visibility. Icons and the rail-view button's enabled state resync via applyNavVisibility.
	toggleSidebar() { this._toggleSidebar(); }

	// Mount the notebook view into the rail's notebook pane the first time it becomes visible (lazy, so a
	// boot in nav mode does not pay for it). Idempotent.
	_ensureNotebookMounted()
	{
		if (this._notebookMounted) { return; }
		let tmpView = this.pict.views['Plansheet-Notebook'];
		if (!tmpView) { return; }
		this._notebookMounted = true;
		tmpView.render();
		if (typeof tmpView.refresh === 'function') { tmpView.refresh(); }
	}

	// Reflect the current rail mode on the top-bar toggle (icon + tooltip swap per mode).
	_updateRailToggle(pMode)
	{
		if (typeof document === 'undefined') { return; }
		let tmpBtn = document.getElementById('Plansheet-RailToggle');
		if (!tmpBtn) { return; }
		let tmpOpen = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">';
		let tmpIcons =
		{
			nav: tmpOpen + '<rect x="3" y="4" width="18" height="16" rx="2"></rect><line x1="9" y1="4" x2="9" y2="20"></line></svg>',
			notebook: tmpOpen + '<path d="M5 4h11a1 1 0 0 1 1 1v16H7a2 2 0 0 1-2-2z"></path><line x1="9" y1="4" x2="9" y2="21"></line></svg>',
			split: tmpOpen + '<rect x="3" y="4" width="18" height="16" rx="2"></rect><line x1="3" y1="12" x2="21" y2="12"></line></svg>'
		};
		let tmpTitles = { nav: 'Sidebar view: navigation', notebook: 'Sidebar view: notebook', split: 'Sidebar view: split' };
		tmpBtn.innerHTML = tmpIcons[pMode] || tmpIcons.nav;
		tmpBtn.setAttribute('title', (tmpTitles[pMode] || 'Sidebar view') + ' -- click to cycle');
		// Keep the companion show/hide button (and this button's disabled state) in step.
		this._updateSidebarToggle();
	}

	// Reflect sidebar visibility on the top-bar show/hide button, and disable the rail-view toggle while the
	// sidebar is hidden (there is no visible view to cycle). Called on boot, on every rail-view change, and
	// whenever visibility flips (applyNavVisibility), so the button icons match the Cmd+B keyboard path too.
	_updateSidebarToggle()
	{
		if (typeof document === 'undefined') { return; }
		let tmpHidden = this.isNavHidden();
		let tmpToggle = document.getElementById('Plansheet-SidebarToggle');
		if (tmpToggle)
		{
			let tmpOpen = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"></rect><line x1="9" y1="4" x2="9" y2="20"></line>';
			// A chevron pointing INTO the pane (hide) when shown, OUT of it (show) when hidden.
			tmpToggle.innerHTML = tmpOpen + (tmpHidden ? '<path d="M13 9l2.5 3-2.5 3"></path>' : '<path d="M15.5 9l-2.5 3 2.5 3"></path>') + '</svg>';
			tmpToggle.setAttribute('title', (tmpHidden ? 'Show sidebar' : 'Hide sidebar') + ' (Cmd+B)');
		}
		let tmpRailBtn = document.getElementById('Plansheet-RailToggle');
		if (tmpRailBtn)
		{
			tmpRailBtn.disabled = tmpHidden;
			tmpRailBtn.setAttribute('aria-disabled', tmpHidden ? 'true' : 'false');
		}
	}

	// Show/hide the whole left rail (the Cmd+B / Cmd+\\ shortcuts + the Appearance toggle share this). Uses
	// the same ps-nav-off mechanism as the Appearance "Hide the left navigation" setting, so the keyboard
	// toggle and the setting stay in step. The launcher (Cmd+K) still reaches everything while it is hidden.
	_toggleSidebar() { this.setNavHidden(!this.isNavHidden()); }

	// Is focus in a text editor or field? Cmd+B must yield to it so it stays Bold while the user types.
	_isEditableTarget(pEl)
	{
		if (!pEl) { return false; }
		let tmpTag = (pEl.tagName || '').toUpperCase();
		if (tmpTag === 'INPUT' || tmpTag === 'TEXTAREA' || tmpTag === 'SELECT') { return true; }
		if (pEl.isContentEditable) { return true; }
		if (typeof pEl.closest === 'function' && pEl.closest('[contenteditable="true"], .ProseMirror, .cm-editor')) { return true; }
		return false;
	}

	// The 9-square top-bar launcher opens the cmd+K command palette (the same overlay the hotkey toggles).
	openCommandLauncher()
	{
		let tmpNav = this.pict.providers['Pict-Navigation'];
		if (tmpNav && typeof tmpNav.toggleLauncher === 'function') { tmpNav.toggleLauncher(); }
	}

	// Left-nav visibility is a local (this-device) preference: a keyboard-first user can hide the docked
	// sidebar and lean on the launcher. Stored in localStorage, applied as a class on <html> the shell CSS
	// reads. The Appearance setting toggles it; applyNavVisibility re-applies it on boot.
	isNavHidden()
	{
		try { return (typeof localStorage !== 'undefined') && localStorage.getItem('plansheet.nav.hidden') === '1'; }
		catch (pError) { return false; }
	}
	applyNavVisibility()
	{
		if (typeof document === 'undefined' || !document.documentElement) { return; }
		if (this.isNavHidden()) { document.documentElement.classList.add('ps-nav-off'); }
		else { document.documentElement.classList.remove('ps-nav-off'); }
		// Keep the top-bar show/hide button icon and the rail-view button's disabled state in step, so the
		// Cmd+B / Cmd+\\ keyboard path updates the buttons the same as clicking them.
		this._updateSidebarToggle();
	}
	setNavHidden(pHidden)
	{
		try { if (pHidden) { localStorage.setItem('plansheet.nav.hidden', '1'); } else { localStorage.removeItem('plansheet.nav.hidden'); } }
		catch (pError) { /* ignore */ }
		this.applyNavVisibility();
	}

	// Point the whiteboard sticker source at the org house-kit's set (surfaced on the session at boot, so
	// any signed-in user can read it without the platform.manage kit endpoint). Vision boards resolve their
	// set per-vision via the lexicon; profiles keep the expressive moodboard set until a brand set is set.
	_applyHouseStickerSet()
	{
		let tmpUser = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || null;
		if (!tmpUser || !this._StickerSources) { return; }
		// A custom set ('custom:<id>') ships its resolved items on the session; a system key the source
		// resolves from the in-bundle registry. The house set feeds whiteboards, the org brand set feeds
		// profile moodboards. Each falls back to its source's context default when nothing is set.
		let fApply = (pSource, pKey, pItems) =>
		{
			if (!pSource || typeof pSource.setActiveSet !== 'function') { return; }
			let tmpHasItems = Array.isArray(pItems) && pItems.length;
			if (!pKey && !tmpHasItems) { return; }
			pSource.setActiveSet(tmpHasItems ? pItems : pKey);
		};
		fApply(this._StickerSources.whiteboard, tmpUser.StickerSet || '', tmpUser.StickerSetItems);
		fApply(this._StickerSources.profile, tmpUser.ProfileStickerSet || '', tmpUser.ProfileStickerSetItems);
	}

	// Render the shared left rail skeleton into #Plansheet-Sidebar, then mount the nav into its nav pane
	// and apply the saved mode (which lazily mounts the notebook the first time it becomes visible). Order
	// matters: the rail skeleton must exist before the nav renders into #Plansheet-RailNav.
	_renderRail()
	{
		let tmpRail = this.pict.views['Plansheet-Rail'];
		if (!tmpRail) { return; }
		tmpRail.render();
		// The sidebar is now one tabbed panel (the Notebook); Navigation is a tab inside it, so there is no
		// separate nav pane to fill and no nav/notebook/split mode to pick. Force the panel on and mount it;
		// the Notebook's default tab renders the docked site nav into its own Navigation host.
		tmpRail.setMode('notebook');
		// Point the Foundational Docs tree at the sidebar's Documentation host up front, so even the reader's
		// first init renders the tree there (not the now-hidden reader nav column).
		this._retargetDocsNav('#PS-Notebook-Documentation');
	}

	_renderSiteNav() { this.renderSiteNavInto('#Plansheet-RailNav'); }

	// Render the docked site-map navigation into a container. Default host is the rail's nav pane; the
	// Notebook's Navigation tab passes its own host (#PS-Notebook-Navigation) so the primary nav can live
	// as a sidebar tab. Scoped to the main groups plus Settings, so settings turn up in the sidebar search.
	renderSiteNavInto(pContainer)
	{
		let tmpNav = this.pict.providers['Pict-Navigation'];
		if (!tmpNav || typeof tmpNav.renderSidebarDocked !== 'function') { return; }
		if (typeof document !== 'undefined' && !document.querySelector(pContainer)) { return; }
		tmpNav.renderSidebarDocked(pContainer);
		this._SiteNavView = tmpNav.render('Sidebar-Docked', pContainer, { Scope: libSiteMap.DOCKED_GROUP_HASHES });
		this._syncSiteNav();
	}

	// ─── Foundational Docs tree in the sidebar's Documentation tab ───────
	// The inline-doc module ships the tree (InlineDoc-Nav) and the article (InlineDoc-Content) as two
	// views glued by a Layout. We leave the reader (FoundationDocs) owning the provider init + the article
	// pane, but re-point the Nav view to render into the sidebar's Documentation host instead of the
	// reader's nav column (which we hide), so the ToC lives in the sidebar and the reader is article-only.
	renderDocsTreeInto(pContainer)
	{
		this._retargetDocsNav(pContainer);
		// Opening the Documentation tab just SHOWS the tree -- it never changes the page. Ensure the
		// inline-doc provider is set up (which loads the sidebar tree, once), then render the tree into the
		// freshly repainted sidebar host. A tree-node click is what navigates to /docs/<path>.
		let tmpFdocs = this.pict.views['Plansheet-FoundationDocs'];
		if (!tmpFdocs || typeof tmpFdocs.ensureDocsProviderInited !== 'function') { return; }
		tmpFdocs.ensureDocsProviderInited().then(() =>
		{
			let tmpNav = this.pict.views['InlineDoc-Nav'];
			if (tmpNav && typeof tmpNav.render === 'function') { tmpNav.render(); }
		});
	}

	// Point the inline-doc Nav view at a container (the sidebar Documentation host). Safe to call repeatedly.
	_retargetDocsNav(pContainer)
	{
		let tmpNav = this.pict.views['InlineDoc-Nav'];
		if (!tmpNav || !tmpNav.options) { return; }
		if (Array.isArray(tmpNav.options.Renderables))
		{
			for (let i = 0; i < tmpNav.options.Renderables.length; i++) { tmpNav.options.Renderables[i].ContentDestinationAddress = pContainer; }
		}
		tmpNav.options.DefaultDestinationAddress = pContainer;
	}

	_onDocsRoute()
	{
		let tmpHash = (typeof window !== 'undefined' && window.location) ? (window.location.hash || '') : '';
		return /^#\/docs(\/|$)/.test(tmpHash);
	}

	_syncSiteNav()
	{
		let tmpNav = this.pict.providers['Pict-Navigation'];
		if (!tmpNav || typeof document === 'undefined') { return; }
		// Defer a tick: _setActiveNav runs inside the route handler, which can fire before the router has
		// finished writing window.location.hash, so reading it now can pick up the previous route.
		let tmpSelf = this;
		setTimeout(function ()
		{
			let tmpHash = (window.location && window.location.hash) ? window.location.hash : '';
			if (tmpHash && typeof tmpNav.setActiveByRoute === 'function') { tmpNav.setActiveByRoute(tmpHash); }
			if (tmpSelf._SiteNavView && typeof tmpSelf._SiteNavView.syncActive === 'function') { tmpSelf._SiteNavView.syncActive(); }
		}, 30);
	}

	// Route a site-nav item click. Main items go straight to their Route; a settings sub-section item
	// opens the settings page and switches to that section (settings is one route with in-page state).
	_onSiteNavigate(pNode)
	{
		if (!pNode || !pNode.Route) { return; }
		// Every item -- settings included -- routes straight to its hash now. Settings sections are
		// real #/settings/<section> routes that render full-width, so there is no special-case here.
		this._navigate(String(pNode.Route).replace(/^#/, ''));
	}

	// ── Mobile menu (the top-bar burger) ─────────────────────────────────────
	// Below 900px pict-section-theme collapses the bar into a burger. Its stock popup just clones the
	// top-bar slots, which on plansheet is only the account chip (the real nav lives in the docked
	// sidebar, hidden at this width) and the chip's anchored popover cannot open inside that modal.
	// Replace the burger with a real mobile menu: the full site-map navigation plus the account
	// actions (switch or create a plan sheet, sign out). Installed on the Theme-TopBar instance.
	_installMobileMenu()
	{
		let tmpSelf = this;
		let tmpTries = 0;
		let fInstall = function ()
		{
			let tmpTopBar = tmpSelf.pict.views['Theme-TopBar'];
			if (tmpTopBar)
			{
				tmpTopBar.openBurgerMenu = function () { return tmpSelf.openMobileMenu(); };
				return;
			}
			// The modal shell mounts the Theme-TopBar view a tick after the workspace first shows, so it
			// may not exist yet when this runs (the override would silently no-op). Retry briefly until
			// it does, then stop.
			if (tmpTries++ < 40 && typeof setTimeout === 'function') { setTimeout(fInstall, 50); }
		};
		fInstall();
	}

	// The "Plansheet" wordmark in the top bar (pict-section-theme's brand mark) renders as a plain
	// span with no link of its own. Open the marketing site in a new tab when it's clicked, the same
	// way _installMobileMenu waits for Theme-TopBar to mount before reaching into its DOM.
	_installBrandMarkLink()
	{
		let tmpSelf = this;
		let tmpTries = 0;
		let fInstall = function ()
		{
			let tmpMark = (typeof document !== 'undefined') ? document.getElementById('Theme-Brand-Mark') : null;
			if (tmpMark)
			{
				// _installSiteNav can run again in the same page load (sign-out then sign-in); guard
				// against binding a second click listener onto the same DOM node.
				if (tmpMark.dataset.brandLinkBound !== 'true')
				{
					// The brand mark is "home": it goes to the cross-tenant "Your plan sheets" screen (WI #339),
					// not the marketing site. Someone validating the app reaches for the logo to get back there.
					tmpMark.title = 'Your plan sheets';
					tmpMark.style.cursor = 'pointer';
					tmpMark.addEventListener('click', function ()
					{
						if (typeof tmpSelf._navigate === 'function') { tmpSelf._navigate('/home'); return; }
						if (typeof window !== 'undefined' && window.location) { window.location.hash = '#/home'; }
					});
					tmpMark.dataset.brandLinkBound = 'true';
				}
				return;
			}
			if (tmpTries++ < 40 && typeof setTimeout === 'function') { setTimeout(fInstall, 50); }
		};
		fInstall();
	}

	openMobileMenu()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		let fEscape = (pValue) => String(pValue == null ? '' : pValue)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
		let tmpLex = (this.pict.AppData.Auth && this.pict.AppData.Auth.User && this.pict.AppData.Auth.User.Lexicon) || null;
		let tmpMap = libSiteMap.buildSiteMap((pEnt) => this._can(pEnt), tmpLex, this._hiddenNavItems());
		// The docked-sidebar groups, plus the personal You group (My Profile, Settings).
		let tmpGroups = tmpMap.filter((pGroup) => libSiteMap.MAIN_GROUP_HASHES.indexOf(pGroup.Hash) >= 0 || pGroup.Hash === 'you');
		let tmpNavHTML = tmpGroups.map((pGroup) =>
			'<div class="cst-mnav-group"><div class="cst-mnav-group-h">' + fEscape(pGroup.Name) + '</div>' +
			(pGroup.Children || []).map((pItem) =>
				'<button class="cst-mnav-item" onclick="_Pict.PictApplication.mobileMenuGo(\'' + fEscape(pItem.Route) + '\')">' + fEscape(pItem.Name) + '</button>').join('') +
			'</div>').join('');
		// Account section. The invitations list and plan-sheet switcher are filled asynchronously by the
		// TopBar-User view, which targets these ids by getElementById; the desktop popover is not open at
		// this width, so there is no id collision.
		let tmpAcctHTML =
			'<div class="cst-mnav-group">' +
				'<div class="cst-mnav-group-h">Plan sheets</div>' +
				'<div class="cst-acct-invites" id="cst-acct-invites" style="display:none"></div>' +
				'<div class="cst-acct-tenant-list" id="cst-acct-tenant-list"><div class="cst-acct-tenant-empty">Loading&hellip;</div></div>' +
				'<button class="cst-acct-new" onclick="_Pict.views[\'Plansheet-TopBar-User\'].createTenant()">+ New plan sheet</button>' +
			'</div>' +
			'<div class="cst-mnav-group">' +
				'<button class="cst-mnav-item cst-mnav-danger" onclick="_Pict.PictApplication.signOut()">Sign out</button>' +
			'</div>';
		tmpModal.show({ title: 'Menu', content: '<div class="cst-mnav">' + tmpNavHTML + tmpAcctHTML + '</div>', closeable: true, buttons: [], width: '320px' });
		let tmpUserView = this.pict.views['Plansheet-TopBar-User'];
		if (tmpUserView && typeof tmpUserView._loadTenants === 'function') { tmpUserView._loadTenants(); }
		if (tmpUserView && typeof tmpUserView._loadInvitations === 'function') { tmpUserView._loadInvitations(); }
	}

	// ── media lightbox ────────────────────────────────────────────────────────
	// An unobtrusive full-screen quick view for a screenshot: the image in a modal
	// with basic zoom (buttons, wheel, double-click) and drag-to-pan. Self-contained,
	// no viewer library; the deep-zoom page (openseadragon) is a separate surface.
	viewImage(pURL, pTitle)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal) { return; }
		this._lightbox = { s: 1, x: 0, y: 0, drag: null };
		let tmpURL = String(pURL || '').replace(/"/g, '&quot;');
		let tmpAlt = String(pTitle || 'Screenshot').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
		let tmpBtn = 'font:inherit;font-size:16px;line-height:1;min-width:36px;height:32px;padding:0 11px;border-radius:7px;border:1px solid rgba(150,180,220,0.35);background:rgba(150,180,220,0.12);color:#eaf2fc;cursor:pointer;';
		let tmpHTML =
			'<div style="position:relative;">'
			+ '<div id="PS-Lightbox-Stage" style="position:relative;width:100%;height:78vh;overflow:hidden;background:#0b1420;border-radius:8px;display:flex;align-items:center;justify-content:center;cursor:grab;touch-action:none;">'
			+ '<img id="PS-Lightbox-Img" src="' + tmpURL + '" alt="' + tmpAlt + '" draggable="false" style="max-width:100%;max-height:100%;transform-origin:center center;user-select:none;-webkit-user-drag:none;">'
			+ '</div>'
			+ '<div style="position:absolute;left:50%;bottom:14px;transform:translateX(-50%);display:flex;gap:8px;align-items:center;background:rgba(12,20,32,0.86);border:1px solid rgba(150,180,220,0.3);border-radius:10px;padding:6px 9px;">'
			+ '<button type="button" title="Zoom out" style="' + tmpBtn + '" onclick="_Pict.PictApplication.lightboxZoom(0.8)">&minus;</button>'
			+ '<button type="button" title="Zoom in" style="' + tmpBtn + '" onclick="_Pict.PictApplication.lightboxZoom(1.25)">+</button>'
			+ '<button type="button" title="Reset" style="' + tmpBtn + 'font-size:12px;letter-spacing:0.5px;" onclick="_Pict.PictApplication.lightboxReset()">Reset</button>'
			+ '<a href="#" title="Open the deep-zoom viewer" style="' + tmpBtn + 'font-size:12px;letter-spacing:0.5px;text-decoration:none;display:inline-flex;align-items:center;border-color:rgba(240,196,74,0.6);color:#f0c44a;" onclick="event.preventDefault();_Pict.PictApplication.openMediaViewer(\'' + tmpURL + '\')">Open viewer</a>'
			+ '</div>'
			+ '</div>';
		tmpModal.show({ title: pTitle || 'Screenshot', content: tmpHTML, closeable: true, buttons: [], width: 'min(1200px, 94vw)' });
		let tmpSelf = this;
		setTimeout(function () { tmpSelf._lightboxInit(); }, 30);
	}

	_lightboxApply()
	{
		let tmpImg = (typeof document !== 'undefined') ? document.getElementById('PS-Lightbox-Img') : null;
		let tmpLB = this._lightbox;
		if (tmpImg && tmpLB) { tmpImg.style.transform = 'translate(' + tmpLB.x + 'px,' + tmpLB.y + 'px) scale(' + tmpLB.s + ')'; }
	}

	lightboxZoom(pFactor)
	{
		if (!this._lightbox) { return; }
		this._lightbox.s = Math.max(1, Math.min(8, this._lightbox.s * pFactor));
		if (this._lightbox.s === 1) { this._lightbox.x = 0; this._lightbox.y = 0; }
		this._lightboxApply();
	}

	lightboxReset() { if (this._lightbox) { this._lightbox.s = 1; this._lightbox.x = 0; this._lightbox.y = 0; this._lightboxApply(); } }

	_lightboxInit()
	{
		if (typeof document === 'undefined') { return; }
		let tmpStage = document.getElementById('PS-Lightbox-Stage');
		if (!tmpStage) { return; }
		let tmpSelf = this;
		tmpStage.addEventListener('wheel', function (pE) { pE.preventDefault(); tmpSelf.lightboxZoom(pE.deltaY < 0 ? 1.15 : 0.87); }, { passive: false });
		tmpStage.addEventListener('pointerdown', function (pE)
		{
			if (!tmpSelf._lightbox || tmpSelf._lightbox.s <= 1) { return; }
			tmpSelf._lightbox.drag = { sx: pE.clientX, sy: pE.clientY, ox: tmpSelf._lightbox.x, oy: tmpSelf._lightbox.y };
			try { tmpStage.setPointerCapture(pE.pointerId); } catch (pErr) { /* ignore */ }
			tmpStage.style.cursor = 'grabbing';
		});
		tmpStage.addEventListener('pointermove', function (pE)
		{
			let tmpD = tmpSelf._lightbox && tmpSelf._lightbox.drag;
			if (!tmpD) { return; }
			tmpSelf._lightbox.x = tmpD.ox + (pE.clientX - tmpD.sx);
			tmpSelf._lightbox.y = tmpD.oy + (pE.clientY - tmpD.sy);
			tmpSelf._lightboxApply();
		});
		let fEnd = function () { if (tmpSelf._lightbox) { tmpSelf._lightbox.drag = null; } tmpStage.style.cursor = 'grab'; };
		tmpStage.addEventListener('pointerup', fEnd);
		tmpStage.addEventListener('pointercancel', fEnd);
		tmpStage.addEventListener('dblclick', function () { if (tmpSelf._lightbox) { (tmpSelf._lightbox.s > 1) ? tmpSelf.lightboxReset() : tmpSelf.lightboxZoom(2.2); } });
	}

	// ── shared searchable picker ──────────────────────────────────────────────
	// Mount a searchable pict-section-picker into a host element, replacing an oversized
	// <select>. Reusable across surfaces (createPicker + render + OnChange). Returns the view.
	// The dropdown floats over its surroundings; pict-section-picker 1.8.2+ anchors the fixed dropdown
	// correctly even inside a transform-centered modal, so no per-modal .pps-pop override is needed.
	mountPicker(pHostSelector, pHash, pOptions, pOnChange, pOpts)
	{
		pOpts = pOpts || {};
		let tmpProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpProvider || typeof tmpProvider.createPicker !== 'function') { return null; }
		let tmpConfig =
		{
			DestinationAddress: pHostSelector,
			Placeholder: pOpts.Placeholder || 'Search...',
			Searchable: true,
			Options: pOptions || [],
			OnChange: (pVal) => { if (typeof pOnChange === 'function') { pOnChange(pVal); } }
		};
		// Server-backed search (WI #445): a caller with a candidate set bigger than one page passes
		// DataProvider/ResolveValue (see CandidateSearch.js) instead of a precomputed Options array.
		if (typeof pOpts.DataProvider === 'function') { tmpConfig.DataProvider = pOpts.DataProvider; }
		if (typeof pOpts.ResolveValue === 'function') { tmpConfig.ResolveValue = pOpts.ResolveValue; }
		// A nullable field (an fk, or a work item's target) needs a way back to "nothing chosen" -- the
		// widget's own pinned clear row (WI #446), not an Options-array "— none —" entry the DataProvider
		// path has no equivalent of.
		if (pOpts.AllowClear) { tmpConfig.AllowClear = true; tmpConfig.ClearLabel = pOpts.ClearLabel || 'Any'; }
		tmpProvider.createPicker(pHash, tmpConfig);
		// createPicker builds the view; render() paints it into the host.
		let tmpView = this.pict.views[pHash];
		if (tmpView) { tmpView.setValue(pOpts.Value || ''); tmpView.render(); }
		this._raisePickerAboveAncestors(pHostSelector);
		return tmpView;
	}

	// The picker's popup (.pps-pop) ships a fixed z-index of 40 -- fine as long as it is painted
	// inside a stacking context that itself out-ranks the rest of the page (a modal dialog, whose
	// own z-index is in the 1000s, is exactly that today). That is an assumption about DOM nesting,
	// not a guarantee: WI #355 ("the list of users to assign to is behind the modal in z-order")
	// is exactly what happens the moment that assumption stops holding, and nothing in the picker
	// or the modal enforces it. Belt-and-suspenders: after mounting, walk the host's ancestors for
	// the highest explicit z-index actually in play and pin the popup (and its click-outside
	// backdrop) comfortably above it, so a picker mounted inside a modal can never lose the z-order
	// race no matter how the surrounding markup changes.
	_raisePickerAboveAncestors(pHostSelector)
	{
		if (typeof document === 'undefined') { return; }
		let tmpHost = document.querySelector(pHostSelector);
		if (!tmpHost) { return; }
		let tmpMaxZ = 0;
		for (let tmpNode = tmpHost.parentElement; tmpNode; tmpNode = tmpNode.parentElement)
		{
			let tmpZ = parseInt(window.getComputedStyle(tmpNode).zIndex, 10);
			if (!isNaN(tmpZ) && tmpZ > tmpMaxZ) { tmpMaxZ = tmpZ; }
		}
		if (tmpMaxZ < 40) { return; }
		let tmpAbove = String(tmpMaxZ + 10);
		let tmpPop = tmpHost.querySelector('.pps-pop');
		let tmpBackdrop = tmpHost.querySelector('.pps-backdrop');
		if (tmpPop) { tmpPop.style.zIndex = tmpAbove; }
		if (tmpBackdrop) { tmpBackdrop.style.zIndex = tmpAbove; }
	}

	// A mobile-menu nav row: close the menu, then route through the normal site-nav handler.
	mobileMenuGo(pRoute)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.dismissModals === 'function') { tmpModal.dismissModals(); }
		this._onSiteNavigate({ Route: pRoute });
	}

	// ── route handlers — render the workspace for the current URL ────────────

	// True if the browser's current hash route is one of pRoutes. Async route handlers call this right
	// before they paint into #Plansheet-Workspace so a slow data load that resolves after the URL has
	// already moved on does not clobber the newer view. The live hash is the single source of truth.
	_routeIsActive(pRoutes)
	{
		if (typeof window === 'undefined' || !window.location) { return true; }
		let tmpHash = window.location.hash ? window.location.hash.replace(/^#/, '') : '';
		let tmpList = Array.isArray(pRoutes) ? pRoutes : [pRoutes];
		return tmpList.indexOf(tmpHash) >= 0;
	}

	// The board's data load is async, so guard the paint: if the URL has moved to another route by the
	// time the list resolves, do not render the board over it. This is what keeps a deep-link / hard
	// reload to a custom route (#/issues, #/checklists, ...) deterministic -- the board can no longer win
	// a boot race against the routed view. '' and '/' both mean the board.
	_renderBoard()
	{
		this._setActiveNav('Board');
		let tmpBoard = this.pict.views['Plansheet-Board'];
		// Seed the focus-view filter from the URL (a shared / reloaded #/board?... link) and load
		// the roster (for the human/AI assignee facet) alongside the board fetch, then narrow.
		tmpBoard.prepareFilter();
		Promise.all([this.pict.providers.BoardAPI.list(), tmpBoard.loadRoster()]).then(() =>
		{
			if (!this._routeIsActive(['', '/', '/board'])) { return; }
			tmpBoard.applyFilter();
			tmpBoard.render();
		});
	}
	_renderList(pKey) { this._setActiveNav(pKey); this.pict.views['Plansheet-EntityList'].showEntity(pKey); }
	// The create and edit forms, entered from their own routes. showEntity already knows how to land on
	// a form as its final render (it does it for the prefill handoff), so these pass that intent in
	// rather than opening the list and then racing it with a second call.
	_renderListNew(pKey) { this._setActiveNav(pKey); this.pict.views['Plansheet-EntityList'].showEntity(pKey, { New: true }); }
	_renderListEdit(pKey, pID) { this._setActiveNav(pKey); this.pict.views['Plansheet-EntityList'].showEntity(pKey, { EditID: pID }); }
	_renderDetail(pKey, pID) { this._setActiveNav(pKey); this.pict.views['Plansheet-EntityDetail'].showDetail(pKey, pID); }
	_renderTestRun(pID) { this._setActiveNav('TestRun'); this.pict.views['Plansheet-TestRunner'].showRun(pID); }
	// The Node workspace (WI #465). Two-step mount: the view paints #NodeWorkspace-Container into the shell (and
	// its onAfterRender calls the provider's renderWorkspace), then the provider loads/creates and fills it.
	_renderNodeWorkspace(pID) { this._setActiveNav('NodeRegistration'); this.pict.views['NodeWorkspace'].render(); this.pict.providers['NodeWorkspace'].loadAsync(pID); }
	_renderNodeCreate() { this._setActiveNav('NodeRegistration'); this.pict.views['NodeWorkspace'].render(); this.pict.providers['NodeWorkspace'].startCreateAsync(null); }
	// The descriptor OpenAction target: a Nodes list row-click routes to the workspace.
	showNodeWorkspace(pID) { this._navigate('/node/' + pID); }
	_renderBlueprint(pID, pEntityKey)
	{
		this._setActiveNav(pEntityKey || 'WorkItem');
		this.pict.views['Plansheet-Blueprint'].show(this.pict.providers.BlueprintAPI.subjectForEntity(pEntityKey || 'WorkItem', pID));
	}
	_renderBlueprintRecord(pID)
	{
		this._setActiveNav('Blueprint');
		this.pict.views['Plansheet-Blueprint'].show(this.pict.providers.BlueprintAPI.subjectForRecord(pID));
	}
	_renderBlueprintComposer(pID) { this.pict.views['Plansheet-BlueprintComposer'].show(pID); }
	_renderBlueprintStudio(pID) { if (typeof this._setPageTitle === 'function') { this._setPageTitle('Design'); } this.pict.views['Plansheet-BlueprintStudio'].open(pID); }
	_renderMediaViewer(pID) { this._setPageTitle('Screenshot'); this.pict.views['Plansheet-MediaViewer'].show(pID); }

	// From the quick-view lightbox: close it and open the full deep-zoom viewer for that image.
	openMediaViewer(pURL)
	{
		let tmpMatch = String(pURL || '').match(/\/Media\/(\d+)\//);
		let tmpID = tmpMatch ? parseInt(tmpMatch[1], 10) : 0;
		if (!tmpID) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.close === 'function') { tmpModal.close(); }
		let tmpRouter = this.pict.providers['Pict-Router'];
		if (tmpRouter) { tmpRouter.navigate('/media/' + tmpID); }
	}

	// Header action on a Blueprint detail: open its composer.
	composeBlueprint(pKey, pID)
	{
		let tmpRouter = this.pict.providers['Pict-Router'];
		if (tmpRouter && pID) { tmpRouter.navigate('/blueprint/' + pID + '/compose'); }
	}

	// Header action on a Deliverable: open its Blueprint composer, creating + attaching a Blueprint the
	// first time (named after the deliverable) so a Solution's bundle is one click from the deliverable.
	composeDeliverableBlueprint(pKey, pID)
	{
		let tmpRouter = this.pict.providers['Pict-Router'];
		if (!pID) { return; }
		const tmpPict = this.pict;
		let fJSON = (pMethod, pURL, pBody) =>
		{
			let tmpMethod = String(pMethod || 'GET').toUpperCase();
			let tmpPromise;
			if (tmpMethod === 'POST') { tmpPromise = libFrameworkClient.postJSON(tmpPict, pURL, pBody); }
			else if (tmpMethod === 'PUT') { tmpPromise = libFrameworkClient.putJSON(tmpPict, pURL, pBody); }
			else if (tmpMethod === 'DELETE') { tmpPromise = libFrameworkClient.delJSON(tmpPict, pURL); }
			else { tmpPromise = libFrameworkClient.getJSON(tmpPict, pURL); }
			return tmpPromise.catch(() => null);
		};
		fJSON('GET', '/1.0/Deliverable/' + pID).then((pDeliverable) =>
		{
			if (pDeliverable && pDeliverable.IDBlueprint) { if (tmpRouter) { tmpRouter.navigate('/blueprint/' + pDeliverable.IDBlueprint + '/compose'); } return; }
			let tmpName = (pDeliverable && pDeliverable.Title) ? (pDeliverable.Title + ' blueprint') : 'Deliverable blueprint';
			fJSON('POST', '/1.0/Blueprint', { Name: tmpName }).then((pBlueprint) =>
			{
				if (!pBlueprint || !pBlueprint.IDBlueprint) { return; }
				fJSON('PUT', '/1.0/Deliverable', { IDDeliverable: pID, IDBlueprint: pBlueprint.IDBlueprint }).then(() =>
				{
					if (tmpRouter) { tmpRouter.navigate('/blueprint/' + pBlueprint.IDBlueprint + '/compose'); }
				});
			});
		});
	}
	_renderDocsOverview() { this._setActiveNav('Docs'); this.pict.views['Plansheet-FoundationDocs'].showOverview(); }
	// Workflow is an administrator-configured platform feature (it lives under the gear, not the main
	// sidebar). Bounce a non-admin who hand-types the URL back to the board.
	_renderWorkflow()
	{
		if (!this._can('users.manage')) { return this._navigate('/board'); }
		this._setActiveNav('Workflow');
		this.pict.views['Plansheet-Workflow'].show();
	}
	// Route handler for /workflow/:tab -- the catalog, designer and board tabs as places of their own.
	// Carries the SAME users.manage gate as /workflow; a route that renders the page without it would
	// be a way around the check rather than a new way to reach the page.
	_renderWorkflowTab(pTab)
	{
		if (!this._can('users.manage')) { return this._navigate('/board'); }
		this._setActiveNav('Workflow');
		this.pict.views['Plansheet-Workflow'].showTabRoute(pTab);
	}
	// Route handler for /workflow/subject/:id -- open that subject in the workflow's subject tab.
	_renderWorkflowSubject(pID)
	{
		if (!this._can('users.manage')) { return this._navigate('/board'); }
		this._setActiveNav('Workflow');
		this.pict.views['Plansheet-Workflow'].showSubjectRoute(Number(pID) || 0);
	}
	// Route handler for /prompts/:tab.
	_renderPromptLibraryTab(pTab) { this._setActiveNav('Prompt Library'); this.pict.views['Plansheet-PromptLibrary'].showTabRoute(pTab); }
	_renderPromptLibrary()
	{
		this._setActiveNav('Prompts');
		this.pict.views['Plansheet-PromptLibrary'].show();
	}
	// Route handler for /prompt/:id -- open that shared prompt in the library's reader (survives reload).
	_renderPrompt(pID)
	{
		this._setActiveNav('Prompts');
		this.pict.views['Plansheet-PromptLibrary'].showSharedPrompt(Number(pID) || 0);
	}
	_renderWhiteboards()
	{
		this._setActiveNav('Whiteboards');
		this.pict.views['Plansheet-Whiteboards'].show();
	}
	// Route handler for /whiteboard/:id -- mount the whiteboards surface and open the one board in its
	// editor. Reaching this by reload / paste opens straight into that board; the list is one click back.
	_renderWhiteboardEditor(pID)
	{
		this._setActiveNav('Whiteboards');
		this.pict.views['Plansheet-Whiteboards'].showBoard(pID);
	}
	_renderChecklists()
	{
		this._setActiveNav('Checklists');
		this.pict.views['Plansheet-Checklists'].show();
	}
	_renderNotesPage()
	{
		this._setActiveNav('Notes');
		this.pict.views['Plansheet-NotesPage'].showList();
	}
	// Route handler for /note/:id -- open that note in the editor pane (survives reload / deep-links).
	_renderNote(pID)
	{
		this._setActiveNav('Notes');
		this.pict.views['Plansheet-NotesPage'].showNote(Number(pID) || 0);
	}
	_renderBookmarksPage()
	{
		this._setActiveNav('Bookmarks');
		this.pict.views['Plansheet-BookmarksPage'].show();
	}
	// The cross-tenant home (WI #339). Renders tenant-less from /Account/Tenants, so it works while
	// signed in regardless of which plan sheet the session is in.
	_renderHome()
	{
		this._setActiveNav('Home');
		this.pict.views['Plansheet-Home'].show();
	}
	_renderGalleryPage()
	{
		this._setActiveNav('Gallery');
		this.pict.views['Plansheet-GalleryPage'].show();
	}
	_renderDrawingsPage()
	{
		this._setActiveNav('Drawings');
		this.pict.views['Plansheet-DrawingsPage'].show();
	}
	// Route handler for /drawing/:id and /drawings/new -- open the Excalidraw editor for that drawing
	// (0 = a new one). Closing / saving navigates back to /drawings (see DrawingsPage.openDrawingRoute).
	_renderDrawingEditor(pID)
	{
		this._setActiveNav('Drawings');
		this.pict.views['Plansheet-DrawingsPage'].openDrawingRoute(Number(pID) || 0);
	}
	_renderOpsConsole() { this._setActiveNav('Ops Console'); this.pict.views['Plansheet-OpsConsole'].show(); }

	_renderReleases() { this._setActiveNav('Releases'); this.pict.views['Plansheet-Releases'].show(); }
	_renderRuns() { this._setActiveNav('Runs'); this.pict.views['Plansheet-Runs'].show(); }
	_renderAsk() { this._setActiveNav('Ask'); this.pict.views['Plansheet-Ask'].showCatalog(); }
	_renderAskTask(pID) { this._setActiveNav('Ask'); this.pict.views['Plansheet-Ask'].showTask(Number(pID) || 0); }
	_renderAskRequest(pID) { this._setActiveNav('Ask'); this.pict.views['Plansheet-Ask'].showRequest(Number(pID) || 0); }
	_renderProjects() { this._setActiveNav('Projects'); this.pict.views['Plansheet-Project'].showList(); }
	_renderProject(pID) { this._setActiveNav('Projects'); this.pict.views['Plansheet-Project'].showDetail(Number(pID) || 0); }
	_renderProjectTypes() { this._setActiveNav('Project Types'); this.pict.views['Plansheet-ProjectTypes'].showList(); }
	_renderProjectType(pID) { this._setActiveNav('Project Types'); this.pict.views['Plansheet-ProjectTypes'].showDetail(Number(pID) || 0); }
	_renderCapabilityActivation() { this._setActiveNav('CapabilityActivation'); this.pict.views['Plansheet-CapabilityActivation'].show(); }
	_renderOrgActivity() { this._setActiveNav('Activity'); this.pict.views['Plansheet-OrgActivity'].show(); }
	_renderSystemActivity(pPeriod) { this._setActiveNav('System Activity'); this.pict.views['Plansheet-SystemActivity'].show(pPeriod); }
	_renderIssues() { this._setActiveNav('Issues'); this.pict.views['Plansheet-IssueBoard'].show(); }
	// Features get a board of their own: work items move at agent speed, features move at product
	// speed, and one surface implied they should move together.
	_renderFeatureBoard() { this._setActiveNav('Feature Board'); this.pict.views['Plansheet-FeatureBoard'].show(); }
	_renderDeliverBoard() { this._setActiveNav('Deliver'); this.pict.views['Plansheet-DeliverableBoard'].show(); }
	_renderCallDesk() { this._setActiveNav('Call Desk'); this.pict.views['Plansheet-CallDesk'].show(); }
	_renderCallDeskCustomer(pID) { this._setActiveNav('Call Desk'); this.pict.views['Plansheet-CallDesk'].showCustomer(pID); }
	_renderStickerSetsPreview() { this._setActiveNav(''); this.pict.views['Plansheet-StickerSetsPreview'].show(); }

	// Build (once) the full-screen overlay that openBoardFullscreen mounts a board into. Styled inline so
	// it does not depend on any view's CSS being injected (it can be opened from a work item detail too).
	// A persistent top bar carries the board name and a high-contrast, always-visible Close button. This is
	// the only exit affordance a presentation board (jumbotron / background) gets: those styles hide the
	// flow's own toolbar, and the flow's in-board fullscreen chrome (.pict-flow-fullscreen) never appears on
	// the host-overlay path. The bar RESERVES space above the canvas (it is a flex row, not a floating
	// overlay), so a background board's full-bleed art can never swallow the exit the way the old translucent
	// corner button was -- leaving Escape as the only way out. The Escape handler stays as a secondary exit.
	_ensureFullscreenOverlay()
	{
		if (this._FullscreenOverlay || typeof document === 'undefined') { return this._FullscreenOverlay; }
		let tmpOverlay = document.createElement('div');
		tmpOverlay.id = 'Whiteboard-Fullscreen-Overlay';
		// z-index ABOVE the flow's own fullscreen (9999) so the close button can never end up underneath a
		// board that entered the flow's fullscreen inside this overlay; the gallery picker (10001) stays on top.
		tmpOverlay.style.cssText = 'position:fixed;inset:0;z-index:10000;background:#0b0f18;display:none;flex-direction:column;';
		// Inline onclick (not addEventListener) so the pointer exit survives any re-mount of the overlay
		// -- a detached listener was a way the overlay got stuck with no way out but a page reload.
		// A presentation board (jumbotron / background) hides its own flow toolbar via pict-section-moodboard's
		// .mb-presentation rules (>=1.2.5 also suppresses the collapsed "expand toolbar" button that used to
		// leak in on hover), so the bar below is the entire overlay chrome -- no host-side toolbar hiding needed.
		tmpOverlay.innerHTML =
			'<div id="Whiteboard-Fullscreen-Bar" style="flex:0 0 auto;position:relative;z-index:3;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 16px;background:#141a26;border-bottom:1px solid rgba(255,255,255,0.10);">' +
				'<span id="Whiteboard-Fullscreen-Title" style="color:#e7ebf3;font-size:15px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">Whiteboard</span>' +
				'<button id="Whiteboard-Fullscreen-Close" title="Close (Esc)" onclick="_Pict.PictApplication.closeBoardFullscreen()" style="flex:0 0 auto;display:inline-flex;align-items:center;gap:7px;border:1px solid rgba(255,255,255,0.22);background:rgba(255,255,255,0.08);color:#dfe4ee;font-size:14px;font-weight:600;line-height:1;padding:8px 15px;border-radius:8px;cursor:pointer;">' +
					'<span style="font-size:16px;line-height:1;opacity:0.85;">&times;</span> Close' +
				'</button>' +
			'</div>' +
			'<div id="Whiteboard-Fullscreen-Canvas" style="flex:1;min-height:0;background:#0b0f18;"></div>';
		document.body.appendChild(tmpOverlay);
		this._FullscreenOverlay = tmpOverlay;
		return tmpOverlay;
	}

	// Show a whiteboard full screen, read-only (its stored display style; a photo board reads full-bleed).
	// Opening the read-only full-screen peek is a NAVIGATION to /whiteboard/:id/fullscreen, so it deep-links
	// and survives a reload. The origin (where it was opened from) is stashed so closing returns there.
	openBoardFullscreen(pID)
	{
		let tmpID = Number(pID) || 0;
		if (!tmpID) { return; }
		this._fullscreenOrigin = (typeof window !== 'undefined' && window.location) ? (window.location.hash || '') : '';
		this.showBoardFullscreen(tmpID);
	}

	// Route handler for /whiteboard/:id/fullscreen -- mount the board into the full-screen overlay.
	_renderBoardFullscreen(pID)
	{
		let tmpAPI = this.pict.providers.MoodboardAPI;
		let tmpID = Number(pID) || 0;
		if (!tmpAPI || !tmpID) { return; }
		this._ensureFullscreenOverlay();
		if (this._FullscreenOverlay) { this._FullscreenOverlay.style.display = 'flex'; }
		if (!this._FullscreenEscHandler)
		{
			this._FullscreenEscHandler = (pEvent) => { if (pEvent.key === 'Escape') { this.closeBoardFullscreen(); } };
			document.addEventListener('keydown', this._FullscreenEscHandler);
		}
		tmpAPI.read(tmpID).then((pRow) =>
		{
			let tmpBoard = tmpAPI.loadBoard(pRow);
			// Name the board in the overlay bar (textContent auto-escapes; the bar is host chrome, not a
			// templated view). Fall back to a generic label when a board has no name.
			let tmpTitleEl = document.getElementById('Whiteboard-Fullscreen-Title');
			if (tmpTitleEl) { tmpTitleEl.textContent = (pRow && pRow.Name) || 'Whiteboard'; }
			let tmpView = this.pict.views['Whiteboard-Fullscreen-View'];
			if (tmpView) { tmpView.render(); tmpView.setBoard(tmpBoard); }
		});
	}

	// The Close button / Escape: leave the fullscreen route back to where it was opened from (the hashchange
	// then hides the overlay via _syncBoardFullscreenToRoute). A cold deep-link has no origin -> /whiteboards.
	closeBoardFullscreen()
	{
		// Take the overlay down immediately (the router's navigate does not always fire hashchange, so we do
		// not rely on _syncBoardFullscreenToRoute here), then return to where it was opened from.
		this._hideBoardFullscreen();
		let tmpOrigin = this._fullscreenOrigin;
		this._fullscreenOrigin = null;
		let tmpRoute = (typeof tmpOrigin === 'string' && tmpOrigin.replace(/^#/, '').charAt(0) === '/') ? tmpOrigin.replace(/^#/, '') : '/whiteboards';
		if (/\/fullscreen\/?$/.test(tmpRoute)) { tmpRoute = '/whiteboards'; } // never return to another fullscreen
		this._navigate(tmpRoute);
	}

	// Hide the overlay + drop its Escape handler (no navigation -- the route has already changed).
	_hideBoardFullscreen()
	{
		if (this._FullscreenOverlay) { this._FullscreenOverlay.style.display = 'none'; }
		if (this._FullscreenEscHandler) { document.removeEventListener('keydown', this._FullscreenEscHandler); this._FullscreenEscHandler = null; }
	}

	// Called on every route change: the fullscreen overlay is a layer over the workspace, not part of it, so
	// leaving the /whiteboard/:id/fullscreen route (Close, Escape, or any nav) must take the overlay down.
	_syncBoardFullscreenToRoute()
	{
		if (typeof window === 'undefined') { return; }
		let tmpHash = (window.location.hash || '').replace(/^#/, '');
		let tmpIsFullscreen = /^\/whiteboard\/\d+\/fullscreen\/?$/.test(tmpHash);
		if (!tmpIsFullscreen && this._FullscreenOverlay && this._FullscreenOverlay.style.display !== 'none')
		{
			this._hideBoardFullscreen();
		}
	}
	// Organization prompt shapes are an administrator-configured platform feature, like Workflow:
	// they live under the gear. Bounce a non-admin who hand-types the URL back to the board.
	_renderPromptShapes()
	{
		if (!this._can('users.manage')) { return this._navigate('/board'); }
		this._setActiveNav('PromptShapes');
		// Routed (3rd arg) so the standalone page's edits deep-link to /prompt-shape/:id.
		this.pict.views['Plansheet-PromptShapes'].showIn('#Plansheet-Workspace', 'Organization', true);
	}
	// Route handler for /prompt-shape/:id and /prompt-shapes/new -- open the standalone shape editor.
	_renderPromptShapeEditor(pID)
	{
		if (!this._can('users.manage')) { return this._navigate('/board'); }
		this._setActiveNav('PromptShapes');
		this.pict.views['Plansheet-PromptShapes'].showRoutedEdit(Number(pID) || 0);
	}
	_renderCommunity()
	{
		this._setActiveNav('Community');
		if (!(this.pict.AppData.Auth && this.pict.AppData.Auth.LoggedIn)) { return this.showAuth('login'); }
		this.pict.views['Plansheet-Community'].show();
	}

	_renderDoc(pPathWithSection)
	{
		// Capture whether the docs workspace was ALREADY the active view, before _setActiveNav flips it to
		// 'Docs'. The idempotency skip below is only safe when we were already in docs showing this exact
		// path (the URL syncing to a tree click). Returning to the SAME doc URL from another view -- e.g.
		// Back out of Settings/Appearance -- leaves InlineDocumentation.CurrentPath stale, so without this
		// guard the skip fires and the content never repaints (the URL changes but nothing loads).
		let tmpWasDocs = (this._activeNavKey === 'Docs');
		this._setActiveNav('Docs');
		let tmpSplit = String(pPathWithSection || '').split('::');
		let tmpPath = tmpSplit[0];
		let tmpSection = (tmpSplit.length > 1) ? tmpSplit[1] : '';
		if (!tmpPath) { this.pict.views['Plansheet-FoundationDocs'].showOverview(); return; }
		this._setPageTitle(this._docTitleFromPath(tmpPath));
		// Idempotent: if the inline-doc already shows this doc and there's no new
		// section to scroll to, this is just the URL syncing to a nav click that
		// already loaded the doc — don't reload it. Only trust that when the docs
		// workspace was already active (tmpWasDocs); otherwise the doc view is not
		// even mounted and we must repaint.
		let tmpState = this.pict.AppData.InlineDocumentation;
		let tmpCurrent = tmpState && tmpState.CurrentPath;
		if (tmpWasDocs && tmpCurrent && tmpCurrent === tmpPath)
		{
			// Already showing this doc. If a section was requested (a pasted
			// section link for the current doc), scroll to it without re-syncing
			// the URL — it is already correct — so the route stays loop-free.
			if (tmpSection)
			{
				let tmpProvider = this.pict.providers['Pict-InlineDocumentation'];
				if (tmpProvider && typeof tmpProvider._scrollToAnchor === 'function')
				{
					this._suppressDocURLSync = true;
					try { tmpProvider._scrollToAnchor(tmpSection); }
					finally { this._suppressDocURLSync = false; }
				}
			}
			return;
		}
		this.pict.views['Plansheet-FoundationDocs'].showSlug(tmpSection ? (tmpPath + '#' + tmpSection) : tmpPath);
	}

	_renderUserSettings() { this._renderSettingsSection('profile', null); }

	// Settings sections render full-width into the workspace now; their nav lives in the shared
	// sidebar's Settings group. Each #/settings/<section> is a real route. If the settings shell is
	// already mounted, just swap the section; otherwise render the shell and let its onAfterRender
	// open the section we stashed in _pendingSection. The optional :id rides a sub-path the settings
	// view already parses (the Drafting Kit editor: draftingkit/<id>).
	_renderSettingsSection(pSection, pID)
	{
		this._setActiveNav('Settings');
		if (!(this.pict.AppData.Auth && this.pict.AppData.Auth.LoggedIn)) { return this.showAuth('login'); }
		this._setPageTitle(this._settingsTitle(pSection));
		let tmpView = this.pict.views['Plansheet-UserSettings'];
		if (!tmpView) { return; }
		let tmpSection = pID ? (String(pSection) + '/' + pID) : (pSection || 'profile');
		if (typeof document !== 'undefined' && document.getElementById('Settings-Content'))
		{
			tmpView.showSection(tmpSection);
		}
		else
		{
			tmpView._pendingSection = tmpSection;
			tmpView.render();
		}
	}

	_renderProfile()
	{
		this._setActiveNav('Community');
		if (!(this.pict.AppData.Auth && this.pict.AppData.Auth.LoggedIn)) { return this.showAuth('login'); }
		let tmpView = this.pict.views['Plansheet-Profile'];
		this.pict.AppData.ProfileView = this.pict.AppData.ProfileView || { Record: null, Blocks: [], BoardID: null };
		// /profile is the signed-in user; refresh from the server, then render.
		if (this.pict.providers.UserAPI)
		{
			this.pict.providers.UserAPI.getMe().then((pAccount) =>
			{
				this.pict.AppData.ProfileView.Record = pAccount || (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || null;
				let tmpRec = this.pict.AppData.ProfileView.Record;
				this._setPageTitle((tmpRec && (tmpRec.DisplayName || tmpRec.Email)) || 'Profile');
				tmpView.show();
			});
		}
		else { this._setPageTitle('Profile'); tmpView.show(); }
	}

	// /profile/:id is a teammate's page (read-only). Your own id falls through to the editable self
	// profile. The record comes from the public Team/Members/:id endpoint.
	_renderProfileFor(pIDUser)
	{
		this._setActiveNav('Community');
		if (!(this.pict.AppData.Auth && this.pict.AppData.Auth.LoggedIn)) { return this.showAuth('login'); }
		let tmpMe = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
		if (tmpMe.IDUser && Number(pIDUser) === tmpMe.IDUser) { return this._renderProfile(); }
		let tmpView = this.pict.views['Plansheet-Profile'];
		this.pict.AppData.ProfileView = this.pict.AppData.ProfileView || { Record: null, Blocks: [], BoardID: null };
		if (this.pict.providers.TeamAPI)
		{
			this.pict.providers.TeamAPI.getMember(pIDUser).then((pMember) =>
			{
				this.pict.AppData.ProfileView.Record = pMember || null;
				this._setPageTitle((pMember && (pMember.DisplayName || pMember.Email)) || 'Profile');
				tmpView.show();
			});
		}
	}

	// ── public navigation (called by views/buttons) — set the URL ────────────
	_showWorkspace()
	{
		// Org requires two-step verification and this account is not enrolled: hold at
		// the forced-enrollment gate (a restricted session reaches only the 2FA
		// enrollment endpoints) instead of the workspace until 2FA is turned on.
		if (this.pict.AppData.Auth && this.pict.AppData.Auth.User && this.pict.AppData.Auth.User.MustEnroll2FA)
		{
			return this.showAuth('enroll');
		}
		// Bring the app chrome back now that we are signed in.
		if (typeof document !== 'undefined' && document.body) { document.body.classList.remove('plansheet-auth-mode'); }
		// The docked site-map sidebar (pict-section-navigation) replaces the hand-rolled rail, and arms
		// the cmd+K launcher.
		this._installSiteNav();
		// Paint this plan sheet's top-bar backdrop (WI #360): the baked moodboard crop that makes each
		// plan sheet's chrome visually its own, so landing in the wrong one is obvious at a glance. Fired
		// once here and never awaited -- the workspace must not wait on a decoration -- and once here is
		// enough, because switching plan sheets reloads the page (reloadForActivePlansheetChange).
		this._applyTopBarBackdrop();
		// Render the workspace for the current route, honoring a pasted deep-link, otherwise the
		// cross-tenant home (WI #339): the canonical landing that shows every plan sheet you belong to.
		let tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash.replace(/^#/, '') : '';
		let tmpTarget = (tmpHash && tmpHash !== '/') ? tmpHash : '/home';
		// Navigate straight to the target so exactly ONE route handler runs. Navigo will not re-fire the
		// handler for the route it is already on, so after an in-app sign-out then sign-in -- where the hash
		// still holds the last route and Navigo still has it resolved -- a plain navigate is a no-op and the
		// sign-in view (rendered into #Plansheet-Workspace) would stay put. Clearing Navigo's resolved-route
		// state makes the navigate below always fire its handler. The previous fix bounced through '/'
		// instead, but that fired _renderBoard, whose async board fetch could resolve AFTER the real route's
		// render and paint the board over it -- a nondeterministic boot race on any deep-link / hard reload
		// to a non-board route.
		// renavigate(): go there, and run the handler even if the router already considers itself there.
		// This used to reach into navigo's PRIVATE _setCurrent(null) to force the same thing, with a
		// root-bounce fallback for older versions that fired _renderBoard and could paint the board over
		// the real route. pict-router 1.0.13 does it properly, so neither hack is needed.
		let tmpRouter = this._router();
		if (tmpRouter && typeof tmpRouter.renavigate === 'function')
		{
			let tmpHashTarget = '#' + String(tmpTarget || '/').replace(/^#/, '');
			if (typeof window !== 'undefined' && window.location && window.history
				&& typeof window.history.pushState === 'function' && String(window.location.hash || '') !== tmpHashTarget)
			{
				window.history.pushState(null, '', (window.location.pathname || '') + (window.location.search || '') + tmpHashTarget);
			}
			tmpRouter.renavigate(tmpTarget, { updateBrowserURL: false });
		}
		else
		{
			this._navigate('/');
			this._navigate(tmpTarget);
		}
	}

	// Fetch the active plan sheet's baked top-bar crop and hand it to the shell, which injects it behind
	// the brand / nav / account chrome (Plansheet-Shell.applyTopBarBackdrop). The SVG comes from the
	// session's own tenant server-side -- the client never names a plan sheet -- and an empty string is
	// the normal "no backdrop set" answer, which clears the bar. Any failure is swallowed by the provider
	// and lands here as an empty backdrop: a decoration is never worth a broken load.
	_applyTopBarBackdrop()
	{
		let tmpShell = this.pict.views['Plansheet-Shell'];
		if (!tmpShell || typeof tmpShell.applyTopBarBackdrop !== 'function') { return; }
		this.pict.providers.OrgAPI.getTopBar()
			.then((pBody) => { tmpShell.applyTopBarBackdrop((pBody && pBody.TopBarBackdropSVG) || ''); })
			.catch(() => { /* the provider already falls back to no backdrop */ });
	}

	// If the user has pinned an explicit default plan sheet (localStorage plansheet.pinned_customer)
	// and the current session is in a different one, switch before rendering the workspace.
	// Returns a promise that resolves once the determination is made. On a successful switch
	// it triggers a page reload (so the promise is never settled; the reload takes over).
	_applyPinnedCustomerIfNeeded()
	{
		let tmpPinned = 0;
		try { tmpPinned = parseInt((typeof localStorage !== 'undefined' ? localStorage.getItem('plansheet.pinned_customer') : null) || '0', 10) || 0; }
		catch (pErr) { tmpPinned = 0; }
		let tmpCurrentIDCustomer = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) ? (this.pict.AppData.Auth.User.IDCustomer || 0) : 0;
		if (!tmpPinned || tmpPinned === tmpCurrentIDCustomer) { return Promise.resolve(); }
		return libFrameworkClient.postJSON(this.pict, '/1.0/Session/Context/' + tmpPinned, {})
			.then((pBody) =>
			{
				if (pBody && pBody.Switched) { this.reloadForActivePlansheetChange(); return; }
				// Switch was rejected (no longer a member): clear the stale pin and continue.
				try { if (typeof localStorage !== 'undefined') { localStorage.removeItem('plansheet.pinned_customer'); } } catch (pStorageErr) { /* ignore */ }
			})
			.catch(() =>
			{
				// Network error or auth failure: clear the pin and carry on.
				try { if (typeof localStorage !== 'undefined') { localStorage.removeItem('plansheet.pinned_customer'); } } catch (pStorageErr) { /* ignore */ }
			});
	}

	showSection(pKey)
	{
		if (pKey === 'Board') { return this._navigate('/board'); }
		if (pKey === 'Docs') { return this._navigate('/docs'); }
		if (pKey === 'Sessions') { return this._navigate('/sessions'); }
		if (pKey === 'Workflow') { return this._navigate('/workflow'); }
		if (pKey === 'Community') { return this._navigate('/community'); }
		if (pKey === 'Settings') { return this._navigate('/settings'); }
		return this._navigate('/' + this._pluralRoute(pKey));
	}

	showBoard() { this._navigate('/board'); }

	showReleases() { this._navigate('/releases'); }

	// Route to /profile/:myId rather than /profile: a distinct :id always re-fires the handler (Navigo
	// will not re-fire /profile coming from /profile/:otherId, a prefix), and _renderProfileFor detects
	// self and renders the editable page.
	showProfile()
	{
		let tmpMe = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
		if (tmpMe.IDUser) { this._navigate('/profile/' + tmpMe.IDUser); }
		else { this._navigate('/profile'); }
	}

	showProfileFor(pIDUser) { this._navigate('/profile/' + pIDUser); }

	showCommunity() { this._navigate('/community'); }

	showEntityDetail(pKey, pID) { this._navigate('/' + this._singularRoute(pKey) + '/' + pID); }
	showWhiteboards() { this._navigate('/whiteboards'); }
	showWhiteboard(pID) { this._navigate('/whiteboard/' + pID); }
	showBoardFullscreen(pID) { this._navigate('/whiteboard/' + pID + '/fullscreen'); }
	showDrawings() { this._navigate('/drawings'); }
	showDrawing(pID) { this._navigate('/drawing/' + pID); }
	showNewDrawing() { this._navigate('/drawings/new'); }
	showNotes() { this._navigate('/notes'); }
	showNote(pID) { this._navigate('/note/' + pID); }
	// Ask navigation: the catalog, a task's input form, and an in-flight/answered request are each a route so
	// Back / forward and reload behave. The Ask view calls these instead of repainting its body in place.
	showAsk() { this._navigate('/ask'); }
	showAskTask(pID) { this._navigate('/ask/task/' + (Number(pID) || 0)); }
	showAskRequest(pID) { this._navigate('/ask/request/' + (Number(pID) || 0)); }
	showProjects() { this._navigate('/projects'); }
	showProject(pID) { this._navigate('/project/' + (Number(pID) || 0)); }
	showProjectTypes() { this._navigate('/project-types'); }
	showProjectType(pID) { this._navigate('/project-type/' + (Number(pID) || 0)); }
	// A settings pane that is working on one thing (a sticker set, a message template) says which,
	// riding the /settings/:section/:id route that Drafting Kit already uses.
	showSettingsSub(pSection, pSub)
	{
		if (!pSection || !pSub) { return false; }
		this._navigate('/settings/' + pSection + '/' + pSub);
		return true;
	}
	showSystemActivityPeriod(pPeriod)
	{
		if (!pPeriod) { return false; }
		this._navigate('/system-activity/' + pPeriod);
		return true;
	}
	showPromptsTab(pTab)
	{
		if (!pTab) { return false; }
		this._navigate('/prompts/' + pTab);
		return true;
	}
	showWorkflowTab(pTab)
	{
		// The subject tab is reached by opening a subject, which has its own route carrying the id.
		// Sending it to /workflow/subject would land on a subject page with no subject.
		if (!pTab || pTab === 'subject') { return false; }
		this._navigate('/workflow/' + pTab);
		return true;
	}
	showCallDeskCustomer(pID)
	{
		this._navigate(pID ? ('/call-desk/' + pID) : '/call-desk');
		return true;
	}
	// Open the create / edit form for an entity as a place of its own. Both return false when the entity
	// has no slug for that shape -- Blueprint has a list but a bespoke detail page, Issue has a detail
	// but a board instead of a list -- so the caller can fall back to rendering in place rather than
	// navigating to a route that was never registered.
	showEntityNew(pKey)
	{
		let tmpSlug = _listSlugFor(pKey);
		if (!tmpSlug) { return false; }
		return this._navigate('/' + tmpSlug + '/new');
	}
	showEntityEdit(pKey, pID)
	{
		let tmpSlug = _detailSlugFor(pKey);
		if (!tmpSlug || !pID) { return false; }
		return this._navigate('/' + tmpSlug + '/' + pID + '/edit');
	}
	// Where a form goes when it closes: back to the record it was editing, else back to the list.
	showEntityAfterForm(pKey, pID)
	{
		let tmpTarget = (pID && _detailSlugFor(pKey))
			? ('/' + _detailSlugFor(pKey) + '/' + pID)
			: (_listSlugFor(pKey) ? ('/' + _listSlugFor(pKey)) : '');
		if (!tmpTarget) { return false; }
		// ALREADY THERE IS NOT A NAVIGATION, and _navigate says so now -- the caller renders instead of
		// believing it moved. This used to compare the hash by hand right here, which is the sort of
		// thing that gets written once per caller and forgotten in the next one.
		return this._navigate(tmpTarget);
	}
	listSlugFor(pKey) { return _listSlugFor(pKey); }
	detailSlugFor(pKey) { return _detailSlugFor(pKey); }
	showPrompt(pID) { this._navigate('/prompt/' + pID); }
	showWorkflowSubject(pID) { this._navigate('/workflow/subject/' + pID); }
	showPromptShapes() { this._navigate('/prompt-shapes'); }
	showPromptShape(pID) { this._navigate('/prompt-shape/' + pID); }
	showNewPromptShape() { this._navigate('/prompt-shapes/new'); }

	// Issue header action: assign the issue to the signed-in user, then repaint the detail.
	assignIssueToMe(pKey, pIDIssue)
	{
		let tmpUser = (this.pict.AppData.Auth && this.pict.AppData.Auth.User) || {};
		let tmpMe = tmpUser.IDUser || 0;
		if (!tmpMe || !pIDIssue) { return; }
		this.pict.providers.IssueAPI.update(pIDIssue, { IDAssignedUser: tmpMe }).then(() =>
		{
			let tmpDetail = this.pict.views['Plansheet-EntityDetail'];
			if (tmpDetail && typeof tmpDetail.showDetail === 'function') { tmpDetail.showDetail('Issue', pIDIssue); }
			let tmpModal = this.pict.views['Pict-Section-Modal'];
			if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast('Assigned to you.', { type: 'success', duration: 3000 }); }
		}).catch(() => { /* ignore */ });
	}

	// Issue header action: open a picker to assign the issue to any teammate (or unassign).
	assignIssue(pKey, pIDIssue)
	{
		if (!pIDIssue) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		let fEsc = (pStr) => String(pStr).replace(/[&<>"]/g, (pCh) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pCh]));
		this.pict.providers.IssueBoardAPI.assignees().then((pUsers) =>
		{
			let tmpOptions = '<option value="0">Unassigned</option>' + pUsers.map((pU) => '<option value="' + pU.IDUser + '">' + fEsc(pU.DisplayName) + '</option>').join('');
			tmpModal.show(
			{
				title: 'Assign issue',
				content: '<p>Assign to:</p><select id="issue-assignee-pick" style="width:100%;box-sizing:border-box;padding:7px 9px;font:inherit;">' + tmpOptions + '</select>',
				buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Assign', Style: 'primary' }]
			}).then((pChoice) =>
			{
				if (pChoice !== 'ok') { return; }
				let tmpEl = (typeof document !== 'undefined') ? document.getElementById('issue-assignee-pick') : null;
				let tmpID = tmpEl ? (parseInt(tmpEl.value, 10) || 0) : 0;
				this.pict.providers.IssueAPI.update(pIDIssue, { IDAssignedUser: tmpID }).then(() =>
				{
					let tmpDetail = this.pict.views['Plansheet-EntityDetail'];
					if (tmpDetail && typeof tmpDetail.showDetail === 'function') { tmpDetail.showDetail('Issue', pIDIssue); }
					if (typeof tmpModal.toast === 'function') { tmpModal.toast('Assignment updated.', { type: 'success', duration: 3000 }); }
				}).catch(() => { /* ignore */ });
			});
		});
	}

	// Issue header action: create a draft vision from the issue (server links Issue.IDTargetVision) and open it.
	spawnVisionFromIssue(pKey, pIDIssue)
	{
		if (!pIDIssue) { return; }
		this.pict.providers.IssueBoardAPI.spawnVision(pIDIssue, {}).then((pResult) =>
		{
			if (pResult && pResult.IDVision) { this._navigate('/vision/' + pResult.IDVision); }
		}).catch(() => { /* ignore */ });
	}

	// Feedback header action: promote a piece of feedback into a tracked WorkItem. A small form pre-fills
	// Title/Description from the feedback, picks the WorkItem Type, and (in one grouped dropdown) the target
	// Vision/Module/Feature it advances. The server links WorkItem.IDSourceFeedback and opens on the new item.
	spawnWorkItemFromFeedback(pKey, pIDFeedback)
	{
		if (!pIDFeedback) { return; }
		let tmpProviders = this.pict.providers;
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let fGo = (pResult) => { if (pResult && pResult.IDWorkItem) { this._navigate('/work-item/' + pResult.IDWorkItem); } };
		// Headless / no modal: spawn straight from the feedback (server prefills Title/Body) and open it.
		if (!tmpModal || typeof tmpModal.show !== 'function')
		{
			return tmpProviders.IssueBoardAPI.spawnWorkItem(pIDFeedback, {}).then(fGo).catch(() => { /* ignore */ });
		}
		let fEsc = (pStr) => String(pStr == null ? '' : pStr).replace(/[&<>"]/g, (pCh) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pCh]));
		Promise.all(
		[
			tmpProviders.FeedbackAPI.read(pIDFeedback),
			(tmpProviders.FeedbackAPI && typeof tmpProviders.FeedbackAPI.list === 'function') ? tmpProviders.FeedbackAPI.list() : Promise.resolve([])
		]).then((pLoaded) =>
		{
			let tmpFeedback = pLoaded[0] || {};
			// Optional: link more feedback to the new work item via the advanced multi-select picker.
			// The source feedback is already linked by the spawn, so exclude it from the candidates.
			let tmpFbPicker = new libFeedbackPicker();
			tmpFbPicker.configure(Array.isArray(pLoaded[1]) ? pLoaded[1] : [], { Exclude: [pIDFeedback] });
			let tmpStyle = 'width:100%;box-sizing:border-box;padding:6px 8px;font:inherit;margin-top:2px;';
			let fField = (pLabel, pControl) => '<div style="margin-bottom:9px;"><label style="font-size:12px;color:var(--theme-color-text-secondary,#5b6376);">' + pLabel + '</label>' + pControl + '</div>';
			let fType = ['Triage', 'Research', 'Innovation', 'Issue', 'Debt', 'Enhancement'].map((pT) => '<option' + (pT === 'Issue' ? ' selected' : '') + '>' + pT + '</option>').join('');
			// The target is a searchable pict-section-picker over ALL of Feature/Vision/Module (WI #446: a
			// server-backed candidate search per type, not a precomputed Options array built from list() --
			// that is at most one page each, so a Feature/Vision/Module past it could not be picked).
			let tmpTargetTypes =
			[
				{ Type: 'Feature', Provider: tmpProviders.FeatureAPI, Descriptor: libEntityDescriptors.Entities.Feature },
				{ Type: 'Vision', Provider: tmpProviders.VisionAPI, Descriptor: libEntityDescriptors.Entities.Vision },
				{ Type: 'Module', Provider: tmpProviders.ModuleAPI, Descriptor: libEntityDescriptors.Entities.Module }
			];
			let tmpSelectedTarget = '';
			if (this.pict.AppData) { this.pict.AppData.SWI_TargetValue = ''; }
			let tmpDialog = tmpModal.show(
			{
				title: 'Spawn work item',
				width: 'min(680px, 95vw)',
				content:
					// The picker's dropdown floats over the modal, anchored to its control, and flips up when
					// near the bottom (this is the last field, above the buttons). pict-section-picker 1.8.2+
					// anchors the fixed dropdown correctly inside a transform-centered modal, so no override.
					fField('Title', '<input id="swi-title" type="text" style="' + tmpStyle + '" value="' + fEsc(tmpFeedback.Title || '') + '">') +
					fField('Type', '<select id="swi-type" style="' + tmpStyle + '">' + fType + '</select>') +
					fField('Description', '<textarea id="swi-desc" style="' + tmpStyle + 'min-height:70px;resize:vertical;">' + fEsc(tmpFeedback.Body || '') + '</textarea>') +
					fField('This work is about', '<div id="swi-target-host" style="margin-top:2px;"></div>') +
					fField('Also link these feedback (optional)', '<div id="swi-fb-host" style="margin-top:2px;"></div>'),
				buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Create work item', Style: 'primary' }]
			});
			let tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
			if (tmpPickerProvider && typeof tmpPickerProvider.createPicker === 'function')
			{
				setTimeout(() =>
				{
					tmpPickerProvider.createPicker('SWI-TargetPicker',
						{
							DestinationAddress: '#swi-target-host',
							ValueAddress: 'AppData.SWI_TargetValue',
							Placeholder: 'Search a feature, vision, or module',
							Searchable: true,
							Options: [],
							DataProvider: libCandidateSearch.dataProviderForTypes(tmpTargetTypes),
							ResolveValue: libCandidateSearch.resolveValueForTypes(tmpTargetTypes),
							OnChange: (pVal) => { tmpSelectedTarget = pVal || ''; }
						});
					// createPicker builds the view; render() paints it into the host (matches the connections linker).
					let tmpPickerView = this.pict.views['SWI-TargetPicker'];
					if (tmpPickerView) { tmpPickerView.setValue(''); tmpPickerView.render(); }
					let tmpFbHost = (typeof document !== 'undefined') ? document.getElementById('swi-fb-host') : null;
					if (tmpFbHost) { tmpFbPicker.mount(tmpFbHost); }
				}, 20);
			}
			tmpDialog.then((pChoice) =>
			{
				if (pChoice !== 'ok') { return; }
				let fVal = (pID) => { let tmpEl = (typeof document !== 'undefined') ? document.getElementById(pID) : null; return tmpEl ? tmpEl.value : ''; };
				let tmpTargetRaw = tmpSelectedTarget || (this.pict.AppData && this.pict.AppData.SWI_TargetValue) || '';
				let tmpTargetType = '', tmpIDTarget = 0;
				if (tmpTargetRaw) { let tmpParts = tmpTargetRaw.split(':'); tmpTargetType = tmpParts[0]; tmpIDTarget = parseInt(tmpParts[1], 10) || 0; }
				let tmpDraft =
				{
					Title: fVal('swi-title').trim(),
					Type: fVal('swi-type'),
					Description: fVal('swi-desc'),
					TargetType: tmpIDTarget ? tmpTargetType : '',
					IDTarget: tmpIDTarget
				};
				let tmpExtras = tmpFbPicker.selectedIDs();
				tmpProviders.IssueBoardAPI.spawnWorkItem(pIDFeedback, tmpDraft).then((pResult) =>
				{
					// Link any extra feedback the person picked to the new work item (best-effort; the
					// work item is already created, so a link failure should not block navigation).
					if (pResult && pResult.IDWorkItem && tmpExtras.length)
					{
						return tmpProviders.IssueBoardAPI.linkFeedbackToWorkItem(pResult.IDWorkItem, tmpExtras).then(() => pResult, () => pResult);
					}
					return pResult;
				}).then(fGo).catch(() => { /* ignore */ });
			});
		}).catch(() => { /* ignore */ });
	}

	// Start a test run of a routine (the Routine detail's header action) and land
	// in the runner. pKey arrives from the generic header-action dispatch.
	// Detail header action on a Work Item: generate a prompt from a shape for this work item.
	// Delegates to the attached-prompts panel, which is already mounted for this owner and knows
	// how to pick the shape, POST, and refresh.
	generatePromptForWorkItem(pKey, pIDWorkItem)
	{
		let tmpView = this.pict.views['Plansheet-PromptAttachments'];
		if (tmpView && typeof tmpView.generateDialog === 'function') { tmpView.generateDialog(); }
	}

	// Detail header action on a Work Item: assign it to a teammate or the claude@plansheet.io AI agent.
	// A modal with the same searchable pict-section-picker the other header actions use; its options are the
	// team roster (humans + agents), plus an Unassigned choice. Writing IDAssignedUser flows through the normal
	// WorkItem PUT, so the append-only WorkflowStatusEvent audit and the assignment notification fire for free.
	assignWorkItem(pKey, pIDWorkItem)
	{
		if (!pIDWorkItem) { return; }
		let tmpProviders = this.pict.providers;
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		Promise.all(
		[
			(tmpProviders.TeamAPI && typeof tmpProviders.TeamAPI.roster === 'function') ? tmpProviders.TeamAPI.roster() : Promise.resolve([]),
			fetch('/1.0/WorkItem/' + encodeURIComponent(pIDWorkItem), { credentials: 'same-origin' }).then((pR) => pR.ok ? pR.json() : {}).catch(() => ({}))
		]).then((pLoaded) =>
		{
			let tmpRoster = Array.isArray(pLoaded[0]) ? pLoaded[0] : ((this.pict.AppData.Team && this.pict.AppData.Team.Roster) || []);
			let tmpWorkItem = pLoaded[1] || {};
			let tmpCurrent = Number(tmpWorkItem.IDAssignedUser) || 0;
			let tmpOptions = [{ Value: '0', Text: 'Unassigned' }].concat(tmpRoster.map((pMember) =>
				({ Value: String(pMember.IDUser), Text: (pMember.DisplayName || ('User #' + pMember.IDUser)) + (pMember.IsAgent ? '  (AI agent)' : '') })));
			let tmpNameByID = {};
			tmpOptions.forEach((pOption) => { tmpNameByID[pOption.Value] = pOption.Text; });
			let tmpSelected = String(tmpCurrent);
			let tmpDialog = tmpModal.show(
			{
				title: 'Assign work item',
				content: '<div style="margin-bottom:9px;"><label style="font-size:12px;color:var(--theme-color-text-secondary,#5b6376);">Assignee</label>' +
					'<div id="awi-assignee-host" style="margin-top:2px;"></div>' +
					'<p style="font-size:12px;color:var(--theme-color-text-secondary,#5b6376);margin:8px 0 0;">Pick a teammate or the AI agent. Choose Unassigned to clear it.</p></div>',
				buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Assign', Style: 'primary' }]
			});
			setTimeout(() =>
			{
				this.mountPicker('#awi-assignee-host', 'AWI-AssigneePicker', tmpOptions,
					(pVal) => { tmpSelected = (pVal == null ? '' : String(pVal)); },
					{ Placeholder: 'Search a teammate or the AI agent', Value: String(tmpCurrent) });
			}, 20);
			tmpDialog.then((pChoice) =>
			{
				if (pChoice !== 'ok') { return; }
				let tmpIDUser = parseInt(tmpSelected, 10) || 0;
				if (tmpIDUser === tmpCurrent)
				{
					if (tmpModal.toast) { tmpModal.toast('No change to the assignee.', { type: 'info' }); }
					return;
				}
				let tmpProvider = tmpProviders[pKey + 'API'];
				if (!tmpProvider || typeof tmpProvider.update !== 'function') { return; }
				tmpProvider.update(pIDWorkItem, { IDAssignedUser: tmpIDUser }).then(() =>
				{
					let tmpName = tmpNameByID[String(tmpIDUser)] || ('user #' + tmpIDUser);
					if (tmpModal.toast) { tmpModal.toast(tmpIDUser ? ('Assigned to ' + tmpName + '.') : 'Assignment cleared.', { type: 'success' }); }
					this._navigate('/work-item/' + pIDWorkItem);
				}).catch(() => { if (tmpModal.toast) { tmpModal.toast('Could not assign the work item.', { type: 'error' }); } });
			});
		}).catch(() => { /* ignore */ });
	}

	// Detail header action on an Issue: promote it into a Work Item (reverse-linked via the Issue join).
	// Mirrors spawnWorkItemFromFeedback: a modal prefilled from the issue with a searchable target picker.
	spawnWorkItemFromIssue(pKey, pIDIssue)
	{
		if (!pIDIssue) { return; }
		let tmpProviders = this.pict.providers;
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let fGo = (pResult) => { if (pResult && pResult.IDWorkItem) { this._navigate('/work-item/' + pResult.IDWorkItem); } };
		if (!tmpModal || typeof tmpModal.show !== 'function')
		{
			return tmpProviders.IssueBoardAPI.spawnWorkItemFromIssue(pIDIssue, {}).then(fGo).catch(() => { /* ignore */ });
		}
		let fEsc = (pStr) => String(pStr == null ? '' : pStr).replace(/[&<>"]/g, (pCh) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pCh]));
		Promise.all(
		[
			fetch('/1.0/Issue/' + encodeURIComponent(pIDIssue), { credentials: 'same-origin' }).then((pR) => pR.ok ? pR.json() : {}).catch(() => ({}))
		]).then((pLoaded) =>
		{
			let tmpIssue = pLoaded[0] || {};
			let tmpStyle = 'width:100%;box-sizing:border-box;padding:6px 8px;font:inherit;margin-top:2px;';
			let fField = (pLabel, pControl) => '<div style="margin-bottom:9px;"><label style="font-size:12px;color:var(--theme-color-text-secondary,#5b6376);">' + pLabel + '</label>' + pControl + '</div>';
			let fType = ['Triage', 'Research', 'Innovation', 'Issue', 'Debt', 'Enhancement'].map((pT) => '<option' + (pT === 'Issue' ? ' selected' : '') + '>' + pT + '</option>').join('');
			// A server-backed candidate search per type (WI #446), same as spawnWorkItemFromFeedback above.
			let tmpTargetTypes =
			[
				{ Type: 'Feature', Provider: tmpProviders.FeatureAPI, Descriptor: libEntityDescriptors.Entities.Feature },
				{ Type: 'Vision', Provider: tmpProviders.VisionAPI, Descriptor: libEntityDescriptors.Entities.Vision },
				{ Type: 'Module', Provider: tmpProviders.ModuleAPI, Descriptor: libEntityDescriptors.Entities.Module }
			];
			let tmpSelectedTarget = '';
			if (this.pict.AppData) { this.pict.AppData.SWI_TargetValue = ''; }
			let tmpDialog = tmpModal.show(
			{
				title: 'Spawn work item from issue',
				content:
					fField('Title', '<input id="swii-title" type="text" style="' + tmpStyle + '" value="' + fEsc(tmpIssue.Title || '') + '">') +
					fField('Type', '<select id="swii-type" style="' + tmpStyle + '">' + fType + '</select>') +
					fField('Description', '<textarea id="swii-desc" style="' + tmpStyle + 'min-height:70px;resize:vertical;">' + fEsc(tmpIssue.Description || '') + '</textarea>') +
					fField('This work is about', '<div id="swii-target-host" style="margin-top:2px;"></div>'),
				buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Create work item', Style: 'primary' }]
			});
			let tmpPickerProvider = this.pict.providers['Pict-Section-Picker'];
			if (tmpPickerProvider && typeof tmpPickerProvider.createPicker === 'function')
			{
				setTimeout(() =>
				{
					tmpPickerProvider.createPicker('SWII-TargetPicker',
						{
							DestinationAddress: '#swii-target-host',
							ValueAddress: 'AppData.SWI_TargetValue',
							Placeholder: 'Search a feature, vision, or module',
							Searchable: true,
							Options: [],
							DataProvider: libCandidateSearch.dataProviderForTypes(tmpTargetTypes),
							ResolveValue: libCandidateSearch.resolveValueForTypes(tmpTargetTypes),
							OnChange: (pVal) => { tmpSelectedTarget = pVal || ''; }
						});
					let tmpPickerView = this.pict.views['SWII-TargetPicker'];
					if (tmpPickerView) { tmpPickerView.setValue(''); tmpPickerView.render(); }
				}, 20);
			}
			tmpDialog.then((pChoice) =>
			{
				if (pChoice !== 'ok') { return; }
				let fVal = (pID) => { let tmpEl = (typeof document !== 'undefined') ? document.getElementById(pID) : null; return tmpEl ? tmpEl.value : ''; };
				let tmpTargetRaw = tmpSelectedTarget || (this.pict.AppData && this.pict.AppData.SWI_TargetValue) || '';
				let tmpTargetType = '', tmpIDTarget = 0;
				if (tmpTargetRaw) { let tmpParts = tmpTargetRaw.split(':'); tmpTargetType = tmpParts[0]; tmpIDTarget = parseInt(tmpParts[1], 10) || 0; }
				let tmpDraft =
				{
					Title: fVal('swii-title').trim(),
					Type: fVal('swii-type'),
					Description: fVal('swii-desc'),
					TargetType: tmpIDTarget ? tmpTargetType : '',
					IDTarget: tmpIDTarget
				};
				tmpProviders.IssueBoardAPI.spawnWorkItemFromIssue(pIDIssue, tmpDraft).then(fGo).catch(() => { /* ignore */ });
			});
		}).catch(() => { /* ignore */ });
	}

	// Detail header action on a Work Item: link many existing Issues to it at once (they become
	// supporting evidence in its generated prompt). A modal with a checkbox list of the tenant's issues.
	linkIssuesToWorkItem(pKey, pIDWorkItem)
	{
		if (!pIDWorkItem) { return; }
		let tmpProviders = this.pict.providers;
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		let fEsc = (pStr) => String(pStr == null ? '' : pStr).replace(/[&<>"]/g, (pCh) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pCh]));
		fetch('/1.0/Issues/0/500', { credentials: 'same-origin' }).then((pR) => pR.ok ? pR.json() : []).then((pBody) =>
		{
			let tmpIssues = (Array.isArray(pBody) ? pBody : (pBody && pBody.Records) || []).filter((pI) => !pI.Deleted);
			if (!tmpIssues.length) { if (tmpModal.toast) { tmpModal.toast('No issues to link yet.', { type: 'info' }); } return; }
			let tmpRows = tmpIssues.map((pI) =>
				'<label style="display:flex;gap:8px;align-items:flex-start;padding:5px 2px;border-bottom:1px solid var(--theme-color-border-subtle,#eef0f4);cursor:pointer;">' +
				'<input type="checkbox" class="li-issue" value="' + pI.IDIssue + '" style="margin-top:3px;flex:0 0 auto;">' +
				'<span style="font-size:13px;"><strong>#' + (pI.IssueNumber || pI.IDIssue) + '</strong> ' + fEsc(pI.Title || '') +
				' <em style="color:var(--theme-color-text-secondary,#5b6376);">[' + fEsc(pI.Reliability || 'Reported') + (pI.Severity ? ' | ' + fEsc(pI.Severity) : '') + ']</em></span></label>').join('');
			let tmpDialog = tmpModal.show(
			{
				title: 'Link issues to this work item',
				content: '<p style="font-size:13px;color:var(--theme-color-text-secondary,#5b6376);margin:0 0 8px;">Pick the issues that inform this work item. They become scoped supporting evidence in its generated prompt.</p><div style="max-height:340px;overflow:auto;">' + tmpRows + '</div>',
				buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Link selected', Style: 'primary' }]
			});
			tmpDialog.then((pChoice) =>
			{
				if (pChoice !== 'ok') { return; }
				let tmpChecked = (typeof document !== 'undefined') ? Array.prototype.slice.call(document.querySelectorAll('.li-issue:checked')).map((pEl) => parseInt(pEl.value, 10) || 0).filter((pX) => pX > 0) : [];
				if (!tmpChecked.length) { return; }
				tmpProviders.IssueBoardAPI.linkIssuesToWorkItem(pIDWorkItem, tmpChecked, 'Addresses').then((pRes) =>
				{
					let tmpN = (pRes && pRes.Linked && pRes.Linked.length) || 0;
					if (tmpModal.toast) { tmpModal.toast('Linked ' + tmpN + ' issue' + (tmpN === 1 ? '' : 's') + ' to this work item.', { type: 'success' }); }
					this._navigate('/work-item/' + pIDWorkItem);
				}).catch(() => { if (tmpModal.toast) { tmpModal.toast('Could not link the issues.', { type: 'error' }); } });
			});
		}).catch(() => { /* ignore */ });
	}

	// Detail header action on a Work Item: link many existing Feedback to it at once via the advanced
	// multi-select picker (search + facets). They become the "feedback that drove this" on both sides
	// and feed the generated prompt as context. The bulk-link route is idempotent + keeps the scalar.
	linkFeedbackToWorkItem(pKey, pIDWorkItem)
	{
		if (!pIDWorkItem) { return; }
		let tmpProviders = this.pict.providers;
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		Promise.all(
		[
			(tmpProviders.FeedbackAPI && typeof tmpProviders.FeedbackAPI.list === 'function') ? tmpProviders.FeedbackAPI.list() : Promise.resolve([]),
			fetch('/1.0/FeedbackWorkItemJoins/FilteredTo/FBV~IDWorkItem~EQ~' + encodeURIComponent(pIDWorkItem) + '/0/500', { credentials: 'same-origin' }).then((pR) => pR.ok ? pR.json() : []).catch(() => [])
		]).then((pLoaded) =>
		{
			let tmpAll = Array.isArray(pLoaded[0]) ? pLoaded[0] : [];
			let tmpJoinBody = pLoaded[1];
			let tmpJoins = Array.isArray(tmpJoinBody) ? tmpJoinBody : ((tmpJoinBody && tmpJoinBody.Records) || []);
			let tmpExclude = tmpJoins.map((pJoin) => Number(pJoin.IDFeedback)).filter((pID) => pID > 0);
			if (!tmpAll.length) { if (tmpModal.toast) { tmpModal.toast('No feedback to link yet.', { type: 'info' }); } return; }
			let tmpPicker = new libFeedbackPicker();
			tmpPicker.configure(tmpAll, { Exclude: tmpExclude });
			let tmpDialog = tmpModal.show(
			{
				title: 'Link feedback to this work item',
				width: 'min(680px, 95vw)',
				content: '<p style="font-size:13px;color:var(--theme-color-text-secondary,#5b6376);margin:0 0 8px;">Pick the feedback that drove this work item. It shows on both the work item and each feedback, and feeds the generated prompt as context.</p><div id="lfb-host"></div>',
				buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Link selected', Style: 'primary' }]
			});
			setTimeout(() =>
			{
				let tmpHost = (typeof document !== 'undefined') ? document.getElementById('lfb-host') : null;
				if (tmpHost) { tmpPicker.mount(tmpHost); }
			}, 20);
			tmpDialog.then((pChoice) =>
			{
				if (pChoice !== 'ok') { return; }
				let tmpChosen = tmpPicker.selectedIDs();
				if (!tmpChosen.length) { return; }
				tmpProviders.IssueBoardAPI.linkFeedbackToWorkItem(pIDWorkItem, tmpChosen).then((pRes) =>
				{
					let tmpN = (pRes && pRes.Linked && pRes.Linked.length) || 0;
					if (tmpModal.toast) { tmpModal.toast('Linked ' + tmpN + ' feedback to this work item.', { type: 'success' }); }
					this._navigate('/work-item/' + pIDWorkItem);
				}).catch(() => { if (tmpModal.toast) { tmpModal.toast('Could not link the feedback.', { type: 'error' }); } });
			});
		}).catch(() => { /* ignore */ });
	}

	startTestRun(pKey, pIDRoutine)
	{
		this.pict.providers.TestRunAPI.start(pIDRoutine)
			.then((pRun) =>
			{
				if (!pRun || !pRun.IDTestRun) { throw new Error('no run returned'); }
				this._navigate('/test-run/' + pRun.IDTestRun);
			})
			.catch((pErr) =>
			{
				let tmpModal = this.pict.views['Pict-Section-Modal'];
				if (tmpModal) tmpModal.toast('Could not start the run: ' + (pErr.message || pErr), { type: 'error', duration: 6000 });
			});
	}

	// Detail header action on an As-Built: confirm it actually works on the dev sandbox. Its Routine is the
	// rigorous path (start a TestRun against dev and walk the steps); marking it validated by hand is the fast
	// path for a small fix already eyeballed there. This is the gate between dev and prod: plansheet-promote
	// refuses to ship a batch that still has anything unvalidated riding it.
	//
	// Called both from the Releases page (validating one As-Built after another) and from an As-Built's own
	// header action. Only the latter should land on the As-Built page afterward -- from Releases, "mark
	// validated" refreshes the list in place so the reviewer can keep going down it without a round trip.
	validateAsBuilt(pKey, pIDAsBuilt)
	{
		if (!pIDAsBuilt) { return; }
		let tmpProviders = this.pict.providers;
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		let tmpCallerHash = (typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash.replace(/^#/, '') : '';
		let tmpFromReleases = tmpCallerHash.indexOf('/releases') === 0;
		fetch('/1.0/AsBuilt/' + encodeURIComponent(pIDAsBuilt), { credentials: 'same-origin' }).then((pR) => pR.ok ? pR.json() : {}).then((pBody) =>
		{
			let tmpAsBuilt = (pBody && pBody.AsBuilt) ? pBody.AsBuilt : (pBody || {});
			let tmpIDRoutine = Number(tmpAsBuilt.IDRoutine) || 0;
			let tmpEnv = tmpAsBuilt.Environment || 'dev';
			let tmpButtons = [{ Hash: 'cancel', Label: 'Cancel' }];
			if (tmpIDRoutine) { tmpButtons.push({ Hash: 'routine', Label: 'Run its routine on ' + tmpEnv }); }
			tmpButtons.push({ Hash: 'mark', Label: 'Mark validated', Style: 'primary' });
			let tmpDialog = tmpModal.show(
			{
				title: 'Validate on ' + tmpEnv,
				content: '<p style="font-size:13px;margin:0 0 8px;">Only a validated As-Built can be promoted. A promote refuses any batch that still has something unvalidated in it.</p>' +
					'<p style="font-size:13px;color:var(--theme-color-text-secondary,#5b6376);margin:0;">' +
					(tmpIDRoutine
						? 'Run its validation routine against ' + tmpEnv + ' and walk the steps, or mark it validated if you have already checked it there.'
						: 'This As-Built has no validation routine, so confirm you have checked it on ' + tmpEnv + ' yourself.') + '</p>',
				buttons: tmpButtons
			});
			tmpDialog.then((pChoice) =>
			{
				if (pChoice === 'routine' && tmpIDRoutine)
				{
					// The run is stamped with the environment it was walked against, so a pass means "it works on dev".
					fetch('/1.0/TestRun/Start',
						{
							method: 'POST', credentials: 'same-origin',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({ IDRoutine: tmpIDRoutine, Environment: tmpEnv })
						})
						.then((pR) => pR.ok ? pR.json() : null)
						.then((pRun) =>
						{
							let tmpIDTestRun = pRun && (pRun.IDTestRun || (pRun.TestRun && pRun.TestRun.IDTestRun));
							if (!tmpIDTestRun) { throw new Error('no run returned'); }
							this._navigate('/test-run/' + tmpIDTestRun);
						})
						.catch(() => { if (tmpModal.toast) { tmpModal.toast('Could not start the test run.', { type: 'error' }); } });
					return;
				}
				if (pChoice !== 'mark') { return; }
				let tmpProvider = tmpProviders[pKey + 'API'];
				if (!tmpProvider || typeof tmpProvider.update !== 'function') { return; }
				tmpProvider.update(pIDAsBuilt, { Status: 'Validated', ValidatedDate: (new Date()).toISOString() }).then(() =>
				{
					if (tmpModal.toast) { tmpModal.toast('Validated. It can be promoted now.', { type: 'success' }); }
					if (tmpFromReleases)
					{
						let tmpReleasesView = this.pict.views['Plansheet-Releases'];
						if (tmpReleasesView) { tmpReleasesView.refresh(); }
					}
					else
					{
						this._navigate('/as-built/' + pIDAsBuilt);
					}
				}).catch(() => { if (tmpModal.toast) { tmpModal.toast('Could not mark it validated.', { type: 'error' }); } });
			});
		}).catch(() => { /* ignore */ });
	}

	// WI #305 Layer 3: suspend / resume a registered node through the gated endpoint (content.approve + logged),
	// so a person can stop a node from claiming any work at 2am from the board, without a terminal. Called from the
	// Make this blueprint the plan sheet's cover, from the Blueprints list -> a blueprint -> "Set as cover"
	// (WI #339 + #340). Any blueprint qualifies now, including the one an entity owns, which is what makes the
	// Blueprints screen the place to choose a representative sheet for the plan sheet. Called from the
	// Blueprint detail HeaderActions as (key, id). PUT /1.0/Plansheet/Cover with 0 clears it.
	setPlansheetCover(pKey, pIDBlueprint)
	{
		if (!pIDBlueprint) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		libFrameworkClient.putJSON(this.pict, '/1.0/Plansheet/Cover', { IDBlueprint: pIDBlueprint })
			.then((pBody) =>
			{
				if (!(pBody && pBody.Success)) { throw new Error((pBody && pBody.Error) || 'Could not set the cover.'); }
				// Keep the loaded org settings in step so the cover box and the plan-sheet cards do not go on
				// showing the previous choice until the next full load.
				if (this.pict.AppData.Org && this.pict.AppData.Org.Settings) { this.pict.AppData.Org.Settings.IDPrimaryBlueprint = pIDBlueprint; }
				if (tmpModal && tmpModal.toast) { tmpModal.toast('This blueprint is now the plan sheet cover.', { type: 'success' }); }
			})
			.catch((pError) =>
			{
				if (tmpModal && tmpModal.toast) { tmpModal.toast((pError && pError.message) || 'Could not set the cover.', { type: 'error' }); }
			});
	}

	// NodeRegistration detail HeaderActions as (key, id); a suspended node is refused by Run/DeployRequest ClaimNext.
	suspendNode(pKey, pIDNodeRegistration) { this._setNodeStatus(pIDNodeRegistration, 'Suspend'); }
	resumeNode(pKey, pIDNodeRegistration) { this._setNodeStatus(pIDNodeRegistration, 'Resume'); }

	_setNodeStatus(pIDNodeRegistration, pVerb)
	{
		if (!pIDNodeRegistration) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		fetch('/1.0/NodeRegistration/' + encodeURIComponent(pIDNodeRegistration) + '/' + pVerb,
			{ method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: '{}' })
			.then((pResponse) => pResponse.ok ? pResponse.json() : pResponse.json().then((pBody) => Promise.reject(pBody)))
			.then((pBody) =>
			{
				if (tmpModal && tmpModal.toast) { tmpModal.toast('Node ' + (pBody.NodeHost || '') + ' is now ' + (pBody.Status || (pVerb + 'd')) + '.', { type: 'success' }); }
				// Land on the list so the updated Status is visible across the fleet.
				this._navigate('/node-registrations');
			})
			.catch((pError) =>
			{
				if (tmpModal && tmpModal.toast) { tmpModal.toast((pError && pError.Error) || ('Could not ' + pVerb.toLowerCase() + ' the node.'), { type: 'error' }); }
			});
	}

	// Manual recovery on the Releases page (Issue #5). A machine can leave a record somewhere it can never leave
	// on its own: an As-Built whose commit landed in a sibling repo is skipped by every dev deploy and sits at
	// Built forever. Superseding retires the record, and the reason is required, so the row ends up saying who
	// gave up on it and why.
	//
	// There is deliberately no "mark it deployed" here. The only honest options are to retire the record or go
	// make it true; claiming a deploy that never happened is the exact failure this page exists to prevent.
	supersedeAsBuilt(pIDAsBuilt)
	{
		if (!pIDAsBuilt) { return; }
		this._closeStuckRecord(
		{
			URL: '/1.0/AsBuilt/' + encodeURIComponent(pIDAsBuilt) + '/Supersede',
			Title: 'Supersede this As-Built',
			Blurb: 'Superseding retires this record. It does not put the work on dev, and nothing on this page will bring it back.',
			Hint: 'Why is it being retired? For example: the commit landed in plansheet-mcp, so no app deploy will ever pick it up.',
			Confirm: 'Supersede',
			Done: 'Superseded. It is off the pending list.',
			Fail: 'Could not supersede it.'
		});
	}

	// The other exit from a stranded As-Built, and the one that was missing (WI #369). Supersede retires a
	// record; this one CORRECTS it, for the row whose work is provably already on dev and simply never got
	// stamped. The server refuses unless it can prove that from evidence a node wrote, so this asks for no
	// reason: the proof is the reason, and it goes onto the audit trail with the row.
	//
	// The page only offers this where the server said it would work, so a refusal here means the page was out
	// of date -- in which case the server's own sentence is what the person needs to read, not a generic error.
	rebindAsBuilt(pIDAsBuilt)
	{
		if (!pIDAsBuilt) { return; }
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		tmpModal.show(
		{
			title: 'Rebind this As-Built',
			content: '<p style="font-size:13px;margin:0 0 8px;">This records that the work is on the dev sandbox, which the deploy '
				+ 'could not confirm for itself when it ran.</p>'
				+ '<p style="font-size:12px;color:var(--theme-color-text-secondary,#5b6376);margin:0;">It only goes through if the server can '
				+ 'still prove the commit is there. Nothing is deployed or rebuilt; the record is brought in line with what already shipped.</p>',
			buttons: [{ Hash: 'cancel', Label: 'Never mind' }, { Hash: 'confirm', Label: 'Rebind', Style: 'primary' }]
		}).then((pChoice) =>
		{
			if (pChoice !== 'confirm') { return; }
			fetch('/1.0/AsBuilt/' + encodeURIComponent(pIDAsBuilt) + '/Rebind',
				{ method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: '{}' })
				.then((pResponse) => pResponse.json().catch(() => ({})).then((pBody) =>
				{
					if (!pResponse.ok) { throw new Error((pBody && pBody.Error) || 'Could not rebind it.'); }
					return pBody;
				}))
				.then((pBody) =>
				{
					if (tmpModal.toast) { tmpModal.toast('Rebound to "' + ((pBody && pBody.Environment) || 'dev') + '". It is ready to validate.', { type: 'success' }); }
					let tmpReleasesView = this.pict.views['Plansheet-Releases'];
					if (tmpReleasesView) { tmpReleasesView.refresh(); }
				})
				.catch((pError) =>
				{
					if (tmpModal.toast) { tmpModal.toast((pError && pError.message) || 'Could not rebind it.', { type: 'error' }); }
				});
		});
	}

	// The deploy-queue twin: a node that died mid-run leaves its request Running with nothing alive to finish it.
	cancelDeployRequest(pIDDeployRequest)
	{
		if (!pIDDeployRequest) { return; }
		this._closeStuckRecord(
		{
			URL: '/1.0/DeployRequest/' + encodeURIComponent(pIDDeployRequest) + '/Cancel',
			Title: 'Cancel this deploy request',
			Blurb: 'Cancelling closes the request out. It does not stop a deploy that is genuinely still running on a node.',
			Hint: 'Why is it being cancelled? For example: the node died mid-run and nothing is left to finish it.',
			Confirm: 'Cancel the request',
			Done: 'Cancelled. The queue is clear.',
			Fail: 'Could not cancel it.'
		});
	}

	// Shared by both recovery actions: ask for a reason, refuse to send without one, POST, and refresh the page in
	// place so the row disappears under the person who cleared it. The reason is enforced on the server too; this
	// only saves a round trip.
	_closeStuckRecord(pOptions)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		let tmpDialogElement = null;
		let tmpDialog = tmpModal.show(
		{
			title: pOptions.Title,
			content: '<p style="font-size:13px;margin:0 0 8px;">' + pOptions.Blurb + '</p>'
				+ '<p style="font-size:12px;color:var(--theme-color-text-secondary,#5b6376);margin:0 0 6px;">' + pOptions.Hint + '</p>'
				+ '<textarea class="ps-recovery-reason" rows="3" style="width:100%;box-sizing:border-box;font:inherit;font-size:13px;'
				+ 'padding:6px 8px;border:1px solid var(--theme-color-border-default,#d8dde6);border-radius:6px;"></textarea>',
			buttons: [{ Hash: 'cancel', Label: 'Never mind' }, { Hash: 'confirm', Label: pOptions.Confirm, Style: 'primary' }],
			onOpen: (pDialog) => { tmpDialogElement = pDialog; }
		});
		tmpDialog.then((pChoice) =>
		{
			if (pChoice !== 'confirm') { return; }
			// Read the reason off the dialog element captured at open. The promise resolves as the dialog is torn
			// down, so the node may already be detached from the document, but it still holds what was typed.
			let tmpField = tmpDialogElement ? tmpDialogElement.querySelector('.ps-recovery-reason') : null;
			let tmpReason = tmpField ? String(tmpField.value || '').trim() : '';
			if (!tmpReason)
			{
				if (tmpModal.toast) { tmpModal.toast('A reason is required.', { type: 'error' }); }
				return;
			}
			fetch(pOptions.URL,
				{
					method: 'POST', credentials: 'same-origin',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ Reason: tmpReason })
				})
				.then((pResponse) => pResponse.json().catch(() => ({})).then((pBody) =>
				{
					// Surface what the server actually said (it refuses a promoted record, or an unentitled
					// person, by name) rather than a generic failure.
					if (!pResponse.ok) { throw new Error((pBody && pBody.Error) || pOptions.Fail); }
					return pBody;
				}))
				.then(() =>
				{
					if (tmpModal.toast) { tmpModal.toast(pOptions.Done, { type: 'success' }); }
					let tmpReleasesView = this.pict.views['Plansheet-Releases'];
					if (tmpReleasesView) { tmpReleasesView.refresh(); }
				})
				.catch((pError) =>
				{
					if (tmpModal.toast) { tmpModal.toast((pError && pError.message) || pOptions.Fail, { type: 'error' }); }
				});
		});
	}

	// A queue click that came back 409 means the lane was already busy: something is in flight for this
	// environment or this toolchain. That is not a failure, it is the answer, and the page it came from is
	// simply out of date. Say the server's own sentence in a neutral tone and refresh in place, so what is
	// on screen becomes true and the button that should not have been there is gone.
	//
	// Anything else really did fail, and keeps the error voice.
	_reportQueueResult(pError, pFallback, pViewIdentifier)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpBusy = (pError && pError.StatusCode === 409);
		if (tmpModal && tmpModal.toast)
		{
			// The busy sentence is longer than a normal toast and worth reading, so it gets more time.
			// Anything else takes the shell's default: toast() merges options over its defaults with
			// Object.assign, so passing duration: undefined would REPLACE the default with undefined and
			// the toast would never dismiss itself.
			let tmpOptions = { type: tmpBusy ? 'info' : 'error' };
			if (tmpBusy) { tmpOptions.duration = 6000; }
			tmpModal.toast(tmpBusy ? (pError.message || pFallback) : (pFallback + ': ' + ((pError && pError.message) || pError)), tmpOptions);
		}
		if (tmpBusy)
		{
			let tmpView = this.pict.views[pViewIdentifier];
			if (tmpView && typeof tmpView.refresh === 'function') { tmpView.refresh(); }
		}
	}

	// The operator override behind an in-flight lane. Same shape as _closeStuckRecord: ask for a reason,
	// refuse to send without one, then act and refresh in place. A reason is required on the server too;
	// this only saves a round trip.
	//
	// Queuing a second promote is genuinely harmless, so the reason is not there to argue anyone out of it.
	// It is there so the extra row is not silent about having been a deliberate exception.
	_forceQueue(pOptions)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		let tmpDialogElement = null;
		tmpModal.show(
		{
			title: pOptions.Title,
			content: '<p style="font-size:13px;margin:0 0 8px;">' + pOptions.Blurb + '</p>'
				+ '<p style="font-size:12px;color:var(--theme-color-text-secondary,#5b6376);margin:0 0 6px;">' + pOptions.Hint + '</p>'
				+ '<textarea class="ps-force-reason" rows="3" style="width:100%;box-sizing:border-box;font:inherit;font-size:13px;'
				+ 'padding:6px 8px;border:1px solid var(--theme-color-border-default,#d8dde6);border-radius:6px;"></textarea>',
			buttons: [{ Hash: 'cancel', Label: 'Never mind' }, { Hash: 'confirm', Label: pOptions.Confirm, Style: 'primary' }],
			onOpen: (pDialog) => { tmpDialogElement = pDialog; }
		}).then((pChoice) =>
		{
			if (pChoice !== 'confirm') { return; }
			let tmpField = tmpDialogElement ? tmpDialogElement.querySelector('.ps-force-reason') : null;
			let tmpReason = tmpField ? String(tmpField.value || '').trim() : '';
			if (!tmpReason)
			{
				if (tmpModal.toast) { tmpModal.toast('A reason is required.', { type: 'error' }); }
				return;
			}
			pOptions.Send(tmpReason).then(() =>
			{
				if (tmpModal.toast) { tmpModal.toast(pOptions.Done, { type: 'success' }); }
				let tmpView = this.pict.views[pOptions.View];
				if (tmpView && typeof tmpView.refresh === 'function') { tmpView.refresh(); }
			}).catch((pError) => this._reportQueueResult(pError, pOptions.Fail, pOptions.View));
		});
	}

	// Releases page: start a Deploy-to-dev run of the current trunk. A deploy node claims the run and its one
	// auto step enqueues the deploy, so the same deploy still runs on the node that holds the credentials -- the
	// run just makes its stage and progress visible on the Runs page. Deploy credentials live on the node.
	//
	// A 409 means a deploy-dev run is already in flight (its own single-flight lane). The Releases page hides
	// this button once the run's deploy request is in flight; there is a brief window after the click, before a
	// node claims the run, where the button is still shown -- the lane 409 makes a second click harmless.
	deployDev()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpAPI = this.pict.providers.RunAPI;
		if (!tmpAPI || typeof tmpAPI.startRun !== 'function') { return; }
		let fQueue = () => tmpAPI.startRun('deploy-dev', {}).then(() =>
		{
			if (tmpModal && tmpModal.toast) { tmpModal.toast('Queued a dev deploy run. Watch its progress on the Runs page.', { type: 'success' }); }
			let tmpView = this.pict.views['Plansheet-Releases']; if (tmpView) { tmpView.refresh(); }
		}).catch((pErr) => this._reportQueueResult(pErr, 'Could not queue the deploy', 'Plansheet-Releases'));
		if (!tmpModal || typeof tmpModal.show !== 'function') { return fQueue(); }
		tmpModal.show(
		{
			title: 'Deploy dev',
			content: '<p style="font-size:13px;margin:0;">Deploy the current trunk (origin/main) to the dev sandbox as a run you can watch on the Runs page. A deploy node runs it; nothing deploys from your browser.</p>',
			buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'go', Label: 'Queue dev deploy', Style: 'primary' }]
		}).then((pChoice) => { if (pChoice === 'go') { fQueue(); } });
	}

	// Releases page: start a Promote-to-production run. Offered whenever dev is running a commit production is
	// not -- As-Builts are the audit trail, not a gate, so an unvalidated (or As-Built-less) dev commit can
	// still be promoted (WI #330). This is a confirm; the run's step is auto, because pressing Promote here is
	// the human decision -- the gated promote lives on the full plansheet-software pipeline.
	//
	// Production takes one promotion at a time (the promote-prod run lane). While one is in flight this button
	// is replaced by what is in flight, so a promote can no longer be queued twice from a stale page.
	promoteToProd()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpAPI = this.pict.providers.RunAPI;
		if (!tmpAPI || typeof tmpAPI.startRun !== 'function') { return; }
		let fQueue = () => tmpAPI.startRun('promote-prod', {}).then(() =>
		{
			if (tmpModal && tmpModal.toast) { tmpModal.toast('Queued a promotion run. Watch its progress on the Runs page.', { type: 'success' }); }
			let tmpView = this.pict.views['Plansheet-Releases']; if (tmpView) { tmpView.refresh(); }
		}).catch((pErr) => this._reportQueueResult(pErr, 'Could not queue the promotion', 'Plansheet-Releases'));
		if (!tmpModal || typeof tmpModal.show !== 'function') { return fQueue(); }
		tmpModal.show(
		{
			title: 'Promote to production',
			content: '<p style="font-size:13px;margin:0;">Promote the current dev commit to production as a run you can watch on the Runs page. A deploy node runs it; nothing deploys from your browser.</p>',
			buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'go', Label: 'Promote to prod', Style: 'primary' }]
		}).then((pChoice) => { if (pChoice === 'go') { fQueue(); } });
	}

	// Start a promotion run past one already in flight. Offered only to someone with content.approve, and only
	// while a promotion is actually in flight, which is the only time the plain button is missing. Force plus a
	// written reason is what gets a run past the single-flight lane; it lands a run.forced beat on the timeline.
	promoteToProdAnyway()
	{
		let tmpAPI = this.pict.providers.RunAPI;
		if (!tmpAPI || typeof tmpAPI.startRun !== 'function') { return; }
		this._forceQueue(
		{
			Title: 'Promote anyway',
			Blurb: 'Production already has a promotion in flight. Starting a second one is harmless, and it will run after the first finishes.',
			Hint: 'Why start another? For example: the first one is stuck on a node that is not coming back, and it has been cancelled.',
			Confirm: 'Start it anyway',
			Done: 'Started. It runs after the one already in flight.',
			Fail: 'Could not start the promotion',
			View: 'Plansheet-Releases',
			Send: (pReason) => tmpAPI.startRun('promote-prod', { Force: true, Reason: pReason })
		});
	}

	// Runs page: queue a run of the chosen toolchain for a node to claim. Nothing executes here; the run
	// is a row with its steps snapshotted, and a node picks it up on its next tick.
	//
	// Takes the catalog INDEX rather than the key, so no tenant-authored string is ever interpolated
	// into the button's onclick (see the Runs-StartChain template for why that nesting is unsafe). The
	// key and name are read back out of AppData here, where they are values rather than markup.
	startToolchainRun(pCatalogIndex)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpAPI = this.pict.providers.RunAPI;
		if (!tmpAPI || typeof tmpAPI.startRun !== 'function') { return; }

		let tmpCatalog = ((this.pict.AppData.Runs || {}).Toolchains || []);
		let tmpChain = tmpCatalog[Number(pCatalogIndex)];
		if (!tmpChain || !tmpChain.Key)
		{
			// The catalog moved under the click (someone else adopted or retired one). Reload rather
			// than starting whatever now sits at that index.
			if (tmpModal && tmpModal.toast) { tmpModal.toast('That toolchain is no longer in the catalog. Refreshing.', { type: 'error' }); }
			let tmpStale = this.pict.views['Plansheet-Runs']; if (tmpStale) { tmpStale.refresh(); }
			return;
		}
		let tmpKey = tmpChain.Key;
		let tmpName = String(tmpChain.Name || tmpKey);
		// The modal renders its content as HTML, so the name has to be escaped on the way in.
		let tmpSafeName = tmpName.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

		let tmpGated = Number(tmpChain.GatedCount) || 0;
		let tmpShape = tmpChain.StepCount + ' step' + (tmpChain.StepCount === 1 ? '' : 's')
			+ (tmpGated ? (', ' + tmpGated + ' of which will stop and wait for your approval') : ', none of which need your approval');

		let fQueue = () => tmpAPI.startRun(tmpKey, {}).then(() =>
		{
			if (tmpModal && tmpModal.toast) { tmpModal.toast('Queued a run of ' + tmpName + '. A node will claim it and bring the gated steps here.', { type: 'success' }); }
			let tmpView = this.pict.views['Plansheet-Runs']; if (tmpView) { tmpView.refresh(); }
		}).catch((pErr) => this._reportQueueResult(pErr, 'Could not queue the run', 'Plansheet-Runs'));
		if (!tmpModal || typeof tmpModal.show !== 'function') { return fQueue(); }
		tmpModal.show(
		{
			title: 'Start a run of ' + tmpName,
			content: '<p style="font-size:13px;margin:0;">Queue a run of <strong>' + tmpSafeName + '</strong>: ' + tmpShape
				+ '. A node does the work; nothing runs from your browser.</p>',
			buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'go', Label: 'Start the run', Style: 'primary' }]
		}).then((pChoice) => { if (pChoice === 'go') { fQueue(); } });
	}

	// Start a run of a chain that already has one, on purpose. Takes the catalog index for the same reason
	// startToolchainRun does: an integer in that onclick cannot break out of the JS string it sits in.
	startToolchainRunAnyway(pCatalogIndex)
	{
		let tmpAPI = this.pict.providers.RunAPI;
		if (!tmpAPI || typeof tmpAPI.startRun !== 'function') { return; }
		let tmpCatalog = ((this.pict.AppData.Runs || {}).Toolchains || []);
		let tmpChain = tmpCatalog[Number(pCatalogIndex)];
		if (!tmpChain || !tmpChain.Key)
		{
			let tmpStale = this.pict.views['Plansheet-Runs']; if (tmpStale) { tmpStale.refresh(); }
			return;
		}
		this._forceQueue(
		{
			Title: 'Start another run of ' + String(tmpChain.Name || tmpChain.Key),
			Blurb: 'This toolchain already has a run in flight. A run waiting for someone to approve a step is still holding the chain, and a node will claim whichever run is next in line.',
			Hint: 'Why start another? For example: the run in flight is parked on a gate nobody is going to answer, and it is being rejected.',
			Confirm: 'Start it anyway',
			Done: 'Started. It queues behind the run already in flight.',
			Fail: 'Could not queue the run',
			View: 'Plansheet-Runs',
			Send: (pReason) => tmpAPI.startRun(tmpChain.Key, { Force: true, Reason: pReason })
		});
	}

	// Runs page: answer a gated step. This is the consent point the whole surface exists for, so it
	// confirms first and says plainly what approving will cause. The server gates it on content.approve.
	consentRunStep(pIDRunStep, pDecision)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpAPI = this.pict.providers.RunAPI;
		if (!tmpAPI || typeof tmpAPI.consent !== 'function') { return; }
		let tmpApprove = (pDecision === 'Approve');
		let fAnswer = () => tmpAPI.consent(pIDRunStep, tmpApprove ? 'Approve' : 'Reject', '').then(() =>
		{
			if (tmpModal && tmpModal.toast)
			{
				tmpModal.toast(tmpApprove ? 'Approved. The node runs this step on its next tick.' : 'Rejected. The run is closed.',
					{ type: tmpApprove ? 'success' : 'info' });
			}
			let tmpView = this.pict.views['Plansheet-Runs']; if (tmpView) { tmpView.refresh(); }
		}).catch((pErr) => { if (tmpModal && tmpModal.toast) { tmpModal.toast('Could not record the decision: ' + (pErr.message || pErr), { type: 'error' }); } });
		if (!tmpModal || typeof tmpModal.show !== 'function') { return fAnswer(); }
		tmpModal.show(
		{
			title: tmpApprove ? 'Approve this step' : 'Reject this step',
			content: tmpApprove
				? '<p style="font-size:13px;margin:0;">The node runs this step on its next tick, using the scope shown on the step. This is the point where the action happens.</p>'
				: '<p style="font-size:13px;margin:0;">The step is marked rejected and the run is closed. Nothing further runs.</p>',
			buttons: [{ Hash: 'cancel', Label: 'Cancel' }, { Hash: 'go', Label: tmpApprove ? 'Approve' : 'Reject', Style: tmpApprove ? 'primary' : 'danger' }]
		}).then((pChoice) => { if (pChoice === 'go') { fAnswer(); } });
	}

	// Runs page: stop a run going wrong, without SSH-ing into the node (Work Item #451). This does not
	// kill anything from the browser -- it marks the run cancel-requested, and the node stops itself on
	// its own next heartbeat, closing the run Cancelled and parking whatever the session had committed
	// (WI #444) rather than losing it. The server answers a no-op (not an error) if nothing is actually
	// running by the time this reaches it.
	cancelRun(pIDRun)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpAPI = this.pict.providers.RunAPI;
		if (!tmpAPI || typeof tmpAPI.cancel !== 'function') { return; }
		let fStop = () => tmpAPI.cancel(pIDRun, 'stopped from the Runs page').then((pResult) =>
		{
			if (tmpModal && tmpModal.toast)
			{
				let tmpStopped = !!(pResult && pResult.Cancelled);
				tmpModal.toast(tmpStopped
					? 'Stop requested. The node ends the run within one heartbeat and parks any work it had committed.'
					: ((pResult && pResult.Reason) || 'Nothing to stop -- that run is no longer in progress.'),
					{ type: tmpStopped ? 'success' : 'info' });
			}
			let tmpView = this.pict.views['Plansheet-Runs']; if (tmpView) { tmpView.refresh(); }
		}).catch((pErr) => { if (tmpModal && tmpModal.toast) { tmpModal.toast('Could not stop the run: ' + (pErr.message || pErr), { type: 'error' }); } });
		if (!tmpModal || typeof tmpModal.show !== 'function') { return fStop(); }
		tmpModal.show(
		{
			title: 'Stop this run',
			content: '<p style="font-size:13px;margin:0;">The node ends it within one heartbeat. Anything the session already committed is preserved on its branch, not lost -- the run just stops going further.</p>',
			buttons: [{ Hash: 'cancel', Label: 'Never mind' }, { Hash: 'go', Label: 'Stop the run', Style: 'danger' }]
		}).then((pChoice) => { if (pChoice === 'go') { fStop(); } });
	}

	showRuns() { this._navigate('/runs'); }

	// Open the Feedback create form with prefilled values (SourceType + IDTestRun
	// from a finished run, or IDKeystoneCustomer from a customer page). Keys that
	// match descriptor Fields land in the form; the rest ride along hidden so the
	// FKs persist through save. The EntityList consumes the stash on its next
	// showEntity('Feedback').
	showNewFeedback(pPrefill)
	{
		this.pict.AppData.EntityListPrefill = { Key: 'Feedback', Values: Object.assign({}, pPrefill || {}) };
		// To the CREATE route, not the list route. This used to navigate to /feedback and then paint a
		// form over it, which needed a hand-rolled workaround for the case where you were already on
		// /feedback (Navigo does not re-fire the current route, so the handoff had to bypass it) -- and
		// it left the URL saying "list" while the pane held a half-filled form. /feedback/new is a
		// different URL from wherever you were, so the router does the work and the address bar is true.
		if (!this.showEntityNew('Feedback')) { this.pict.views['Plansheet-EntityList'].showEntity('Feedback'); }
	}

	// Public: route to the full-page doc editor (copy/paste-linkable, reloadable).
	// pDocPath is a Docs Lake path (may contain slashes); it rides the wildcard.
	showDocEditor(pOwnerType, pIDOwner, pDocPath)
	{
		this._navigate('/doc-edit/' + pOwnerType + '/' + (Number(pIDOwner) || 0) + '/' + String(pDocPath || ''));
	}

	// Route handler for /doc-edit/* : render the editor for the matched target.
	_renderDocEditor(pMatch)
	{
		this._setActiveNav('Docs');
		this._setPageTitle('Editing ' + this._docTitleFromPath(pMatch && pMatch.Path));
		this.pict.views['Plansheet-DocEditor'].showEditor(pMatch.OwnerType, pMatch.IDOwner, pMatch.Path);
	}

	showDrawingEditor(pContext, fOnInsert)
	{
		this.pict.views['Plansheet-DrawingEditor'].open(pContext, fOnInsert);
	}

	showFoundationDoc(pSlug)
	{
		let tmpPath = String(pSlug || ''); let tmpSection = '';
		let tmpHashIdx = tmpPath.indexOf('#');
		if (tmpHashIdx >= 0) { tmpSection = tmpPath.slice(tmpHashIdx + 1); tmpPath = tmpPath.slice(0, tmpHashIdx); }
		this._navigate('/docs/' + tmpPath + (tmpSection ? '::' + tmpSection : ''));
	}

	// Called by the docs view after the inline-doc section loads a document, so
	// the URL reflects what's on screen (without triggering a reload).
	syncDocURL(pPath, pSection)
	{
		if (!pPath) { return; }
		this._navigate('/docs/' + pPath + (pSection ? '::' + pSection : ''));
	}

	// ---- Navigation handlers called by views ----

	showAuth(pMode)
	{
		// Strip the app chrome (top bar + sidebar) so the sign-in screen stands on its own.
		if (typeof document !== 'undefined' && document.body) { document.body.classList.add('plansheet-auth-mode'); }
		if (this.pict.AppData.Auth)
		{
			this.pict.AppData.Auth.UISlot = this.pict.AppData.Auth.UISlot || {};
			this.pict.AppData.Auth.UISlot.Mode = (pMode === 'signup' || pMode === 'verify' || pMode === 'enroll') ? pMode : 'login';
		}
		this.pict.views['Plansheet-Auth'].render();
	}

	// Wire the magic-link engine's hooks into plansheet's auth state. onLoginSuccess bridges through
	// AuthClient.checkSession so AppData.Auth is populated exactly as a normal sign-in; a 2FA account hands
	// its challenge envelope to the existing 'verify' mode; a dead/expired link drops back to the login
	// screen with the reason.
	_wireMagicLogin()
	{
		let tmpView = this.pict.views['Plansheet-MagicLogin'];
		if (!tmpView) { return; }
		let tmpApp = this;
		tmpView.onLoginSuccess = function ()
		{
			// Mirror the normal post-checkSession sign-in sequence exactly: repaint the top-bar user chip out
			// of its guest state, enable 401 session recovery, honor the pinned plan sheet, then show the
			// workspace. (The earlier version skipped the top-bar repaint, so it kept showing "Sign in".)
			tmpApp.pict.providers.AuthClient.checkSession().then((pLoggedIn) =>
			{
				if (tmpApp.pict.views['Plansheet-TopBar-User']) { tmpApp.pict.views['Plansheet-TopBar-User'].render(); }
				if (pLoggedIn)
				{
					tmpApp._clearMagicHash();
					tmpApp._enableSessionRecovery();
					tmpApp._applyPinnedCustomerIfNeeded().then(() => tmpApp._showWorkspace());
				}
				else { tmpApp.showAuth('login'); }
			}).catch(() => tmpApp.showAuth('login'));
		};
		tmpView.onMagicLinkChallenge = function (pEnvelope)
		{
			let tmpEnv = pEnvelope || {};
			if (tmpApp.pict.AppData.Auth)
			{
				tmpApp.pict.AppData.Auth.PendingChallenge =
				{
					ChallengeToken: tmpEnv.ChallengeToken,
					Channel: tmpEnv.Channel,
					MaskedDestination: tmpEnv.MaskedDestination
				};
			}
			tmpApp._clearMagicHash();
			tmpApp.showAuth('verify');
		};
		tmpView.onLoginFailed = function (pError)
		{
			tmpApp._clearMagicHash();
			if (tmpApp.pict.AppData.Auth)
			{
				tmpApp.pict.AppData.Auth.UISlot = tmpApp.pict.AppData.Auth.UISlot || {};
				tmpApp.pict.AppData.Auth.UISlot.Error = [{ Message: pError || 'That sign-in link is no longer valid.' }];
			}
			tmpApp.showAuth('login');
		};
	}

	// Extract a magic-link token from the current URL hash (#/magic/<token>), or '' if none.
	_magicTokenFromURL()
	{
		if (typeof window === 'undefined' || !window.location) { return ''; }
		let tmpMatch = String(window.location.hash || '').match(/^#\/magic\/([^\/?#]+)/);
		return (tmpMatch && tmpMatch[1]) ? decodeURIComponent(tmpMatch[1]) : '';
	}

	// Verify a magic-link token: show the auth screen as a backdrop while the engine verifies, then its
	// hooks sign in / challenge / fail.
	_handleMagicLink(pToken)
	{
		this.showAuth('login');
		let tmpView = this.pict.views['Plansheet-MagicLogin'];
		if (!tmpView || typeof tmpView.verifyMagicLink !== 'function') { return; }
		tmpView.verifyMagicLink(pToken);
	}

	// Replace the #/magic/<token> URL with #/ so the one-time token does not linger in history or a reload.
	_clearMagicHash()
	{
		if (typeof window === 'undefined' || !window.history || !window.history.replaceState) { return; }
		try { window.history.replaceState(null, '', (window.location.pathname || '/') + (window.location.search || '') + '#/'); }
		catch (pErr) { /* no-op */ }
	}

	// --- Invite links (#/invite/<token>): auto-accept ---------------------------
	_inviteTokenFromURL()
	{
		if (typeof window === 'undefined' || !window.location) { return ''; }
		let tmpMatch = String(window.location.hash || '').match(/^#\/invite\/([^\/?#]+)/);
		return (tmpMatch && tmpMatch[1]) ? decodeURIComponent(tmpMatch[1]) : '';
	}

	// Handle a clicked invite link: accept immediately if already signed in; otherwise show the sign-in
	// screen (with context) and accept right after they sign in. The token is stashed across sign-in.
	_handleInviteToken(pToken)
	{
		this._PendingInviteToken = pToken;
		this.pict.providers.AuthClient.checkSession().then((pLoggedIn) =>
		{
			if (this.pict.views['Plansheet-TopBar-User']) { this.pict.views['Plansheet-TopBar-User'].render(); }
			if (pLoggedIn) { this._acceptPendingInvite(); }
			else { this._showInviteAuth(pToken); }
		}).catch(() => this._showInviteAuth(pToken));
	}

	// Signed out: show the sign-in screen and, best-effort, a toast with the invite's context. The pending
	// token stays stashed and is accepted by onAuthChanged once they sign in or sign up.
	_showInviteAuth(pToken)
	{
		this.showAuth('login');
		libFrameworkClient.getJSON(this.pict, '/1.0/Invitation/ByToken/' + encodeURIComponent(pToken)).then((pBody) =>
		{
			let tmpModal = this.pict.views['Pict-Section-Modal'];
			if (!tmpModal || typeof tmpModal.toast !== 'function') { return; }
			if (pBody && pBody.Valid && pBody.CustomerName)
			{
				tmpModal.toast('You are invited to ' + pBody.CustomerName + '. Sign in (or create an account) with ' + (pBody.Email || 'your email') + ' to join.', { type: 'info', duration: 8000 });
			}
			else if (pBody && pBody.Expired)
			{
				tmpModal.toast('That invitation has expired -- ask for a new one.', { type: 'error' });
			}
		}).catch(() => { /* context toast is best-effort */ });
	}

	// Accept the stashed invite token (the server reactivates/creates the persona and switches the session
	// into the plan sheet). On success, re-sync auth and show the plan sheet; on a mismatch/expiry, show the
	// app with the reason.
	_acceptPendingInvite()
	{
		let tmpToken = this._PendingInviteToken || '';
		this._PendingInviteToken = null;
		if (!tmpToken) { this._clearMagicHash(); this._showWorkspace(); return; }
		libFrameworkClient.postJSON(this.pict, '/1.0/Account/Invitations/AcceptByToken', { Token: tmpToken }).then(() =>
		{
			this._clearMagicHash();
			this.pict.providers.AuthClient.checkSession().then(() =>
			{
				if (this.pict.views['Plansheet-TopBar-User']) { this.pict.views['Plansheet-TopBar-User'].render(); }
				this._enableSessionRecovery();
				this._showWorkspace();
				let tmpModal = this.pict.views['Pict-Section-Modal'];
				if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast('You joined the plan sheet.', { type: 'success' }); }
			});
		}).catch((pErr) =>
		{
			this._clearMagicHash();
			this._showWorkspace();
			let tmpModal = this.pict.views['Pict-Section-Modal'];
			if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast((pErr && pErr.message) || 'Could not accept the invitation.', { type: 'error' }); }
		});
	}

	// The session browse surface: a normal entity list at #/sessions (the
	// calendar was retired). The vendored viewer calls this to back out -- the
	// Dropzone after an upload, the Layout's back button -- and it routes there so
	// the browser URL and history stay correct (the viewer/uploader are their own
	// routes, #/session/:id and #/sessions/upload).
	showSessions()
	{
		this._navigate('/sessions');
	}

	// Public: route to a stored session's viewer. The Session list's "Open" maps
	// here (the descriptor's OpenAction), so a session is copy/paste-linkable and
	// survives a reload; the route renders via openServerSession().
	showSession(pID)
	{
		this._navigate('/session/' + (Number(pID) || 0));
	}

	/**
	 * Render a parsed session into the workspace. Called by the section's
	 * loadSessionFromZip() (a calendar click) and by the Dropzone's local
	 * fallback path. The parser has already written the normalized session to
	 * AppData.Session; the vendored Layout owns #ClaudeTransformer-Workspace
	 * and builds the Signature / Timeline / Conversation sub-containers its
	 * siblings then fill.
	 */
	onSessionLoaded(pSession)
	{
		this.pict.views.Layout.updateLabels();
		this.pict.views.Layout.render();
		this.pict.views.Signature.render();
		this.pict.views.Timeline.render();
		this.pict.views.Conversation.render();
	}

	// Mount the session-viewer's container into the workspace via the Pict DOM
	// abstraction (ContentAssignment) instead of raw innerHTML.
	_mountTransformerContainer()
	{
		this.pict.ContentAssignment.assignContent('#Plansheet-Workspace',
			'<div id="ClaudeTransformer-Workspace" style="height:100%;display:flex;flex-direction:column"></div>');
	}

	// Center a spinner in the workspace while a session downloads + parses (a big
	// transcript takes a beat). The SVG animates on its own (SMIL), so this needs
	// no injected stylesheet; the viewer's first render replaces it.
	_showSessionLoading(pMessage)
	{
		let tmpHTML =
			'<div style="flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;color:var(--theme-color-text-secondary,#5b6376)">'
			+ '<svg width="36" height="36" viewBox="0 0 50 50" aria-hidden="true">'
			+ '<circle cx="25" cy="25" r="20" fill="none" stroke="var(--theme-color-border-default,#d8dde6)" stroke-width="5"></circle>'
			+ '<circle cx="25" cy="25" r="20" fill="none" stroke="var(--theme-color-brand-primary,#2988df)" stroke-width="5" stroke-linecap="round" stroke-dasharray="94 31">'
			+ '<animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite"></animateTransform>'
			+ '</circle></svg>'
			+ '<div style="font-size:14px">' + (pMessage || 'Loading...') + '</div>'
			+ '</div>';
		this.pict.ContentAssignment.assignContent('#ClaudeTransformer-Workspace', tmpHTML);
	}

	// Public: route to the session uploader (the Sessions list's "Upload session"
	// button maps here via the descriptor's CreateButton).
	showUploader()
	{
		this._navigate('/sessions/upload');
	}

	// Route handler for /sessions/upload: mount the dropzone in the workspace. A
	// signed-in user who drops a .zip has it uploaded to their account by the
	// Dropzone (via SessionAPI), which lands a new row and returns to the list.
	_renderUploader()
	{
		this._setActiveNav('Session');
		this._mountTransformerContainer();
		this.pict.views['Pict-Section-ClaudeTransformer'].showDropzone();
	}

	signOut()
	{
		// Close the mobile menu (or any open modal) before swapping to the auth screen; signing out
		// does not reload, so a lingering modal would otherwise cover the login.
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.dismissModals === 'function') { tmpModal.dismissModals(); }
		// Deliberate sign-out: disarm session recovery so the impending 401s do
		// not pop the re-auth modal over the login screen.
		this._disableSessionRecovery();
		this.pict.providers.AuthClient.logout().then(() =>
		{
			if (this.pict.views['Plansheet-TopBar-User']) this.pict.views['Plansheet-TopBar-User'].render();
			this.showAuth('login');
		});
	}

	onAuthChanged()
	{
		if (this.pict.views['Plansheet-TopBar-User']) this.pict.views['Plansheet-TopBar-User'].render();
		if (this.pict.AppData.Auth && this.pict.AppData.Auth.LoggedIn)
		{
			this._enableSessionRecovery();
			// If they signed in to accept an invitation (clicked an invite link while signed out), accept it
			// now -- it switches them into the plan sheet. Otherwise show the normal workspace.
			if (this._PendingInviteToken) { this._acceptPendingInvite(); }
			else { this._showWorkspace(); }
		}
		else
		{
			this.showAuth('login');
		}
	}

	/**
	 * Arm transparent session recovery: on a 401, the Fable RestClient behind
	 * EntityProvider awaits the in-place re-auth modal and replays the request.
	 * The hook is UI-agnostic in the framework; here we hand it the app's modal.
	 * Idempotent -- safe to call on every auth transition.
	 */
	_enableSessionRecovery()
	{
		let tmpRestClient = this._sessionRestClient();
		if (!tmpRestClient) { return; }
		let tmpReAuth = this.pict.views['Plansheet-ReAuth'];
		if (!tmpReAuth) { return; }
		// Stash the identity while the session is live -- the proactive check nulls
		// AppData.Auth.User on expiry, so the modal could not prefill without this.
		if (typeof tmpReAuth.rememberIdentity === 'function' && this.pict.AppData.Auth)
		{
			tmpReAuth.rememberIdentity(this.pict.AppData.Auth.User);
		}
		tmpRestClient.authenticationRecovery = () => tmpReAuth.ensure().then((pRecovered) =>
		{
			// However recovery was triggered (a 401 replay or the proactive
			// banner), a success clears any lingering expiry banner.
			if (pRecovered) { this._hideSessionExpiredBanner(); }
			return pRecovered;
		});
		// Proactive half of the story: notice expiry before the next click fails.
		this._installProactiveExpiryWatch();
	}

	/**
	 * Proactive expiry awareness (Layer 4). When the tab regains focus or becomes
	 * visible, re-check the session (debounced). If it lapsed while the page sat
	 * idle, drop a passive, non-blocking banner that opens the same in-place
	 * re-auth modal -- BEFORE the user's next click silently 401s. This also
	 * covers the <img>/blob 401 gap the fetch/RestClient hook cannot see.
	 * Idempotent; the listeners install once per app lifetime.
	 */
	_installProactiveExpiryWatch()
	{
		if (this._proactiveExpiryWatchInstalled) { return; }
		if (typeof window === 'undefined' || typeof document === 'undefined') { return; }
		this._proactiveExpiryWatchInstalled = true;

		let fCheck = () =>
		{
			// Only when the tab is actually visible, and only if we currently
			// believe we are signed in (so a deliberately signed-out user is left
			// alone).
			if (document.hidden) { return; }
			if (!(this.pict.AppData.Auth && this.pict.AppData.Auth.LoggedIn)) { return; }
			this.pict.providers.AuthClient.checkSession().then((pLoggedIn) =>
			{
				if (!pLoggedIn) { this._showSessionExpiredBanner(); }
			}).catch(() => { /* a failed check is not proof of expiry; stay quiet */ });
		};

		let tmpDebounceTimer = null;
		let fDebounced = () =>
		{
			if (tmpDebounceTimer) { clearTimeout(tmpDebounceTimer); }
			tmpDebounceTimer = setTimeout(fCheck, 500);
		};

		window.addEventListener('focus', fDebounced);
		document.addEventListener('visibilitychange', fDebounced);
	}

	/**
	 * A passive, dismissible banner announcing the session lapsed. Clicking it
	 * opens the in-place re-auth modal (the same ensure() the RestClient hook
	 * uses, so the two share one modal). Non-blocking: the page stays usable
	 * (reads just fail until re-auth), which is the honest state.
	 */
	_showSessionExpiredBanner()
	{
		if (typeof document === 'undefined') { return; }
		if (document.getElementById('Plansheet-SessionBanner')) { return; }

		let tmpBanner = document.createElement('div');
		tmpBanner.id = 'Plansheet-SessionBanner';
		tmpBanner.style.cssText =
			'position:fixed;top:0;left:0;right:0;z-index:10050;display:flex;align-items:center;gap:14px;'
			+ 'justify-content:center;padding:9px 16px;font-size:13px;font-family:-apple-system,Segoe UI,Roboto,sans-serif;'
			+ 'background:var(--theme-color-surface-raised,#2a2130);color:var(--theme-color-text,#f0e9ef);'
			+ 'border-bottom:1px solid var(--theme-color-brand-primary,#f0c44a);box-shadow:0 2px 10px rgba(0,0,0,0.28);';
		tmpBanner.innerHTML =
			'<span>Your session expired. Sign back in to keep your place.</span>'
			+ '<button id="Plansheet-SessionBanner-Signin" style="border:1px solid var(--theme-color-brand-primary,#f0c44a);background:var(--theme-color-brand-primary,#f0c44a);color:var(--theme-color-text-on-brand,#1a1206);border-radius:6px;padding:4px 12px;font-size:12.5px;font-weight:600;cursor:pointer;">Sign back in</button>'
			+ '<button id="Plansheet-SessionBanner-Dismiss" title="Dismiss" style="border:none;background:transparent;color:inherit;font-size:18px;line-height:1;cursor:pointer;opacity:0.7;">&times;</button>';
		document.body.appendChild(tmpBanner);

		let fSignin = document.getElementById('Plansheet-SessionBanner-Signin');
		if (fSignin)
		{
			fSignin.onclick = () =>
			{
				let tmpReAuth = this.pict.views['Plansheet-ReAuth'];
				if (!tmpReAuth) { this.showAuth('login'); return; }
				tmpReAuth.ensure().then((pRecovered) =>
				{
					// Recovered or handed off to the full screen -- either way the
					// banner has done its job.
					this._hideSessionExpiredBanner();
				});
			};
		}
		let fDismiss = document.getElementById('Plansheet-SessionBanner-Dismiss');
		if (fDismiss) { fDismiss.onclick = () => this._hideSessionExpiredBanner(); }
	}

	_hideSessionExpiredBanner()
	{
		if (typeof document === 'undefined') { return; }
		let tmpBanner = document.getElementById('Plansheet-SessionBanner');
		if (tmpBanner && tmpBanner.parentNode) { tmpBanner.parentNode.removeChild(tmpBanner); }
	}

	// Disarm recovery (deliberate sign-out). A null hook returns the framework to
	// its default pass-through: a 401 is handed back to the caller untouched.
	_disableSessionRecovery()
	{
		let tmpRestClient = this._sessionRestClient();
		if (tmpRestClient) { tmpRestClient.authenticationRecovery = null; }
		// A deliberate sign-out is not an expiry -- clear any banner too.
		this._hideSessionExpiredBanner();
	}

	// The single RestClient instance every migrated data path shares -- the one
	// auto-instantiated inside pict.EntityProvider. Wiring the recovery hook here
	// covers EntityProvider CRUD and any provider that issues custom calls through
	// this same client.
	_sessionRestClient()
	{
		return (this.pict.EntityProvider && this.pict.EntityProvider.restClient) || this.pict.RestClient || null;
	}

	/**
	 * Route handler for /session/:id. Downloads the zip and hands it to the viewer
	 * for rendering; the viewer's back button returns to the list via
	 * showSessions(). The Sessions list's "Open" reaches here through showSession
	 * (its descriptor OpenAction), so the URL is copy/paste-stable and reloadable.
	 */
	openServerSession(pIDSessionFile)
	{
		this._setActiveNav('Session');
		let tmpAPI = this.pict.providers.SessionAPI;
		// On a fresh deep-link / reload the session list has not loaded yet, so
		// make sure AppData.Sessions exists before recording the selection.
		tmpAPI.initializeAppData();
		this.pict.AppData.Sessions.SelectedID = pIDSessionFile;
		// Name the page after the session when its row is already in memory (came from the list);
		// on a cold deep-link the row is not loaded yet, so fall back to a generic "Session".
		let tmpSessionRow = (this.pict.AppData.Sessions.Index && this.pict.AppData.Sessions.Index[pIDSessionFile]) || null;
		this._setPageTitle((tmpSessionRow && tmpSessionRow.Title) || 'Session');

		// Swap the workspace contents for the section's container and show a
		// spinner straight away, then ask the section to render. The section's
		// Layout view replaces the container content with the per-session UI once
		// the blob has downloaded and parsed.
		this._mountTransformerContainer();
		this._showSessionLoading('Opening session...');

		tmpAPI.downloadSessionBlob(pIDSessionFile).then((pBuffer) =>
		{
			this.pict.views['Pict-Section-ClaudeTransformer'].loadSessionFromZip(pBuffer, (pErr) =>
			{
				if (pErr)
				{
					let tmpModal = this.pict.views['Pict-Section-Modal'];
					if (tmpModal) tmpModal.toast('Failed to load session: ' + (pErr.message || pErr), { type: 'error', duration: 6000 });
					this.showSessions();
				}
			});
		}).catch((pErr) =>
		{
			let tmpModal = this.pict.views['Pict-Section-Modal'];
			if (tmpModal) tmpModal.toast('Could not download session: ' + (pErr.message || pErr), { type: 'error', duration: 6000 });
			this.showSessions();
		});
	}
}

module.exports = PictApplicationPlansheet;

module.exports.default_configuration =
{
	Name: 'Plansheet',
	Hash: 'Plansheet'
};
